import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import JSZip from 'jszip';
import { Muxer, ArrayBufferTarget } from 'webm-muxer';
import './EditorApp.css';
import animcueLogo from '../assets/animcue-logo.svg';
import exportWarningIconSrc from '../assets/icons/export-warning.svg';
import PresetOverlay from '../components/PresetOverlay';
import ImageImportOverlay from '../components/ImageImportOverlay';
import { CANVAS_FORMATS, DEFAULT_CANVAS_FORMAT_ID, getCanvasFormatById } from '../canvasFormats';
import { DEFAULT_ACTIVE_PRESET_ID, DEFAULT_PRESETS } from '../presets/defaultPresets';
import { savePresetStorage } from '../presets/presetStorage';
import {
  applyPresetToConfig,
  applyPresetToConfigPreservingThemeColors,
  clonePreset,
  duplicatePreset,
  extractVisualConfigFromPreset,
  upsertCustomPreset,
} from '../presets/presetUtils';
import { applyMainScaleToVisualElements } from '../presets/mainScaleTransform';
import { MAX_SCALE, MIN_SCALE } from '../presets/scaleLimits';
import { buildTemplateLayerBlueprint } from '../presets/templateLayerBlueprint';
import {
  applyAutoSizeBindingToShape,
  getAutoSizeBindingSourceIds,
  resolveBindingSourceBounds,
  sanitizeAutoSizeBinding,
} from '../presets/primitiveAutoSizeBindingUtils';
import {
  DEFAULT_SNAP_GRID_TYPE,
  SNAP_GRID_TYPE_CENTER_CROSS,
  SNAP_GRID_TYPE_LOWER_THIRD,
  SNAP_GRID_TYPE_THIRDS,
  buildCanvasVisibleGridLines,
  snapTranslatedBounds,
} from '../components/editorSnapUtils';
import {
  TEMPLATE_MAIN_TEXT_LAYER_ID,
  TEMPLATE_SHAPE_LAYER_ID,
  TEMPLATE_SUB_TEXT_LAYER_ID,
  layerIdToPrimitiveId,
  normalizePresetLayerLocks,
  normalizePresetLayerOrder,
  primitiveLayerId,
} from '../presets/presetLayerUtils';
import {
  buildCanvasFontString,
  collectTextLayerFontFamilies,
  sanitizeTextLayerFontFamily,
} from '../fonts/textLayerFonts';
import { ensureGoogleFontsLoaded } from '../fonts/googleFontLoader';
import {
  buildImageReplacementPatch,
  getImagePrimitiveEntries,
  isImagePrimitive,
} from '../presets/imageLayerUtils';
import MirrorLeftIcon from '../icons/MirrorLeftIcon';
import GoLiveLaunchDialog from '../live/GoLiveLaunchDialog';
import {
  normalizePrimitiveWithResetSnapshot,
  normalizePresetWithResetSnapshots,
  stampPrimitiveResetSnapshot,
} from '../presets/primitiveResetSnapshot';
import { getProjectSnapshot, replaceProjectState, useProjectSnapshot } from '../state/projectStore';
import { getLivePlaylistSnapshot } from '../state/livePlaylistStore';
import { getLiveUrl, openOrFocusLiveWindow } from '../live/liveWindowManager';
import {
  LOCAL_REQUEST_ERROR_CODES,
  classifyLocalRequestError,
  createLocalHelperRoom,
  ensureLocalHelperReady,
  getGoLiveHelperDownloadUrl,
  getLocalHelperPlatform,
  getSafeLocalLaunchRelayOrigin,
  requestLocalHelperStatus,
} from '../live/localHelperClient';
/* global VideoEncoder, VideoFrame */

const DEFAULT_FPS = 60;
const MAX_PRESET_UNDO_STEPS = 50;
const FPS_OPTIONS = [24, 25, 30, 50, 60];
const MIN_TRANSITION = 0.2;
const MIN_STILL = 0.1;
const MIN_FADE = 0.05;
const GO_LIVE_LAUNCH_STATE_CREATING_ROOM = 'creating-room';
const GO_LIVE_LAUNCH_STATE_ERROR = 'error';
const TEMP_PREVIEW_PERSON = {
  name: 'Temp Name',
  title: 'Temp Title',
  mirrored: false,
  genericTextByPresetId: {},
};
const TEMPLATE_PRESET_LOGO_SRC = '/preset-logo-photo-icon.svg';
const MAIN_TEMPLATE_LOGO_BADGE_IMAGE_ID = '__template-logo-badge__';
const ASPECT_RATIO_STORAGE_KEY = 'lowerThirds.ui.aspectRatio';
const MAIN_GRID_SNAP_STORAGE_KEY = 'lowerThirds.ui.mainGridSnap.enabled';
const MAIN_GRID_SNAP_GRID_TYPE_STORAGE_KEY = 'lowerThirds.ui.mainGridSnap.gridType';
const MAIN_GRID_SNAP_GRID_VISIBLE_STORAGE_KEY = 'lowerThirds.ui.mainGridSnap.gridVisible';
const MAIN_MOVE_AXIS_LOCK_THRESHOLD = 4;
const GO_LIVE_LAUNCH_STATE_IDLE = 'idle';
const GO_LIVE_LOCAL_RELAY_PHASE_CHECKING = 'checking';
const GO_LIVE_LOCAL_RELAY_PHASE_READY = 'ready';
const GO_LIVE_LOCAL_RELAY_PHASE_OFFLINE = 'offline';
const GO_LIVE_LOCAL_RELAY_READY_MESSAGE = 'Relay live on this computer.';
const GO_LIVE_LOCAL_RELAY_CHECKING_MESSAGE = 'Checking for local relay...';
const GO_LIVE_LOCAL_RELAY_UNAVAILABLE_MESSAGE = 'Local relay not detected. Download and open GoLive Helper to enable Local mode.';
const GO_LIVE_LOCAL_RELAY_POLL_INTERVAL_MS = 2000;
const KOFI_BUTTON_SCRIPT_ID = 'animcue-kofi-button-script';
const KOFI_BUTTON_SCRIPT_SRC = 'https://storage.ko-fi.com/cdn/widget/Widget_2.js';
const KOFI_BUTTON_TEXT = 'Support me on Ko-fi';
const KOFI_BUTTON_COLOR = '#72a4f2';
const KOFI_BUTTON_PAGE_ID = 'S6S61WV6LI';

const getExternalScriptPromiseStore = () => {
  if (typeof window === 'undefined') return null;
  if (!window.__animcueExternalScriptPromises) {
    window.__animcueExternalScriptPromises = {};
  }
  return window.__animcueExternalScriptPromises;
};

const loadExternalScriptOnce = (scriptId, src) => {
  if (typeof document === 'undefined') return Promise.resolve(null);

  const promiseStore = getExternalScriptPromiseStore();
  if (promiseStore?.[scriptId]) return promiseStore[scriptId];

  const existingScript = document.getElementById(scriptId);
  if (existingScript?.dataset.loaded === 'true') {
    return Promise.resolve(existingScript);
  }

  const promise = new Promise((resolve, reject) => {
    const cleanupListeners = (script, handleLoad, handleError) => {
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
    };

    const attachListeners = (script) => {
      const handleLoad = () => {
        script.dataset.loaded = 'true';
        cleanupListeners(script, handleLoad, handleError);
        resolve(script);
      };

      const handleError = () => {
        cleanupListeners(script, handleLoad, handleError);
        if (promiseStore) delete promiseStore[scriptId];
        script.remove?.();
        reject(new Error(`Unable to load external script: ${src}`));
      };

      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
    };

    if (existingScript) {
      attachListeners(existingScript);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = src;
    script.async = true;
    script.dataset.loaded = 'false';
    attachListeners(script);
    (document.head || document.body || document.documentElement).appendChild(script);
  });

  if (promiseStore) {
    promiseStore[scriptId] = promise;
  }

  return promise;
};

const getGoLiveLocalRelayUnavailableMessage = (error) => {
  switch (error?.code) {
    case LOCAL_REQUEST_ERROR_CODES.PERMISSION_DENIED:
    case LOCAL_REQUEST_ERROR_CODES.INVALID_RESPONSE:
      return error.message || GO_LIVE_LOCAL_RELAY_UNAVAILABLE_MESSAGE;
    case LOCAL_REQUEST_ERROR_CODES.NETWORK_UNREACHABLE:
    case LOCAL_REQUEST_ERROR_CODES.UNKNOWN:
    case LOCAL_REQUEST_ERROR_CODES.TIMEOUT:
    default:
      return GO_LIVE_LOCAL_RELAY_UNAVAILABLE_MESSAGE;
  }
};
const SNAP_GRID_TYPE_OPTIONS = [
  { value: SNAP_GRID_TYPE_CENTER_CROSS, label: 'Center' },
  { value: SNAP_GRID_TYPE_THIRDS, label: 'Thirds' },
  { value: SNAP_GRID_TYPE_LOWER_THIRD, label: 'Lower third' },
];

const EXPORT_FORMAT_DEFINITIONS = [
  {
    id: 'video-webm-alpha',
    label: 'WebM video (Alpha)',
    kind: 'video',
    extension: 'webm',
    supportsAlpha: true,
    note: 'Transparent WebM (VP9) via WebCodecs.',
  },
  {
    id: 'video-webm',
    label: 'WebM video',
    kind: 'video',
    extension: 'webm',
    supportsAlpha: false,
    note: 'Browser-recorded WebM video.',
  },
  {
    id: 'video-mp4',
    label: 'MP4 video',
    kind: 'video',
    extension: 'mp4',
    supportsAlpha: false,
    note: 'Experimental: only available when browser MediaRecorder supports MP4.',
  },
  {
    id: 'video-mov-alpha-prores',
    label: 'MOV video (Alpha, ProRes 4444)',
    kind: 'video',
    extension: 'mov',
    supportsAlpha: true,
    note: 'Experimental: loads ffmpeg.wasm on first use (slow, high memory, segmented encode enabled).',
  },
  {
    id: 'still-png',
    label: 'PNG still image',
    kind: 'still',
    extension: 'png',
    supportsAlpha: true,
    note: 'Single frame still image.',
  },
  {
    id: 'still-webp',
    label: 'WebP still image',
    kind: 'still',
    extension: 'webp',
    supportsAlpha: true,
    note: 'Smaller still image when supported by the browser.',
  },
  {
    id: 'sequence-png-zip',
    label: 'PNG frame sequence (ZIP)',
    kind: 'sequence',
    extension: 'zip',
    supportsAlpha: true,
    note: 'Exports every frame as transparent PNG inside a ZIP.',
  },
];

const MEDIARECORDER_WEBM_MIME_CANDIDATES = [
  'video/webm; codecs=vp9',
  'video/webm',
];

const MEDIARECORDER_MP4_MIME_CANDIDATES = [
  'video/mp4; codecs=avc1.42E01E',
  'video/mp4;codecs=avc1.42E01E',
  'video/mp4',
];

const FFMPEG_CORE_BASE_PATH = '/vendor/ffmpeg';
const MOV_SEGMENT_MIN_FRAMES = 30;
const MOV_SEGMENT_MAX_FRAMES = 60;
const MOV_SEGMENT_TARGET_RAW_RGBA_BYTES = 384 * 1024 * 1024;
const MOV_PREFLIGHT_SOFT_PIXEL_FRAMES = 1.2e9;
const MOV_PREFLIGHT_SOFT_BATCH_PIXEL_FRAMES = 2.5e9;
const MOV_PREFLIGHT_HARD_PIXEL_FRAMES = 2.5e9;
const MOV_PREFLIGHT_HARD_BATCH_PIXEL_FRAMES = 6.0e9;
const FFMPEG_PROBE_PNG_DATA_URL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQIHWP4////fwAJ+wP9KobjigAAAABJRU5ErkJggg==';
const MOV_SLOW_WARNING_MESSAGE = 'WARNING! Extremely slow export times. Grab a coffee while you wait';

const createEmptyExportDialogState = () => ({
  isOpen: false,
  scope: 'single',
  personId: null,
  isChecking: false,
  checkingMessage: null,
  options: [],
  selectedFormatId: null,
  error: null,
});

const fadeCurveOptions = [
  { value: 'linear', label: 'Linear', icon: '/fade-linear.webp' },
  { value: 'ease-in', label: 'Ease In', icon: '/fade-ease-in.webp' }, // Starts slow then ramps up
  { value: 'ease-out', label: 'Ease Out', icon: '/fade-ease-out.webp' } // Starts fast then ramps down
];

const applyFadeCurve = (t, curve) => {
  const clamped = Math.min(Math.max(t, 0), 1);
  if (curve === 'ease-in') return clamped * clamped * clamped;
  if (curve === 'ease-out') return 1 - Math.pow(1 - clamped, 3);
  return clamped;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const toRadians = (degrees) => (degrees * Math.PI) / 180;
const primitiveColorRoles = new Set(['primary', 'secondary', 'textUpper', 'textLower']);
const primitiveColorModes = new Set(['theme', 'custom']);
const textSourceSet = new Set(['main', 'sub', 'generic']);
const hexColorPattern = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i;

const withDefaultNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeTextSource = (value, fallback = 'main') => (
  textSourceSet.has(value) ? value : fallback
);

const normalizeLayerName = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const getPersonGenericPresetBucket = (person, presetId) => {
  if (!person || typeof person !== 'object') return null;
  if (typeof presetId !== 'string' || !presetId.trim()) return null;
  const source = person.genericTextByPresetId;
  if (!source || typeof source !== 'object') return null;
  const bucket = source[presetId];
  return bucket && typeof bucket === 'object' ? bucket : null;
};

const resolvePersonGenericTextValue = ({
  person,
  presetId,
  primitiveId,
  fallbackText = '',
}) => {
  const bucket = getPersonGenericPresetBucket(person, presetId);
  if (!bucket || typeof primitiveId !== 'string' || !primitiveId.trim()) return fallbackText;
  if (!Object.prototype.hasOwnProperty.call(bucket, primitiveId)) return fallbackText;
  return typeof bucket[primitiveId] === 'string' ? bucket[primitiveId] : fallbackText;
};

const updatePersonGenericTextValue = ({
  person,
  presetId,
  primitiveId,
  nextValue,
  fallbackText = '',
}) => {
  if (!person || typeof person !== 'object') return person;
  if (typeof presetId !== 'string' || !presetId.trim()) return person;
  if (typeof primitiveId !== 'string' || !primitiveId.trim()) return person;

  const normalizedNextValue = typeof nextValue === 'string' ? nextValue : '';
  const currentGenericTextByPresetId = person.genericTextByPresetId && typeof person.genericTextByPresetId === 'object'
    ? person.genericTextByPresetId
    : {};
  const currentBucket = currentGenericTextByPresetId[presetId] && typeof currentGenericTextByPresetId[presetId] === 'object'
    ? currentGenericTextByPresetId[presetId]
    : {};
  const hasCurrentValue = Object.prototype.hasOwnProperty.call(currentBucket, primitiveId);
  const currentValue = hasCurrentValue ? currentBucket[primitiveId] : fallbackText;

  if (currentValue === normalizedNextValue) return person;

  const nextGenericTextByPresetId = { ...currentGenericTextByPresetId };
  const nextBucket = { ...currentBucket };

  if (normalizedNextValue === fallbackText) {
    delete nextBucket[primitiveId];
  } else {
    nextBucket[primitiveId] = normalizedNextValue;
  }

  if (Object.keys(nextBucket).length) nextGenericTextByPresetId[presetId] = nextBucket;
  else delete nextGenericTextByPresetId[presetId];

  const hadBuckets = Object.keys(currentGenericTextByPresetId).length > 0;
  const hasBuckets = Object.keys(nextGenericTextByPresetId).length > 0;
  if (!hadBuckets && !hasBuckets) return person;

  return {
    ...person,
    genericTextByPresetId: nextGenericTextByPresetId,
  };
};

const cleanupPersonGenericTextOverridesForPreset = ({
  person,
  presetId,
  validPrimitiveIds = null,
}) => {
  if (!person || typeof person !== 'object') return person;
  if (typeof presetId !== 'string' || !presetId.trim()) return person;
  const currentGenericTextByPresetId = person.genericTextByPresetId && typeof person.genericTextByPresetId === 'object'
    ? person.genericTextByPresetId
    : {};
  const currentBucket = currentGenericTextByPresetId[presetId];
  if (!currentBucket || typeof currentBucket !== 'object') return person;

  if (!validPrimitiveIds) {
    const nextGenericTextByPresetId = { ...currentGenericTextByPresetId };
    delete nextGenericTextByPresetId[presetId];
    return {
      ...person,
      genericTextByPresetId: nextGenericTextByPresetId,
    };
  }

  const nextBucketEntries = Object.entries(currentBucket).filter(([primitiveId]) => validPrimitiveIds.has(primitiveId));
  if (nextBucketEntries.length === Object.keys(currentBucket).length) return person;

  const nextGenericTextByPresetId = { ...currentGenericTextByPresetId };
  if (nextBucketEntries.length) nextGenericTextByPresetId[presetId] = Object.fromEntries(nextBucketEntries);
  else delete nextGenericTextByPresetId[presetId];

  return {
    ...person,
    genericTextByPresetId: nextGenericTextByPresetId,
  };
};

const readStoredBoolean = (key, fallback) => {
  if (typeof window === 'undefined' || !window.localStorage) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return fallback;
  } catch {
    return fallback;
  }
};

const readStoredString = (key, fallback, allowedValues = []) => {
  if (typeof window === 'undefined' || !window.localStorage) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    if (allowedValues.length && !allowedValues.includes(raw)) return fallback;
    return raw;
  } catch {
    return fallback;
  }
};

const isMediaRecorderMimeSupported = (mimeType) => {
  if (!mimeType || typeof MediaRecorder === 'undefined') return false;
  if (typeof MediaRecorder.isTypeSupported !== 'function') return true;
  try {
    return MediaRecorder.isTypeSupported(mimeType);
  } catch {
    return false;
  }
};

const pickSupportedMediaRecorderMime = (candidates = []) => (
  candidates.find((mimeType) => isMediaRecorderMimeSupported(mimeType)) || null
);

const getCanvasImageMimeSupport = (mimeType) => {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    if (!canvas || typeof canvas.toDataURL !== 'function') return false;
    const dataUrl = canvas.toDataURL(mimeType);
    return typeof dataUrl === 'string' && dataUrl.startsWith(`data:${mimeType}`);
  } catch {
    return false;
  }
};

const dataUrlToBlob = (dataUrl) => {
  const parts = String(dataUrl || '').split(',');
  if (parts.length < 2) {
    throw new Error('Invalid data URL');
  }
  const header = parts[0];
  const payload = parts.slice(1).join(',');
  const mimeMatch = header.match(/^data:([^;]+)(;base64)?/i);
  const mimeType = mimeMatch?.[1] || 'application/octet-stream';
  const isBase64 = /;base64/i.test(header);
  if (!isBase64) {
    return new Blob([decodeURIComponent(payload)], { type: mimeType });
  }
  if (typeof atob !== 'function') {
    throw new Error('Base64 decoding unavailable');
  }
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
};

const canvasToBlob = (canvas, mimeType = 'image/png', quality) => (
  new Promise((resolve, reject) => {
    if (!canvas) {
      reject(new Error('Canvas is unavailable'));
      return;
    }
    if (typeof canvas.toBlob === 'function') {
      try {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
            return;
          }
          reject(new Error(`Failed to encode canvas as ${mimeType}`));
        }, mimeType, quality);
        return;
      } catch (error) {
        reject(error);
        return;
      }
    }
    if (typeof canvas.toDataURL !== 'function') {
      reject(new Error('Canvas image export is unavailable in this browser'));
      return;
    }
    try {
      resolve(dataUrlToBlob(canvas.toDataURL(mimeType, quality)));
    } catch (error) {
      reject(error);
    }
  })
);

const getStillMimeTypeForFormat = (formatId) => {
  if (formatId === 'still-webp') return 'image/webp';
  return 'image/png';
};

const getSequenceFrameExtensionForMime = (mimeType) => {
  if (mimeType === 'image/webp') return 'webp';
  return 'png';
};

const buildProjectSyncSignature = (project) => JSON.stringify({
  people: project?.people || [],
  selectedPersonId: project?.selectedPersonId || null,
  activePresetId: project?.activePresetId || null,
  customPresets: project?.customPresets || [],
  config: project?.config || null,
  aspectRatioId: project?.aspectRatioId || null,
  fps: project?.fps || null,
});

const buildExportProgressHeader = (formatOption, alphaRequested) => {
  const baseLabel = formatOption?.kind === 'sequence' ? 'Rendering frame sequence' : 'Rendering export';
  if (formatOption?.id === 'video-webm-alpha') return 'Rendering (Alpha channel)';
  if (formatOption?.id === 'video-mov-alpha-prores') return alphaRequested ? 'Rendering MOV (Alpha)' : 'Rendering MOV export';
  if (alphaRequested) return `${baseLabel} (Alpha)`;
  return baseLabel;
};

const getExportFormatDisplayOrder = (options = []) => (
  options
    .map((option, index) => ({ option, index }))
    .sort((a, b) => {
      const groupA = a.option?.selectable ? 0 : 1;
      const groupB = b.option?.selectable ? 0 : 1;
      if (groupA !== groupB) return groupA - groupB;
      return a.index - b.index;
    })
    .map((entry) => entry.option)
);

const getExportFormatHelpContent = (option) => {
  const warningParts = [];
  if (option?.disabledReason) warningParts.push(option.disabledReason);
  if (option?.id === 'video-mov-alpha-prores') warningParts.push(MOV_SLOW_WARNING_MESSAGE);

  return {
    warningMessage: warningParts.length ? warningParts.join('\n\n') : null,
  };
};

const computeMonotonicEta = (progressState, percent, nowMs = performance.now()) => {
  if (!progressState || typeof progressState !== 'object') {
    return { percent: clamp(Number(percent) || 0, 0, 1), etaMs: null };
  }
  const nextPercent = Math.max(
    Number(progressState.lastPercent) || 0,
    clamp(Number(percent) || 0, 0, 1),
  );
  progressState.lastPercent = nextPercent;

  if (!Number.isFinite(progressState.startTimeMs)) {
    progressState.startTimeMs = nowMs;
  }
  if (nextPercent >= 1) {
    return { percent: nextPercent, etaMs: 0 };
  }
  if (nextPercent <= 0.01) {
    return { percent: nextPercent, etaMs: null };
  }
  const elapsed = Math.max(0, nowMs - progressState.startTimeMs);
  if (!Number.isFinite(elapsed) || elapsed <= 0) {
    return { percent: nextPercent, etaMs: null };
  }

  return {
    percent: nextPercent,
    etaMs: Math.max(0, (elapsed / nextPercent) - elapsed),
  };
};

const computeMovSegmentFrameCount = (width, height) => {
  const bytesPerFrame = Math.max(1, Math.round(Math.max(1, width) * Math.max(1, height) * 4));
  const dynamicFrames = Math.floor(MOV_SEGMENT_TARGET_RAW_RGBA_BYTES / bytesPerFrame);
  return clamp(dynamicFrames, MOV_SEGMENT_MIN_FRAMES, MOV_SEGMENT_MAX_FRAMES);
};

const getMovPreflightAssessment = ({ width, height, totalFramesPerClip, clipCount }) => {
  const safeWidth = Math.max(1, Number(width) || 1);
  const safeHeight = Math.max(1, Number(height) || 1);
  const safeFrames = Math.max(1, Number(totalFramesPerClip) || 1);
  const safeClipCount = Math.max(1, Number(clipCount) || 1);
  const clipPixelFrames = safeWidth * safeHeight * safeFrames;
  const batchPixelFrames = clipPixelFrames * safeClipCount;

  if (clipPixelFrames > MOV_PREFLIGHT_HARD_PIXEL_FRAMES) {
    return {
      blocked: true,
      reason: 'Clip is too large for browser-only MOV export (estimated workload exceeds safe limits).',
      warning: null,
      clipPixelFrames,
      batchPixelFrames,
    };
  }
  if (batchPixelFrames > MOV_PREFLIGHT_HARD_BATCH_PIXEL_FRAMES) {
    return {
      blocked: true,
      reason: 'Batch is too large for browser-only MOV export (estimated workload exceeds safe limits).',
      warning: null,
      clipPixelFrames,
      batchPixelFrames,
    };
  }

  let warning = null;
  if (clipPixelFrames > MOV_PREFLIGHT_SOFT_PIXEL_FRAMES || batchPixelFrames > MOV_PREFLIGHT_SOFT_BATCH_PIXEL_FRAMES) {
    warning = 'Large MOV export: segmented encoding reduces memory spikes, but browser RAM limits may still fail on long ProRes renders.';
  }

  return {
    blocked: false,
    reason: null,
    warning,
    clipPixelFrames,
    batchPixelFrames,
  };
};

const unionBounds = (boundsList) => {
  const valid = boundsList.filter(Boolean);
  if (!valid.length) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  const minX = Math.min(...valid.map((bounds) => bounds.minX));
  const minY = Math.min(...valid.map((bounds) => bounds.minY));
  const maxX = Math.max(...valid.map((bounds) => bounds.maxX));
  const maxY = Math.max(...valid.map((bounds) => bounds.maxY));
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const rectToBounds = (x, y, width, height) => ({
  minX: x,
  minY: y,
  maxX: x + width,
  maxY: y + height,
  width,
  height,
});

const getBoundsFromPoints = (points) => {
  if (!points.length) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  const minX = Math.min(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxX = Math.max(...points.map((point) => point.x));
  const maxY = Math.max(...points.map((point) => point.y));
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const rotatePoint = (point, pivot, angleRadians) => {
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  const sin = Math.sin(angleRadians);
  const cos = Math.cos(angleRadians);
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  };
};

const GridEyeOpenIcon = () => (
  <svg className="layer-action-eye-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M15.0007 12C15.0007 13.6569 13.6576 15 12.0007 15C10.3439 15 9.00073 13.6569 9.00073 12C9.00073 10.3431 10.3439 9 12.0007 9C13.6576 9 15.0007 10.3431 15.0007 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.0012 5C7.52354 5 3.73326 7.94288 2.45898 12C3.73324 16.0571 7.52354 19 12.0012 19C16.4788 19 20.2691 16.0571 21.5434 12C20.2691 7.94291 16.4788 5 12.0012 5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GridEyeClosedIcon = () => (
  <svg className="layer-action-eye-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const rotateBounds = (bounds, pivot, angleRadians) => {
  if (!bounds) return null;
  if (!angleRadians) return { ...bounds };
  const rotatedPoints = [
    rotatePoint({ x: bounds.minX, y: bounds.minY }, pivot, angleRadians),
    rotatePoint({ x: bounds.maxX, y: bounds.minY }, pivot, angleRadians),
    rotatePoint({ x: bounds.maxX, y: bounds.maxY }, pivot, angleRadians),
    rotatePoint({ x: bounds.minX, y: bounds.maxY }, pivot, angleRadians),
  ];
  return getBoundsFromPoints(rotatedPoints);
};

const getParallelogramPoints = (x, y, width, height, skewX = 0) => ([
  { x, y },
  { x: x + width, y },
  { x: x + width + skewX, y: y + height },
  { x: x + skewX, y: y + height },
]);

const traceParallelogramPath = (ctx, x, y, width, height, skewX = 0) => {
  const points = getParallelogramPoints(x, y, width, height, skewX);
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.closePath();
};

const isTemplateManagedPrimitive = (shape) => (
  Boolean(shape?.templatePart)
  || (typeof shape?.templateFamily === 'string' && shape.templateFamily.trim())
);

const resolveThemeColor = (role, config) => {
  if (role === 'secondary') return config.colorSecondary;
  if (role === 'textUpper') return config.colorTextUpper;
  if (role === 'textLower') return config.colorTextLower;
  return config.colorPrimary;
};

const normalizePrimitiveColorRole = (role, fallback = 'primary') => (
  primitiveColorRoles.has(role) ? role : fallback
);

const normalizePrimitiveColorMode = (mode, fallback = 'theme') => (
  primitiveColorModes.has(mode) ? mode : fallback
);

const normalizePrimitiveColorValue = (value, fallback = '#ffffff') => (
  typeof value === 'string' && hexColorPattern.test(value) ? value : fallback
);
const serializePresetDraft = (preset) => JSON.stringify(preset ?? null);

const resolvePrimitiveChannelColor = (shape, config, channel) => {
  if (!shape) return '#ffffff';
  const legacyRole = normalizePrimitiveColorRole(shape.colorRole, 'primary');
  const roleField = channel === 'stroke' ? 'strokeColorRole' : 'fillColorRole';
  const modeField = channel === 'stroke' ? 'strokeColorMode' : 'fillColorMode';
  const valueField = channel === 'stroke' ? 'strokeColorValue' : 'fillColorValue';
  const colorRole = normalizePrimitiveColorRole(shape[roleField], legacyRole);
  const colorMode = normalizePrimitiveColorMode(shape[modeField], 'theme');
  const customValue = normalizePrimitiveColorValue(shape[valueField], resolveThemeColor(colorRole, config));
  if (colorMode === 'custom') return customValue;
  return resolveThemeColor(colorRole, config);
};

const resolvePrimitiveFillColor = (shape, config) => (
  resolvePrimitiveChannelColor(shape, config, 'fill')
);

const resolvePrimitiveStrokeColor = (shape, config) => (
  resolvePrimitiveChannelColor(shape, config, 'stroke')
);

const estimateTextWidth = (text, fontPx) => (
  (text?.length || 0) * Math.max(0, fontPx) * 0.56
);

const measureTextWidth = (ctx, text, font) => {
  if (!ctx || !text) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
};

const fitFontToWidth = ({
  ctx,
  text,
  preferredPx,
  minPx,
  maxWidth,
  weight = 'bold',
  fontFamily = null,
}) => {
  const initial = Math.max(minPx, preferredPx);
  if (!text || !maxWidth || maxWidth <= 0) return initial;
  const font = buildCanvasFontString({ weight, px: initial, family: fontFamily });
  const measured = ctx
    ? measureTextWidth(ctx, text, font)
    : estimateTextWidth(text, initial);
  if (!measured || measured <= maxWidth) return initial;
  return clamp((initial * maxWidth) / measured, minPx, initial);
};

const buildPrimitiveTextLayout = ({
  ctx,
  shapeType,
  baseBounds,
  showMain,
  showSub,
  mainText,
  subText,
  mainFontPx,
  subFontPx,
  scale,
}) => {
  if (!baseBounds || (!showMain && !showSub)) return null;

  const textBox = shapeType === 'line'
    ? (() => {
      const singleLineHeight = Math.max(46 * scale, subFontPx + 16 * scale);
      const doubleLineHeight = Math.max(78 * scale, mainFontPx + subFontPx + 24 * scale);
      const height = showMain && showSub ? doubleLineHeight : singleLineHeight;
      const gapFromLine = Math.max(8 * scale, 4);
      return rectToBounds(baseBounds.minX, baseBounds.minY - height - gapFromLine, baseBounds.width, height);
    })()
    : baseBounds;

  const paddingX = clamp(textBox.width * 0.08, Math.max(6, 6 * scale), Math.max(24, 24 * scale));
  const maxWidth = Math.max(10, textBox.width - paddingX * 2);
  const mainMinPx = Math.max(12, 10 * scale);
  const subMinPx = Math.max(10, 8 * scale);

  const mainPreferred = showSub
    ? Math.min(mainFontPx, textBox.height * 0.36)
    : Math.min(mainFontPx, textBox.height * 0.58);
  const subPreferred = showMain
    ? Math.min(subFontPx, textBox.height * 0.26)
    : Math.min(subFontPx, textBox.height * 0.58);

  const lines = [];
  if (showMain) {
    const fontPx = fitFontToWidth({
      ctx,
      text: mainText,
      preferredPx: mainPreferred,
      minPx: mainMinPx,
      maxWidth,
      weight: 'bold',
    });
    const font = `bold ${fontPx}px Arial`;
    const width = ctx
      ? measureTextWidth(ctx, mainText, font)
      : estimateTextWidth(mainText, fontPx);
    const y = showSub
      ? textBox.minY + textBox.height * 0.48
      : textBox.minY + textBox.height * 0.65;
    lines.push({
      kind: 'main',
      text: mainText,
      font,
      fontPx,
      x: textBox.minX + paddingX,
      y,
      width,
    });
  }
  if (showSub) {
    const fontPx = fitFontToWidth({
      ctx,
      text: subText,
      preferredPx: subPreferred,
      minPx: subMinPx,
      maxWidth,
      weight: '600',
    });
    const font = `600 ${fontPx}px Arial`;
    const width = ctx
      ? measureTextWidth(ctx, subText, font)
      : estimateTextWidth(subText, fontPx);
    const y = showMain
      ? textBox.minY + textBox.height * 0.82
      : textBox.minY + textBox.height * 0.65;
    lines.push({
      kind: 'sub',
      text: subText,
      font,
      fontPx,
      x: textBox.minX + paddingX,
      y,
      width,
    });
  }

  const localBounds = unionBounds(lines.map((line) => (
    rectToBounds(line.x, line.y - line.fontPx, line.width, line.fontPx + Math.max(6, 8 * scale))
  )));

  return { lines, localBounds };
};

const buildStandaloneTextLayerLayout = ({
  ctx,
  baseBounds,
  textSource,
  text,
  mainFontPx,
  subFontPx,
  scale,
  textPaddingX,
  textFontRatio,
  textBaselineRatio,
  textFontFamily = null,
  fitToWidth = true,
}) => {
  if (!baseBounds) return null;
  const kind = normalizeTextSource(textSource, 'main');
  const textValue = typeof text === 'string' ? text : '';
  const fallbackFontPx = kind === 'main' ? mainFontPx : subFontPx;
  const boxHeight = Math.max(1, baseBounds.height);
  const paddingX = clamp(
    withDefaultNumber(textPaddingX, 0),
    0,
    Math.max(0, baseBounds.width - 4),
  );
  const fontRatio = clamp(withDefaultNumber(textFontRatio, fallbackFontPx / boxHeight), 0.2, 1.5);
  const preferredPx = Math.max(8, boxHeight * fontRatio);
  const baselineRatio = clamp(withDefaultNumber(textBaselineRatio, 0.78), 0.2, 1.2);
  const maxWidth = Math.max(8, baseBounds.width - paddingX * 2);
  const fontWeight = kind === 'main' ? 'bold' : '600';
  const fontPx = fitToWidth
    ? fitFontToWidth({
      ctx,
      text: textValue,
      preferredPx,
      minPx: Math.max(8, preferredPx * 0.35),
      maxWidth,
      weight: fontWeight,
      fontFamily: textFontFamily,
    })
    : preferredPx;
  const font = buildCanvasFontString({
    weight: fontWeight,
    px: fontPx,
    family: textFontFamily,
  });
  const width = ctx
    ? measureTextWidth(ctx, textValue, font)
    : estimateTextWidth(textValue, fontPx);
  const y = baseBounds.minY + boxHeight * baselineRatio;
  const x = baseBounds.minX + paddingX;
  const line = {
    kind,
    text: textValue,
    font,
    fontPx,
    x,
    y,
    width,
  };
  const localBounds = rectToBounds(
    x,
    y - fontPx,
    Math.max(1, width),
    fontPx + Math.max(4, 6 * scale),
  );
  return { lines: [line], localBounds };
};

const App = () => {
  // --- STATE MANAGEMENT ---
  const projectBootstrap = useMemo(() => getProjectSnapshot(), []);
  const externalProject = useProjectSnapshot();
  const [customPresets, setCustomPresets] = useState(projectBootstrap.customPresets);
  const [activePresetId, setActivePresetId] = useState(projectBootstrap.activePresetId);
  const [config, setConfig] = useState(projectBootstrap.config);
  const [fps, setFps] = useState(projectBootstrap.fps || DEFAULT_FPS);
  const [aspectRatioId, setAspectRatioId] = useState(() => (
    projectBootstrap.aspectRatioId
    || readStoredString(
      ASPECT_RATIO_STORAGE_KEY,
      DEFAULT_CANVAS_FORMAT_ID,
      CANVAS_FORMATS.map((format) => format.id),
    )
  ));
  const [people, setPeople] = useState(projectBootstrap.people);
  const [selectedPersonId, setSelectedPersonId] = useState(projectBootstrap.selectedPersonId);
  const [isPresetOverlayOpen, setIsPresetOverlayOpen] = useState(false);
  const [overlayPresetId, setOverlayPresetId] = useState(projectBootstrap.activePresetId);
  const [draftPreset, setDraftPreset] = useState(null);
  const [isDraftDirty, setIsDraftDirty] = useState(false);
  const [overlayDraftUndoCount, setOverlayDraftUndoCount] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const [isMainImageImportOpen, setIsMainImageImportOpen] = useState(false);
  const [selectedMainImagePrimitiveId, setSelectedMainImagePrimitiveId] = useState(null);
  const [, setFontLoadRevision] = useState(0);

  const preparePresetDraft = useCallback((preset) => (
    normalizePresetWithResetSnapshots(clonePreset(preset))
  ), []);

  const [isRecording, setIsRecording] = useState(false);
  const [goLiveError, setGoLiveError] = useState(null);
  const [isGoLiveDialogOpen, setIsGoLiveDialogOpen] = useState(false);
  const [goLiveLaunchState, setGoLiveLaunchState] = useState(GO_LIVE_LAUNCH_STATE_IDLE);
  const [localRelayPhase, setLocalRelayPhase] = useState(GO_LIVE_LOCAL_RELAY_PHASE_CHECKING);
  const [localRelayMessage, setLocalRelayMessage] = useState(GO_LIVE_LOCAL_RELAY_CHECKING_MESSAGE);
  const [localRelayStatus, setLocalRelayStatus] = useState(null);
  const [exportProgress, setExportProgress] = useState(null); // { mode: 'mediarecorder', percent: 0-1, etaMs?: number }
  const [exportDialog, setExportDialog] = useState(() => createEmptyExportDialogState());
  const [exportFormatHelpPopover, setExportFormatHelpPopover] = useState(null); // { optionId, kind: 'warning', pinned }
  const [ffmpegRuntimeState, setFfmpegRuntimeState] = useState({
    status: 'idle',
    hasProResSupport: null,
    errorReason: null,
    probeCompleted: false,
  });
  const [openFadeMenu, setOpenFadeMenu] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [fadeDrag, setFadeDrag] = useState(null);
  const [mainPositionDrag, setMainPositionDrag] = useState(null);
  const isCreatingLiveRoom = goLiveLaunchState === GO_LIVE_LAUNCH_STATE_CREATING_ROOM;
  const helperDownloadUrl = useMemo(() => getGoLiveHelperDownloadUrl(), []);
  const helperPlatform = useMemo(() => getLocalHelperPlatform(), []);
  const [isMainGridSnapEnabled, setIsMainGridSnapEnabled] = useState(() => (
    readStoredBoolean(MAIN_GRID_SNAP_STORAGE_KEY, false)
  ));
  const [mainSnapGridType, setMainSnapGridType] = useState(() => (
    readStoredString(
      MAIN_GRID_SNAP_GRID_TYPE_STORAGE_KEY,
      DEFAULT_SNAP_GRID_TYPE,
      SNAP_GRID_TYPE_OPTIONS.map((option) => option.value),
    )
  ));
  const [isMainSnapGridVisible, setIsMainSnapGridVisible] = useState(() => (
    readStoredBoolean(MAIN_GRID_SNAP_GRID_VISIBLE_STORAGE_KEY, true)
  ));
  const [mainSnapGuides, setMainSnapGuides] = useState({ vertical: [], horizontal: [] });
  const syncedProjectSignatureRef = useRef(buildProjectSyncSignature(projectBootstrap));

  const activeCanvasFormat = useMemo(
    () => getCanvasFormatById(aspectRatioId),
    [aspectRatioId],
  );
  const CANVAS_WIDTH = activeCanvasFormat.width;
  const CANVAS_HEIGHT = activeCanvasFormat.height;
  const canvasAspectRatio = `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`;

  const canvasRef = useRef(null);
  const requestRef = useRef();
  const timelineRef = useRef(null);
  const mainPositionRef = useRef(null);
  const mainMoveAxisLockRef = useRef(null);
  const introSelectRef = useRef(null);
  const outroSelectRef = useRef(null);
  const webCodecsSupportRef = useRef({ key: null, supported: null });
  const webCodecsAlphaConfigRef = useRef(null);
  const webCodecsOpaqueConfigRef = useRef(null);
  const lastSelectedExportFormatIdRef = useRef(null);
  const exportDialogRequestIdRef = useRef(0);
  const ffmpegRef = useRef(null);
  const ffmpegLoadPromiseRef = useRef(null);
  const ffmpegActiveJobRef = useRef(null);
  const ffmpegProgressRelayRef = useRef(null);
  const ffmpegLogRelayRef = useRef(null);
  const imageCacheRef = useRef(new Map());
  const kofiButtonHostRef = useRef(null);
  const renderScratchCanvasRef = useRef({ canvas: null, ctx: null });
  const cancelRenderRef = useRef(null);
  const overlayDraftUndoStackRef = useRef([]);
  const overlayDraftBaselineRef = useRef(null);
  const overlayDraftBaselineSerializedRef = useRef(serializePresetDraft(null));
  const overlayActiveUndoGroupRef = useRef(null);
  const overlayDraftCurrentRef = useRef(null);
  const activeTextLayerFontPreloadSignatureRef = useRef('');
  const overlayTextLayerFontPreloadSignatureRef = useRef('');
  const setOverlayDraftState = useCallback((nextDraft) => {
    overlayDraftCurrentRef.current = nextDraft;
    setDraftPreset(nextDraft);
  }, []);
  const resetOverlayDraftUndoSession = useCallback((baselineDraft = null) => {
    const baselineClone = baselineDraft ? clonePreset(baselineDraft) : null;
    overlayDraftUndoStackRef.current = [];
    overlayDraftBaselineRef.current = baselineClone;
    overlayDraftBaselineSerializedRef.current = serializePresetDraft(baselineClone);
    overlayActiveUndoGroupRef.current = null;
    setOverlayDraftUndoCount(0);
  }, []);
  const pushOverlayDraftUndoSnapshot = useCallback((snapshotDraft) => {
    if (!snapshotDraft) return;
    const nextStack = [
      ...overlayDraftUndoStackRef.current,
      clonePreset(snapshotDraft),
    ];
    if (nextStack.length > MAX_PRESET_UNDO_STEPS) {
      nextStack.splice(0, nextStack.length - MAX_PRESET_UNDO_STEPS);
    }
    overlayDraftUndoStackRef.current = nextStack;
    setOverlayDraftUndoCount(nextStack.length);
  }, []);
  const selectedPerson = people.find(p => p.id === selectedPersonId) || people[0];
  const allPresets = useMemo(() => [...DEFAULT_PRESETS, ...customPresets], [customPresets]);
  const mainImageEntries = useMemo(
    () => getImagePrimitiveEntries(config?.shapePrimitives),
    [config?.shapePrimitives],
  );
  const hasVirtualMainLogoBadgeImage = useMemo(() => (
    config?.preset === 'logo-badge'
    && config?.templateShapeVisible !== false
    && mainImageEntries.length === 0
  ), [config?.preset, config?.templateShapeVisible, mainImageEntries.length]);
  const mainSidebarImageEntries = useMemo(() => (
    hasVirtualMainLogoBadgeImage
      ? [{
        primitiveId: MAIN_TEMPLATE_LOGO_BADGE_IMAGE_ID,
        primitive: {
          layerKind: 'image',
          type: 'image',
          imageCropMode: 'original',
          templatePart: 'logo-icon',
          imageData: TEMPLATE_PRESET_LOGO_SRC,
        },
        index: -1,
        defaultLabel: 'Image 1',
        label: 'Image 1',
      }]
      : mainImageEntries
  ), [hasVirtualMainLogoBadgeImage, mainImageEntries]);
  const hasMultipleMainImages = mainSidebarImageEntries.length > 1;
  const selectedMainImageEntry = useMemo(
    () => mainSidebarImageEntries.find((entry) => entry.primitiveId === selectedMainImagePrimitiveId) || mainSidebarImageEntries[0] || null,
    [mainSidebarImageEntries, selectedMainImagePrimitiveId],
  );
  const activeConfigTextLayerFonts = useMemo(
    () => collectTextLayerFontFamilies(config?.shapePrimitives),
    [config?.shapePrimitives],
  );
  const overlayDraftTextLayerFonts = useMemo(
    () => collectTextLayerFontFamilies(draftPreset?.shapePrimitives),
    [draftPreset?.shapePrimitives],
  );
  const ensurePresetTextLayerFontsLoaded = useCallback(async (presetLike) => {
    const families = collectTextLayerFontFamilies(presetLike?.shapePrimitives);
    if (!families.length) return;
    try {
      await ensureGoogleFontsLoaded(families);
    } catch (error) {
      console.warn('[LowerThirds] Text layer font preload failed', error);
    } finally {
      setFontLoadRevision((prev) => prev + 1);
    }
  }, []);

  const totalDuration = useMemo(
    () => config.transitionInDuration + config.stillDuration + config.transitionOutDuration,
    [config.stillDuration, config.transitionInDuration, config.transitionOutDuration]
  );
  const [totalInputValue, setTotalInputValue] = useState(totalDuration.toFixed(2));
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const isSlide = (type) => type === 'slide-left' || type === 'slide-right';
  useEffect(() => {
    if (!isEditingTotal) {
      setTotalInputValue(totalDuration.toFixed(2));
    }
  }, [isEditingTotal, totalDuration]);

  useEffect(() => {
    const host = kofiButtonHostRef.current;
    if (!host) return undefined;

    let isCancelled = false;

    const renderKofiButton = async () => {
      try {
        await loadExternalScriptOnce(KOFI_BUTTON_SCRIPT_ID, KOFI_BUTTON_SCRIPT_SRC);
        if (isCancelled || !window.kofiwidget2?.init || !window.kofiwidget2?.getHTML) return;

        window.kofiwidget2.init(KOFI_BUTTON_TEXT, KOFI_BUTTON_COLOR, KOFI_BUTTON_PAGE_ID);
        host.innerHTML = window.kofiwidget2.getHTML();
      } catch (error) {
        if (!isCancelled) {
          console.warn('[AnimCue] Failed to load Ko-fi button.', error);
        }
      }
    };

    host.innerHTML = '';
    renderKofiButton();

    return () => {
      isCancelled = true;
      host.innerHTML = '';
    };
  }, []);

  useEffect(() => {
    if (!isGoLiveDialogOpen || isCreatingLiveRoom) return undefined;

    let isCancelled = false;
    let pollTimerId = null;

    setLocalRelayPhase(GO_LIVE_LOCAL_RELAY_PHASE_CHECKING);
    setLocalRelayMessage(GO_LIVE_LOCAL_RELAY_CHECKING_MESSAGE);

    const probeLocalRelay = async () => {
      try {
        const helperStatus = await requestLocalHelperStatus({ platform: helperPlatform });
        if (isCancelled) return;
        setLocalRelayStatus(helperStatus);
        setLocalRelayPhase(GO_LIVE_LOCAL_RELAY_PHASE_READY);
        setLocalRelayMessage(GO_LIVE_LOCAL_RELAY_READY_MESSAGE);
      } catch (error) {
        if (isCancelled) return;
        const classifiedError = classifyLocalRequestError(error, { platform: helperPlatform });
        setLocalRelayStatus(null);
        setLocalRelayPhase(GO_LIVE_LOCAL_RELAY_PHASE_OFFLINE);
        setLocalRelayMessage(getGoLiveLocalRelayUnavailableMessage(classifiedError));
      } finally {
        if (isCancelled) return;
        pollTimerId = setTimeout(probeLocalRelay, GO_LIVE_LOCAL_RELAY_POLL_INTERVAL_MS);
      }
    };

    probeLocalRelay();

    return () => {
      isCancelled = true;
      if (pollTimerId) clearTimeout(pollTimerId);
    };
  }, [helperPlatform, isCreatingLiveRoom, isGoLiveDialogOpen]);

  useEffect(() => {
    const result = savePresetStorage({ activePresetId, customPresets });
    if (result && result.success === false) {
      setSaveError('Failed to save presets: Local storage quota exceeded. Try using smaller images.');
    } else {
      setSaveError(null);
    }
  }, [activePresetId, customPresets]);

  useEffect(() => {
    const nextProject = {
      version: 1,
      people,
      selectedPersonId,
      activePresetId,
      customPresets,
      config,
      aspectRatioId,
      fps,
    };
    const signature = buildProjectSyncSignature(nextProject);
    if (signature === syncedProjectSignatureRef.current) return;
    syncedProjectSignatureRef.current = signature;
    replaceProjectState(nextProject);
  }, [activePresetId, aspectRatioId, config, customPresets, fps, people, selectedPersonId]);

  useEffect(() => {
    const signature = buildProjectSyncSignature(externalProject);
    if (signature === syncedProjectSignatureRef.current) return;
    syncedProjectSignatureRef.current = signature;
    setCustomPresets(externalProject.customPresets);
    setActivePresetId(externalProject.activePresetId);
    setConfig(externalProject.config);
    setFps(externalProject.fps);
    setAspectRatioId(externalProject.aspectRatioId);
    setPeople(externalProject.people);
    setSelectedPersonId(externalProject.selectedPersonId);
    setOverlayPresetId(externalProject.activePresetId);
  }, [externalProject]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(ASPECT_RATIO_STORAGE_KEY, aspectRatioId);
    } catch {
      // Ignore localStorage write failures.
    }
  }, [aspectRatioId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(MAIN_GRID_SNAP_STORAGE_KEY, String(isMainGridSnapEnabled));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [isMainGridSnapEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(MAIN_GRID_SNAP_GRID_TYPE_STORAGE_KEY, mainSnapGridType);
    } catch {
      // Ignore localStorage write failures.
    }
  }, [mainSnapGridType]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(MAIN_GRID_SNAP_GRID_VISIBLE_STORAGE_KEY, String(isMainSnapGridVisible));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [isMainSnapGridVisible]);

  useEffect(() => {
    if (!activeConfigTextLayerFonts.length) return;
    const signature = activeConfigTextLayerFonts.join('|');
    if (activeTextLayerFontPreloadSignatureRef.current === signature) return;
    activeTextLayerFontPreloadSignatureRef.current = signature;
    ensurePresetTextLayerFontsLoaded({ shapePrimitives: config.shapePrimitives });
  }, [activeConfigTextLayerFonts, config.shapePrimitives, ensurePresetTextLayerFontsLoaded]);

  useEffect(() => {
    if (!mainSidebarImageEntries.length) {
      setSelectedMainImagePrimitiveId(null);
      setIsMainImageImportOpen(false);
      return;
    }
    if (mainSidebarImageEntries.some((entry) => entry.primitiveId === selectedMainImagePrimitiveId)) return;
    setSelectedMainImagePrimitiveId(mainSidebarImageEntries[0].primitiveId);
  }, [mainSidebarImageEntries, selectedMainImagePrimitiveId]);

  useEffect(() => {
    if (!isPresetOverlayOpen || !overlayDraftTextLayerFonts.length) return;
    const signature = overlayDraftTextLayerFonts.join('|');
    if (overlayTextLayerFontPreloadSignatureRef.current === signature) return;
    overlayTextLayerFontPreloadSignatureRef.current = signature;
    ensurePresetTextLayerFontsLoaded({ shapePrimitives: draftPreset?.shapePrimitives });
  }, [
    draftPreset?.shapePrimitives,
    ensurePresetTextLayerFontsLoaded,
    isPresetOverlayOpen,
    overlayDraftTextLayerFonts,
  ]);

  useEffect(() => {
    if (allPresets.some((preset) => preset.id === activePresetId)) return;
    const fallback = allPresets.find((preset) => preset.id === DEFAULT_ACTIVE_PRESET_ID) || allPresets[0];
    if (!fallback) return;
    setActivePresetId(fallback.id);
    setConfig((prev) => applyPresetToConfig(prev, fallback));
  }, [activePresetId, allPresets]);

  const slugify = (text) =>
    text
      ? text.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'lower-third'
      : 'lower-third';

  const measureCtx = useMemo(() => {
    const canvas = document.createElement('canvas');
    return canvas.getContext('2d');
  }, []);

  const getDesignMetrics = useCallback((ctx, personOverride, configOverride = config) => {
    const scale = clamp(withDefaultNumber(configOverride.scale, 1), MIN_SCALE, MAX_SCALE);
    const rotation = clamp(withDefaultNumber(configOverride.rotation, 0), -180, 180);
    const globalRotationRadians = toRadians(rotation);
    const resolvedPresetId = typeof configOverride?.presetId === 'string' && configOverride.presetId.trim()
      ? configOverride.presetId.trim()
      : activePresetId;
    const activePerson = personOverride || selectedPerson || {
      name: 'Name',
      title: 'Title',
      mirrored: false,
      genericTextByPresetId: {},
    };
    const mainText = activePerson.name || 'Name';
    const subText = activePerson.title || 'Title';

    const mainFontPx = 60 * scale;
    const subFontPx = 40 * scale;
    const mainFont = `bold ${mainFontPx}px Arial`;
    const subFont = `${subFontPx}px Arial`;

    let mainTextWidth = 300 * scale;
    let subTextWidth = 200 * scale;
    if (ctx) {
      ctx.font = mainFont;
      mainTextWidth = ctx.measureText(mainText).width;
      ctx.font = subFont;
      subTextWidth = ctx.measureText(subText).width;
    }

    const classic = {
      radius: clamp(withDefaultNumber(configOverride.classicRadius, 10), 0, 60) * scale,
      accentWidth: clamp(withDefaultNumber(configOverride.classicAccentWidth, 15), 0, 80) * scale,
      textPaddingX: clamp(withDefaultNumber(configOverride.classicTextPaddingX, 40), 10, 120) * scale,
      mainTextY: 60 * scale,
      subTextY: 100 * scale,
    };
    const minimal = {
      nameHeight: clamp(withDefaultNumber(configOverride.minimalNameHeight, 70), 40, 140) * scale,
      titleHeight: clamp(withDefaultNumber(configOverride.minimalTitleHeight, 50), 30, 120) * scale,
      textPaddingX: clamp(withDefaultNumber(configOverride.minimalTextPaddingX, 20), 10, 80) * scale,
    };
    minimal.nameWidth = mainTextWidth + minimal.textPaddingX * 2;
    minimal.titleWidth = subTextWidth + minimal.textPaddingX * 2;
    minimal.nameTextY = minimal.nameHeight * 0.78;
    minimal.titleTextY = minimal.nameHeight + minimal.titleHeight * 0.8;

    const modern = {
      skewOffset: clamp(withDefaultNumber(configOverride.modernSkewOffset, 50), 0, 120) * scale,
      accentOffset: clamp(withDefaultNumber(configOverride.modernAccentOffset, 30), 0, 80) * scale,
      accentWidth: clamp(withDefaultNumber(configOverride.modernAccentWidth, 20), 0, 80) * scale,
      textX: clamp(withDefaultNumber(configOverride.modernTextX, 80), 20, 200) * scale,
      mainTextY: 60 * scale,
      subTextY: 100 * scale,
      skewFactor: 0.2,
    };
    const edgeLink = {
      barX: -160 * scale,
      barWidth: Math.max(mainTextWidth, subTextWidth) + 160 * scale,
      bodyHeight: 118 * scale,
      tipX: Math.max(mainTextWidth, subTextWidth),
      tipWidth: 60 * scale,
      tipSkewX: -36 * scale,
      bodyWidth: Math.max(mainTextWidth, subTextWidth) + 220 * scale,
      accentX: -160 * scale,
      accentWidth: 18 * scale,
      textX: -92 * scale,
      mainTextY: 60 * scale,
      subTextY: 100 * scale,
    };
    const logoBadge = {
      bodyX: 64 * scale,
      bodyWidth: Math.max(mainTextWidth, subTextWidth) + 188 * scale,
      bodyHeight: 120 * scale,
      bodyRadius: 0,
      accentX: 82 * scale,
      accentY: 12 * scale,
      accentWidth: 12 * scale,
      accentHeight: 96 * scale,
      accentRadius: 6 * scale,
      logoRingX: 0,
      logoRingY: 10 * scale,
      logoRingSize: 100 * scale,
      logoPlateX: -72 * scale,
      logoPlateY: 0,
      logoPlateSize: 120 * scale,
      logoImageX: -54 * scale,
      logoImageY: 18 * scale,
      logoImageWidth: 84 * scale,
      logoImageHeight: 84 * scale,
      textX: 132 * scale,
      mainTextY: 60 * scale,
      subTextY: 100 * scale,
    };

    const classicBodyWidth = Math.max(mainTextWidth, subTextWidth) + classic.textPaddingX + 20 * scale;
    const classicBodyHeight = 120 * scale;
    const modernBodyWidth = Math.max(mainTextWidth, subTextWidth) + modern.textX + 20 * scale;
    const modernBodyHeight = 120 * scale;

    const isMinimal = configOverride.preset === 'minimal-box';
    const isModern = configOverride.preset === 'modern-accent';
    const isEdgeLink = configOverride.preset === 'edge-link';
    const isLogoBadge = configOverride.preset === 'logo-badge';
    const templateShapeVisible = configOverride.templateShapeVisible !== false;
    const templateMainTextVisible = configOverride.templateMainTextVisible !== false;
    const templateSubTextVisible = configOverride.templateSubTextVisible !== false;

    let templateShapeBounds;
    let mainTextBounds;
    let subTextBounds;
    let totalWidth;
    let totalHeight;

    if (isMinimal) {
      templateShapeBounds = rectToBounds(0, 0, Math.max(minimal.nameWidth, minimal.titleWidth), minimal.nameHeight + minimal.titleHeight);
      mainTextBounds = rectToBounds(
        minimal.textPaddingX,
        minimal.nameTextY - mainFontPx,
        mainTextWidth,
        mainFontPx + 10 * scale,
      );
      subTextBounds = rectToBounds(
        minimal.textPaddingX,
        minimal.titleTextY - subFontPx,
        subTextWidth,
        subFontPx + 10 * scale,
      );
      totalWidth = templateShapeBounds.width;
      totalHeight = templateShapeBounds.height;
    } else if (isModern) {
      const stripBounds = getBoundsFromPoints([
        { x: modern.skewOffset, y: 0 },
        { x: modern.skewOffset + modernBodyWidth, y: 0 },
        { x: modern.skewOffset + modernBodyWidth - modern.skewFactor * modernBodyHeight, y: modernBodyHeight },
        { x: modern.skewOffset - modern.skewFactor * modernBodyHeight, y: modernBodyHeight },
      ]);
      const accentBounds = getBoundsFromPoints([
        { x: modern.accentOffset, y: 0 },
        { x: modern.accentOffset + modern.accentWidth, y: 0 },
        { x: modern.accentOffset + modern.accentWidth - modern.skewFactor * modernBodyHeight, y: modernBodyHeight },
        { x: modern.accentOffset - modern.skewFactor * modernBodyHeight, y: modernBodyHeight },
      ]);
      templateShapeBounds = unionBounds([stripBounds, accentBounds]);
      mainTextBounds = rectToBounds(modern.textX, modern.mainTextY - mainFontPx, mainTextWidth, mainFontPx + 10 * scale);
      subTextBounds = rectToBounds(modern.textX, modern.subTextY - subFontPx, subTextWidth, subFontPx + 10 * scale);
      totalWidth = modernBodyWidth + modern.skewOffset;
      totalHeight = modernBodyHeight;
    } else if (isEdgeLink) {
      const bodyBounds = rectToBounds(edgeLink.barX, 0, edgeLink.barWidth, edgeLink.bodyHeight);
      const accentBounds = rectToBounds(edgeLink.accentX, 0, edgeLink.accentWidth, edgeLink.bodyHeight);
      const tipBounds = getBoundsFromPoints(getParallelogramPoints(
        edgeLink.tipX,
        0,
        edgeLink.tipWidth,
        edgeLink.bodyHeight,
        edgeLink.tipSkewX,
      ));
      templateShapeBounds = unionBounds([bodyBounds, accentBounds, tipBounds]);
      mainTextBounds = rectToBounds(edgeLink.textX, edgeLink.mainTextY - mainFontPx, mainTextWidth, mainFontPx + 10 * scale);
      subTextBounds = rectToBounds(edgeLink.textX, edgeLink.subTextY - subFontPx, subTextWidth, subFontPx + 10 * scale);
      totalWidth = templateShapeBounds.width;
      totalHeight = templateShapeBounds.height;
    } else if (isLogoBadge) {
      const bodyBounds = rectToBounds(logoBadge.bodyX, 0, logoBadge.bodyWidth, logoBadge.bodyHeight);
      const accentBounds = rectToBounds(
        logoBadge.accentX,
        logoBadge.accentY,
        logoBadge.accentWidth,
        logoBadge.accentHeight,
      );
      const logoPlateBounds = rectToBounds(
        logoBadge.logoPlateX,
        logoBadge.logoPlateY,
        logoBadge.logoPlateSize,
        logoBadge.logoPlateSize,
      );
      templateShapeBounds = unionBounds([bodyBounds, accentBounds, logoPlateBounds]);
      mainTextBounds = rectToBounds(logoBadge.textX, logoBadge.mainTextY - mainFontPx, mainTextWidth, mainFontPx + 10 * scale);
      subTextBounds = rectToBounds(logoBadge.textX, logoBadge.subTextY - subFontPx, subTextWidth, subFontPx + 10 * scale);
      totalWidth = templateShapeBounds.width;
      totalHeight = templateShapeBounds.height;
    } else {
      templateShapeBounds = rectToBounds(0, 0, classicBodyWidth, classicBodyHeight);
      mainTextBounds = rectToBounds(classic.textPaddingX, classic.mainTextY - mainFontPx, mainTextWidth, mainFontPx + 10 * scale);
      subTextBounds = rectToBounds(classic.textPaddingX, classic.subTextY - subFontPx, subTextWidth, subFontPx + 10 * scale);
      totalWidth = classicBodyWidth;
      totalHeight = classicBodyHeight;
    }

    const templateSelectionBounds = unionBounds([templateShapeBounds, mainTextBounds, subTextBounds]);
    const visibleTemplateBounds = [
      templateShapeVisible ? templateShapeBounds : null,
      templateMainTextVisible ? mainTextBounds : null,
      templateSubTextVisible ? subTextBounds : null,
    ].filter(Boolean);
    const templateVisibleBounds = visibleTemplateBounds.length ? unionBounds(visibleTemplateBounds) : null;
    const rotationPivot = {
      x: templateShapeBounds.minX + templateShapeBounds.width / 2,
      y: templateShapeBounds.minY + templateShapeBounds.height / 2,
    };

    const sourcePrimitives = Array.isArray(configOverride.shapePrimitives)
      ? configOverride.shapePrimitives
      : [];
    const linkedSourceTextIds = new Set(
      sourcePrimitives.flatMap((shape) => {
        const type = shape?.type || 'rect';
        const layerKind = shape?.layerKind === 'text'
          ? 'text'
          : (shape?.layerKind === 'image' || type === 'image' ? 'image' : 'shape');
        const binding = sanitizeAutoSizeBinding(shape?.autoSizeBinding, { type, layerKind });
        if (!binding || binding.enabled === false) return [];
        return getAutoSizeBindingSourceIds(binding);
      }),
    );
    const buildPrimitiveMetricsList = (primitiveSource) => primitiveSource.map((shape, index) => {
      const primitiveId = shape?.id || `shape-${index}`;
      const type = shape?.type || 'rect';
      const width = Math.max(8, withDefaultNumber(shape?.width, type === 'circle' ? 90 : 220));
      const height = type === 'circle'
        ? width
        : Math.max(1, withDefaultNumber(shape?.height, type === 'line' ? 4 : 80));
      const x = withDefaultNumber(shape?.x, 60);
      const y = withDefaultNumber(shape?.y, 40);
      const strokeWidth = clamp(withDefaultNumber(shape?.strokeWidth, 3), 1, 80);
      const primitiveRotation = clamp(withDefaultNumber(shape?.rotation, 0), -180, 180);
      const skewX = clamp(withDefaultNumber(shape?.skewX, 0), -1920, 1920);
      const layerKind = shape?.layerKind === 'text'
        ? 'text'
        : (shape?.layerKind === 'image' || type === 'image' ? 'image' : 'shape');
      const textSource = normalizeTextSource(shape?.textSource, 'main');
      const textPaddingX = withDefaultNumber(shape?.textPaddingX, 0);
      const textFontRatio = withDefaultNumber(shape?.textFontRatio, textSource === 'main' ? 0.8 : 0.7);
      const textBaselineRatio = withDefaultNumber(shape?.textBaselineRatio, 0.78);
      const textFontFamily = sanitizeTextLayerFontFamily(shape?.textFontFamily);
      const textValue = typeof shape?.textValue === 'string' ? shape.textValue : '';
      const hideFromPeoplePanel = Boolean(shape?.hideFromPeoplePanel);
      const autoSizeBinding = sanitizeAutoSizeBinding(shape?.autoSizeBinding, { type, layerKind });

      let baseBounds;
      let shapeVisualBounds;
      if (type === 'line') {
        const lineHeight = Math.max(strokeWidth, height);
        baseBounds = rectToBounds(x, y - lineHeight / 2, width, lineHeight);
        shapeVisualBounds = baseBounds;
      } else if (type === 'circle') {
        baseBounds = rectToBounds(x, y, width, width);
        shapeVisualBounds = baseBounds;
      } else if (type === 'parallelogram') {
        baseBounds = rectToBounds(x, y, width, height);
        shapeVisualBounds = getBoundsFromPoints(getParallelogramPoints(x, y, width, height, skewX));
      } else {
        baseBounds = rectToBounds(x, y, width, height);
        shapeVisualBounds = baseBounds;
      }

      const center = { x: baseBounds.minX + baseBounds.width / 2, y: baseBounds.minY + baseBounds.height / 2 };
      const shapeLocalBounds = rotateBounds(shapeVisualBounds, center, toRadians(primitiveRotation));
      const boundsWithGlobal = rotateBounds(shapeLocalBounds, rotationPivot, globalRotationRadians);
      const legacyColorRole = normalizePrimitiveColorRole(shape?.colorRole, 'primary');
      const fillColorMode = normalizePrimitiveColorMode(shape?.fillColorMode, 'theme');
      const fillColorRole = normalizePrimitiveColorRole(shape?.fillColorRole, legacyColorRole);
      const fillColorValue = normalizePrimitiveColorValue(shape?.fillColorValue, configOverride.colorPrimary);
      const strokeColorMode = normalizePrimitiveColorMode(shape?.strokeColorMode, 'theme');
      const strokeColorRole = normalizePrimitiveColorRole(shape?.strokeColorRole, legacyColorRole);
      const strokeColorValue = normalizePrimitiveColorValue(shape?.strokeColorValue, configOverride.colorPrimary);
      const primitiveMainTextVisible = shape?.mainTextVisible === true;
      const primitiveSubTextVisible = shape?.subTextVisible === true;
      const primitiveVisible = shape?.visible !== false;
      const resolvedTextValue = textSource === 'main'
        ? mainText
        : textSource === 'sub'
          ? subText
          : hideFromPeoplePanel
            ? textValue
          : resolvePersonGenericTextValue({
            person: activePerson,
            presetId: resolvedPresetId,
            primitiveId,
            fallbackText: textValue,
          });
      const textLayout = layerKind === 'text'
        ? buildStandaloneTextLayerLayout({
          ctx,
          baseBounds,
          textSource,
          text: resolvedTextValue,
          mainFontPx,
          subFontPx,
          scale,
          textPaddingX,
          textFontRatio,
          textBaselineRatio,
          textFontFamily,
          fitToWidth: !linkedSourceTextIds.has(primitiveId),
        })
        : buildPrimitiveTextLayout({
          ctx,
          shapeType: type,
          baseBounds,
          showMain: primitiveMainTextVisible,
          showSub: primitiveSubTextVisible,
          mainText,
          subText,
          mainFontPx,
          subFontPx,
          scale,
        });
      const textLocalBounds = textLayout?.localBounds
        ? rotateBounds(textLayout.localBounds, center, toRadians(primitiveRotation))
        : null;
      const hitLocalBounds = layerKind === 'text'
        ? (textLocalBounds || shapeLocalBounds)
        : unionBounds([shapeLocalBounds, textLocalBounds]);
      const hitBoundsWithGlobal = rotateBounds(hitLocalBounds, rotationPivot, globalRotationRadians);
      return {
        ...shape,
        id: primitiveId,
        type,
        x,
        y,
        width: type === 'circle' ? width : baseBounds.width,
        height: type === 'circle' ? width : (type === 'line' ? Math.max(strokeWidth, height) : baseBounds.height),
        strokeWidth,
        rotation: primitiveRotation,
        cornerRadius: clamp(withDefaultNumber(shape?.cornerRadius, 16), 0, 540),
        skewX,
        fill: layerKind === 'text' ? false : (shape?.fill !== false),
        stroke: layerKind === 'text' ? false : Boolean(shape?.stroke),
        visible: primitiveVisible,
        layerKind,
        textSource,
        textValue,
        hideFromPeoplePanel,
        textPaddingX,
        textFontRatio,
        textBaselineRatio,
        textFontFamily,
        templateFamily: shape?.templateFamily || null,
        templatePart: shape?.templatePart || null,
        autoSizeBinding,
        colorRole: legacyColorRole,
        fillColorMode,
        fillColorRole,
        fillColorValue,
        strokeColorMode,
        strokeColorRole,
        strokeColorValue,
        mainTextVisible: primitiveMainTextVisible,
        subTextVisible: primitiveSubTextVisible,
        textLayout,
        // Export the rotated text content footprint so auto-size bindings follow the
        // actual on-canvas text bounds (especially during rotated text scaling).
        contentLocalBounds: layerKind === 'text' ? (textLocalBounds || textLayout?.localBounds || null) : null,
        baseBounds,
        localBounds: shapeLocalBounds,
        hitLocalBounds,
        boundsWithGlobal,
        hitBoundsWithGlobal,
      };
    });

    const provisionalPrimitives = buildPrimitiveMetricsList(sourcePrimitives);
    const textContentBoundsById = new Map(
      provisionalPrimitives
        .filter((shape) => shape?.layerKind === 'text' && shape?.id && shape?.contentLocalBounds)
        .map((shape) => [shape.id, shape.contentLocalBounds]),
    );

    const primitivesSourceWithBindings = sourcePrimitives.map((shape, index) => {
      if (!shape || typeof shape !== 'object') return shape;
      const provisional = provisionalPrimitives[index] || null;
      const binding = sanitizeAutoSizeBinding(shape.autoSizeBinding, provisional || shape);
      if (!binding) return shape;
      const normalizedShape = { ...shape, autoSizeBinding: binding };
      if (binding.enabled === false) return normalizedShape;
      const resolvedSourceBounds = resolveBindingSourceBounds({
        binding,
        textBoundsById: textContentBoundsById,
      });
      if (!resolvedSourceBounds.unionBounds) return normalizedShape;
      return applyAutoSizeBindingToShape({
        shape: normalizedShape,
        binding,
        sourceTextBounds: resolvedSourceBounds.unionBounds,
        primarySourceTextBounds: resolvedSourceBounds.primaryBounds || resolvedSourceBounds.unionBounds,
        targetGeometry: { baseBounds: provisional?.baseBounds || null },
      });
    });

    const primitives = buildPrimitiveMetricsList(primitivesSourceWithBindings);

    const templateSelectionBoundsWithGlobal = rotateBounds(templateSelectionBounds, rotationPivot, globalRotationRadians);
    const templateVisibleBoundsWithGlobal = templateVisibleBounds
      ? rotateBounds(templateVisibleBounds, rotationPivot, globalRotationRadians)
      : null;
    const renderBounds = unionBounds([
      templateVisibleBoundsWithGlobal,
      ...primitives.map((shape) => (
        shape.visible === false ? null : (shape.hitBoundsWithGlobal || shape.boundsWithGlobal)
      )),
    ]);

    return {
      scale,
      rotation,
      globalRotationRadians,
      rotationPivot,
      mainText,
      subText,
      mainFont,
      subFont,
      mainFontPx,
      subFontPx,
      mainTextWidth,
      subTextWidth,
      totalWidth,
      totalHeight,
      templateShapeVisible,
      templateMainTextVisible,
      templateSubTextVisible,
      baseBoxWidth: isMinimal
        ? templateShapeBounds.width
        : (isModern
          ? modernBodyWidth
          : (isEdgeLink ? edgeLink.bodyWidth : (isLogoBadge ? logoBadge.bodyWidth : classicBodyWidth))),
      baseBoxHeight: isMinimal
        ? templateShapeBounds.height
        : (isModern
          ? modernBodyHeight
          : (isEdgeLink ? edgeLink.bodyHeight : (isLogoBadge ? logoBadge.bodyHeight : classicBodyHeight))),
      renderBounds,
      templateShapeBounds,
      templateVisibleBounds,
      templateVisibleBoundsWithGlobal,
      templateSelectionBounds,
      templateSelectionBoundsWithGlobal,
      templateHitBounds: templateSelectionBounds,
      templateBoundsWithGlobal: templateSelectionBoundsWithGlobal,
      textBounds: {
        main: mainTextBounds,
        sub: subTextBounds,
      },
      classic,
      minimal,
      modern,
      edgeLink,
      logoBadge,
      primitives,
    };
  }, [activePresetId, config, selectedPerson]);

  const getDurations = useCallback((targetFps = fps, configOverride = config) => {
    const resolvedFps = Math.max(1, targetFps);
    const {
      transitionInDuration,
      transitionOutDuration,
      stillDuration,
      transitionInType,
      transitionOutType,
      fadeInDuration,
      fadeOutDuration
    } = configOverride;
    const transitionInFrames = Math.max(1, Math.round(transitionInDuration * resolvedFps));
    const transitionOutFrames = Math.max(1, Math.round(transitionOutDuration * resolvedFps));
    const stillFrames = Math.max(0, Math.round(stillDuration * resolvedFps));
    const resolvedFadeInSeconds = transitionInType === 'fade'
      ? transitionInDuration
      : Math.min(transitionInDuration, fadeInDuration ?? transitionInDuration / 2);
    const resolvedFadeOutSeconds = transitionOutType === 'fade'
      ? transitionOutDuration
      : Math.min(transitionOutDuration, fadeOutDuration ?? transitionOutDuration / 2);
    const fadeInFrames = Math.max(1, Math.round(Math.max(resolvedFadeInSeconds, MIN_FADE) * resolvedFps));
    const fadeOutFrames = Math.max(1, Math.round(Math.max(resolvedFadeOutSeconds, MIN_FADE) * resolvedFps));
    const totalFrames = transitionInFrames + transitionOutFrames + stillFrames;
    return { transitionInFrames, transitionOutFrames, stillFrames, fadeInFrames, fadeOutFrames, totalFrames };
  }, [config, fps]);

  // --- RENDERING ENGINE ---

  // Helper to draw rounded rects
  const roundRect = (ctx, x, y, w, h, r, color, mode = 'fill') => {
    const radius = clamp(r, 0, Math.min(w, h) / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    if (mode === 'stroke') {
      ctx.strokeStyle = color;
      ctx.stroke();
      return;
    }
    ctx.fillStyle = color;
    ctx.fill();
  };

  const drawReadableText = (ctx, text, x, y, width, mirrored) => {
    if (!mirrored) {
      ctx.fillText(text, x, y);
      return;
    }
    ctx.save();
    ctx.translate(x + width, 0);
    ctx.scale(-1, 1);
    ctx.fillText(text, 0, y);
    ctx.restore();
  };

  const drawReadableImage = (ctx, image, x, y, width, height, mirrored) => {
    if (!mirrored) {
      ctx.drawImage(image, x, y, width, height);
      return;
    }
    ctx.save();
    ctx.translate(x + width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(image, 0, y, width, height);
    ctx.restore();
  };

  const getCachedImage = useCallback((imageSrc) => {
    if (!imageSrc) return null;
    const cache = imageCacheRef.current;
    const existing = cache.get(imageSrc);
    if (existing?.status === 'loaded') return existing.image;
    if (existing?.status === 'loading') return null;

    const image = new Image();
    cache.set(imageSrc, { status: 'loading', image: null });
    image.onload = () => {
      cache.set(imageSrc, { status: 'loaded', image });
    };
    image.onerror = () => {
      cache.set(imageSrc, { status: 'error', image: null });
    };
    image.src = imageSrc;
    return null;
  }, []);

  const getRenderScratchSurface = useCallback(() => {
    const current = renderScratchCanvasRef.current || {};
    let { canvas, ctx } = current;
    if (!canvas) {
      canvas = document.createElement('canvas');
    }
    if (!ctx) {
      ctx = canvas.getContext('2d');
    }
    if (!canvas || !ctx) return null;
    if (canvas.width !== CANVAS_WIDTH) canvas.width = CANVAS_WIDTH;
    if (canvas.height !== CANVAS_HEIGHT) canvas.height = CANVAS_HEIGHT;
    renderScratchCanvasRef.current = { canvas, ctx };
    return renderScratchCanvasRef.current;
  }, [CANVAS_HEIGHT, CANVAS_WIDTH]);

  const drawFrame = useCallback((ctx, frame, personOverride, visualConfigOverride = null, options = {}) => {
    const renderConfig = visualConfigOverride
      ? { ...config, ...visualConfigOverride }
      : config;

    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    if (renderConfig.backgroundMode === 'green') {
      const keyColor = renderConfig.backgroundKeyColor || '#00FF00';
      ctx.fillStyle = keyColor;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }

    const staticStill = Boolean(options.staticStill);
    const {
      transitionInFrames,
      transitionOutFrames,
      stillFrames,
      fadeInFrames,
      fadeOutFrames,
      totalFrames,
    } = getDurations(undefined, renderConfig);

    const metrics = getDesignMetrics(ctx, personOverride, renderConfig);
    const {
      baseBoxWidth: boxWidth,
      baseBoxHeight: boxHeight,
      renderBounds,
      classic,
      minimal,
      modern,
      edgeLink,
      logoBadge,
      mainText,
      subText,
      mainTextWidth,
      subTextWidth,
      mainFont,
      subFont,
      scale,
      templateShapeVisible,
      templateMainTextVisible,
      templateSubTextVisible,
    } = metrics;
    const isMirrored = personOverride?.mirrored === true;

    let drawX = renderConfig.posX;
    const drawY = renderConfig.posY;
    let fadeProgress = 1;

    if (!staticStill) {
      const easeOut = (t) => 1 - Math.pow(1 - t, 3);
      const easeIn = (t) => t * t * t;
      const fadeInActive = renderConfig.fadeIn || renderConfig.transitionInType === 'fade';
      const fadeOutActive = renderConfig.fadeOut || renderConfig.transitionOutType === 'fade';
      const atExit = frame >= transitionInFrames + stillFrames;
      const fadeInCurve = renderConfig.fadeInCurve || 'linear';
      const fadeOutCurve = renderConfig.fadeOutCurve || 'linear';
      const slideOvershoot = 120 * scale;
      const startXLeft = -renderBounds.maxX - slideOvershoot;
      const startXRight = CANVAS_WIDTH - renderBounds.minX + slideOvershoot;
      const entryProgress = Math.min(1, frame / transitionInFrames);
      const exitProgress = atExit
        ? Math.min(1, (frame - transitionInFrames - stillFrames) / transitionOutFrames)
        : 0;

      if (frame < transitionInFrames) {
        if (renderConfig.transitionInType === 'slide-left') {
          drawX = startXLeft + (renderConfig.posX - startXLeft) * easeOut(entryProgress);
        } else if (renderConfig.transitionInType === 'slide-right') {
          drawX = startXRight + (renderConfig.posX - startXRight) * easeOut(entryProgress);
        }
      } else if (frame >= transitionInFrames + stillFrames && frame < totalFrames) {
        if (renderConfig.transitionOutType === 'slide-left') {
          drawX = renderConfig.posX + (startXLeft - renderConfig.posX) * easeIn(exitProgress);
        } else if (renderConfig.transitionOutType === 'slide-right') {
          drawX = renderConfig.posX + (startXRight - renderConfig.posX) * easeIn(exitProgress);
        }
      }

      if (fadeInActive && frame < fadeInFrames) {
        fadeProgress = applyFadeCurve(frame / fadeInFrames, fadeInCurve);
      }
      if (fadeOutActive && atExit) {
        const fadeStart = transitionInFrames + stillFrames + Math.max(0, transitionOutFrames - fadeOutFrames);
        const fadeFrame = frame - fadeStart;
        if (fadeFrame >= 0 && fadeFrame < fadeOutFrames) {
          fadeProgress = Math.min(
            fadeProgress,
            1 - applyFadeCurve(fadeFrame / fadeOutFrames, fadeOutCurve),
          );
        } else if (frame >= fadeStart + fadeOutFrames) {
          fadeProgress = 0;
        }
      }
    }

    const renderLowerThirdScene = (targetCtx) => {
      const drawTemplateShapeLayer = () => {
        if (!templateShapeVisible) return;
        if (renderConfig.preset === 'minimal-box') {
          roundRect(targetCtx, 0, 0, minimal.nameWidth, minimal.nameHeight, 0, renderConfig.colorPrimary);
          roundRect(targetCtx, 0, minimal.nameHeight, minimal.titleWidth, minimal.titleHeight, 0, renderConfig.colorSecondary);
          return;
        }
        if (renderConfig.preset === 'edge-link') {
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(edgeLink.barX, 0, edgeLink.barWidth, edgeLink.bodyHeight);
          traceParallelogramPath(
            targetCtx,
            edgeLink.tipX,
            0,
            edgeLink.tipWidth,
            edgeLink.bodyHeight,
            edgeLink.tipSkewX,
          );
          targetCtx.fill();
          targetCtx.fillStyle = renderConfig.colorPrimary;
          targetCtx.fillRect(edgeLink.accentX, 0, edgeLink.accentWidth, edgeLink.bodyHeight);
          return;
        }
        if (renderConfig.preset === 'logo-badge') {
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(
            logoBadge.bodyX,
            0,
            logoBadge.bodyWidth,
            logoBadge.bodyHeight,
          );
          roundRect(
            targetCtx,
            logoBadge.accentX,
            logoBadge.accentY,
            logoBadge.accentWidth,
            logoBadge.accentHeight,
            logoBadge.accentRadius,
            renderConfig.colorPrimary,
          );
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(
            logoBadge.logoPlateX,
            logoBadge.logoPlateY,
            logoBadge.logoPlateSize,
            logoBadge.logoPlateSize,
          );

          const logoImage = getCachedImage(TEMPLATE_PRESET_LOGO_SRC);
          if (logoImage) {
            drawReadableImage(
              targetCtx,
              logoImage,
              logoBadge.logoImageX,
              logoBadge.logoImageY,
              logoBadge.logoImageWidth,
              logoBadge.logoImageHeight,
              isMirrored,
            );
          }
          return;
        }
        if (renderConfig.preset === 'modern-accent') {
          targetCtx.save();
          targetCtx.transform(1, 0, -modern.skewFactor, 1, 0, 0);
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(modern.skewOffset, 0, boxWidth, boxHeight);
          targetCtx.fillStyle = renderConfig.colorPrimary;
          targetCtx.fillRect(modern.accentOffset, 0, modern.accentWidth, boxHeight);
          targetCtx.restore();
          return;
        }
        roundRect(targetCtx, 0, 0, boxWidth, boxHeight, classic.radius, renderConfig.colorSecondary);
        roundRect(targetCtx, 0, 0, classic.accentWidth, boxHeight, classic.radius, renderConfig.colorPrimary);
      };

      const drawPrimitiveLayer = (shape) => {
        if (!shape || shape.visible === false) return;
        const fillColor = resolvePrimitiveFillColor(shape, renderConfig);
        const strokeColor = resolvePrimitiveStrokeColor(shape, renderConfig);
        const centerX = shape.baseBounds.minX + shape.baseBounds.width / 2;
        const centerY = shape.baseBounds.minY + shape.baseBounds.height / 2;
        targetCtx.save();
        targetCtx.translate(centerX, centerY);
        targetCtx.rotate(toRadians(shape.rotation));
        targetCtx.translate(-centerX, -centerY);

        if (shape.type === 'image') {
          if (shape.imageData && shape.width > 0 && shape.height > 0) {
            const imageObj = getCachedImage(shape.imageData);
            if (imageObj) {
              drawReadableImage(targetCtx, imageObj, shape.x, shape.y, shape.width, shape.height, isMirrored);
            }
          }
        } else if (shape.layerKind !== 'text') {
          if (shape.type === 'line') {
            targetCtx.beginPath();
            targetCtx.moveTo(shape.x, shape.y);
            targetCtx.lineTo(shape.x + shape.width, shape.y);
            targetCtx.strokeStyle = strokeColor;
            targetCtx.lineWidth = shape.strokeWidth;
            targetCtx.lineCap = 'round';
            targetCtx.stroke();
          } else if (shape.type === 'circle') {
            const radius = shape.width / 2;
            const cx = shape.x + radius;
            const cy = shape.y + radius;
            targetCtx.beginPath();
            targetCtx.arc(cx, cy, radius, 0, Math.PI * 2);
            if (shape.fill) {
              targetCtx.fillStyle = fillColor;
              targetCtx.fill();
            }
            if (shape.stroke) {
              targetCtx.strokeStyle = strokeColor;
              targetCtx.lineWidth = shape.strokeWidth;
              targetCtx.stroke();
            }
          } else if (shape.type === 'parallelogram') {
            traceParallelogramPath(targetCtx, shape.x, shape.y, shape.width, shape.height, withDefaultNumber(shape.skewX, 0));
            if (shape.fill) {
              targetCtx.fillStyle = fillColor;
              targetCtx.fill();
            }
            if (shape.stroke) {
              traceParallelogramPath(targetCtx, shape.x, shape.y, shape.width, shape.height, withDefaultNumber(shape.skewX, 0));
              targetCtx.strokeStyle = strokeColor;
              targetCtx.lineWidth = shape.strokeWidth;
              targetCtx.stroke();
            }
          } else {
            const radius = shape.type === 'roundedRect' ? shape.cornerRadius : 0;
            if (shape.fill) {
              roundRect(targetCtx, shape.x, shape.y, shape.width, shape.height, radius, fillColor);
            }
            if (shape.stroke) {
              targetCtx.lineWidth = shape.strokeWidth;
              roundRect(targetCtx, shape.x, shape.y, shape.width, shape.height, radius, strokeColor, 'stroke');
            }
          }
        }

        if (shape.textLayout?.lines?.length) {
          for (const line of shape.textLayout.lines) {
            targetCtx.fillStyle = shape.layerKind === 'text'
              ? fillColor
              : (line.kind === 'main' ? renderConfig.colorTextUpper : renderConfig.colorTextLower);
            targetCtx.font = line.font;
            drawReadableText(targetCtx, line.text, line.x, line.y, line.width, isMirrored);
          }
        }

        targetCtx.restore();
      };

      const drawTemplateMainTextLayer = () => {
        if (!templateMainTextVisible) return;
        targetCtx.fillStyle = renderConfig.colorTextUpper;
        targetCtx.font = mainFont;
        if (renderConfig.preset === 'minimal-box') {
          drawReadableText(targetCtx, mainText, minimal.textPaddingX, minimal.nameTextY, mainTextWidth, isMirrored);
        } else if (renderConfig.preset === 'edge-link') {
          drawReadableText(targetCtx, mainText, edgeLink.textX, edgeLink.mainTextY, mainTextWidth, isMirrored);
        } else if (renderConfig.preset === 'logo-badge') {
          drawReadableText(targetCtx, mainText, logoBadge.textX, logoBadge.mainTextY, mainTextWidth, isMirrored);
        } else if (renderConfig.preset === 'modern-accent') {
          drawReadableText(targetCtx, mainText, modern.textX, modern.mainTextY, mainTextWidth, isMirrored);
        } else {
          drawReadableText(targetCtx, mainText, classic.textPaddingX, classic.mainTextY, mainTextWidth, isMirrored);
        }
      };

      const drawTemplateSubTextLayer = () => {
        if (!templateSubTextVisible) return;
        targetCtx.fillStyle = renderConfig.colorTextLower;
        targetCtx.font = subFont;
        if (renderConfig.preset === 'minimal-box') {
          drawReadableText(targetCtx, subText, minimal.textPaddingX, minimal.titleTextY, subTextWidth, isMirrored);
        } else if (renderConfig.preset === 'edge-link') {
          drawReadableText(targetCtx, subText, edgeLink.textX, edgeLink.subTextY, subTextWidth, isMirrored);
        } else if (renderConfig.preset === 'logo-badge') {
          drawReadableText(targetCtx, subText, logoBadge.textX, logoBadge.subTextY, subTextWidth, isMirrored);
        } else if (renderConfig.preset === 'modern-accent') {
          drawReadableText(targetCtx, subText, modern.textX, modern.subTextY, subTextWidth, isMirrored);
        } else {
          drawReadableText(targetCtx, subText, classic.textPaddingX, classic.subTextY, subTextWidth, isMirrored);
        }
      };

      targetCtx.save();
      if (isMirrored) {
        targetCtx.translate(CANVAS_WIDTH, 0);
        targetCtx.scale(-1, 1);
      }
      targetCtx.translate(drawX + metrics.rotationPivot.x, drawY + metrics.rotationPivot.y);
      targetCtx.rotate(metrics.globalRotationRadians);
      targetCtx.translate(-metrics.rotationPivot.x, -metrics.rotationPivot.y);

      const primitiveLayerMap = new Map(
        metrics.primitives.map((shape) => [primitiveLayerId(shape.id), shape]),
      );
      const renderLayerOrder = normalizePresetLayerOrder(renderConfig.layerOrder, renderConfig.shapePrimitives);
      for (const layerId of renderLayerOrder) {
        if (layerId === TEMPLATE_SHAPE_LAYER_ID) {
          drawTemplateShapeLayer();
          continue;
        }
        if (layerId === TEMPLATE_MAIN_TEXT_LAYER_ID) {
          drawTemplateMainTextLayer();
          continue;
        }
        if (layerId === TEMPLATE_SUB_TEXT_LAYER_ID) {
          drawTemplateSubTextLayer();
          continue;
        }
        if (!layerIdToPrimitiveId(layerId)) continue;
        drawPrimitiveLayer(primitiveLayerMap.get(layerId));
      }

      targetCtx.restore();
    };

    if (fadeProgress <= 0) {
      return {
        drawX,
        drawY,
        metrics,
      };
    }

    if (fadeProgress >= 1) {
      renderLowerThirdScene(ctx);
      return {
        drawX,
        drawY,
        metrics,
      };
    }

    const scratchSurface = getRenderScratchSurface();
    if (!scratchSurface?.canvas || !scratchSurface?.ctx) {
      ctx.save();
      ctx.globalAlpha = fadeProgress;
      renderLowerThirdScene(ctx);
      ctx.restore();
      return {
        drawX,
        drawY,
        metrics,
      };
    }

    scratchSurface.ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    renderLowerThirdScene(scratchSurface.ctx);

    ctx.save();
    ctx.globalAlpha = fadeProgress;
    ctx.drawImage(scratchSurface.canvas, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.restore();

    return {
      drawX,
      drawY,
      metrics,
    };
  }, [
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    config,
    getCachedImage,
    getDurations,
    getDesignMetrics,
    getRenderScratchSurface,
  ]);

  // --- PREVIEW LOOP ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== 'function') return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    // Scale for high DPI
    // But for video export, we usually want exact 1920x1080 pixels
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    // Animation Loop
    const previewFps = Math.max(1, fps);
    const frameDurationMs = 1000 / previewFps;
    let startTime = performance.now();

    const animate = (now) => {
      if (!isRecording) {
        const { totalFrames } = getDurations();
        const pauseFrames = previewFps; // 1s pause between loops
        const loopWindow = totalFrames + pauseFrames;
        const elapsedFrames = Math.floor((now - startTime) / frameDurationMs);
        const loopedFrame = loopWindow ? elapsedFrames % loopWindow : 0;
        const activeFrame = loopedFrame < totalFrames ? loopedFrame : totalFrames;
        drawFrame(ctx, activeFrame, selectedPerson);
      }
      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(requestRef.current);
  }, [CANVAS_HEIGHT, CANVAS_WIDTH, drawFrame, fps, getDurations, isRecording, selectedPerson]);


  // --- EXPORT LOGIC ---
  const checkWebCodecsSupport = useCallback(async (targetFps = fps) => {
    const resolvedFps = Math.max(1, targetFps);
    const supportKey = `${resolvedFps}:${CANVAS_WIDTH}x${CANVAS_HEIGHT}`;
    if (webCodecsSupportRef.current?.key === supportKey && webCodecsSupportRef.current.supported !== null) {
      return webCodecsSupportRef.current.supported;
    }

    if (
      typeof VideoEncoder === 'undefined' ||
      typeof VideoFrame === 'undefined' ||
      typeof VideoEncoder.isConfigSupported !== 'function'
    ) {
      console.info('[LowerThirds] WebCodecs unavailable in this browser (missing APIs)');
      webCodecsSupportRef.current = { key: supportKey, supported: false };
      webCodecsAlphaConfigRef.current = null;
      webCodecsOpaqueConfigRef.current = null;
      return false;
    }

    let supportedAlpha = null;
    let supportedOpaque = null;

    const candidates = [
      { codec: 'vp09.00.10.08', width: CANVAS_WIDTH, height: CANVAS_HEIGHT, bitrate: 5_000_000, framerate: resolvedFps, alpha: 'keep' },
      { codec: 'vp9', width: CANVAS_WIDTH, height: CANVAS_HEIGHT, bitrate: 5_000_000, framerate: resolvedFps, alpha: 'keep' },
      { codec: 'vp09.00.10.08', width: CANVAS_WIDTH, height: CANVAS_HEIGHT, bitrate: 4_000_000, framerate: resolvedFps, alpha: 'keep' },
      // Fallbacks without alpha (will produce an opaque background)
      { codec: 'vp09.00.10.08', width: CANVAS_WIDTH, height: CANVAS_HEIGHT, bitrate: 5_000_000, framerate: resolvedFps, alpha: 'discard' },
      { codec: 'vp9', width: CANVAS_WIDTH, height: CANVAS_HEIGHT, bitrate: 5_000_000, framerate: resolvedFps, alpha: 'discard' },
      { codec: 'vp09.00.10.08', width: CANVAS_WIDTH, height: CANVAS_HEIGHT, bitrate: 4_000_000, framerate: resolvedFps, alpha: 'discard' },
    ];

    for (const cfg of candidates) {
      try {
        const support = await VideoEncoder.isConfigSupported(cfg);
        console.info('[LowerThirds] WebCodecs candidate', cfg, '->', support);
        if (support?.supported) {
          const resolved = { ...cfg, ...(support.config || {}) };
          if (resolved.alpha && resolved.alpha !== 'discard') {
            supportedAlpha = supportedAlpha || resolved;
          } else {
            supportedOpaque = supportedOpaque || resolved;
          }
          if (supportedAlpha && supportedOpaque) break;
        }
      } catch (err) {
        console.warn('[LowerThirds] WebCodecs candidate failed', cfg, err);
      }
    }

    webCodecsAlphaConfigRef.current = supportedAlpha;
    webCodecsOpaqueConfigRef.current = supportedOpaque;
    webCodecsSupportRef.current = {
      key: supportKey,
      supported: Boolean(supportedAlpha || supportedOpaque)
    };

    if (!webCodecsSupportRef.current.supported) {
      console.info('[LowerThirds] WebCodecs rejected all candidates; falling back to MediaRecorder');
    } else if (!supportedAlpha && supportedOpaque) {
      console.info('[LowerThirds] WebCodecs available but only without alpha; use green background for chroma key');
    }

    return webCodecsSupportRef.current.supported;
  }, [CANVAS_HEIGHT, CANVAS_WIDTH, fps]);

  const resetFfmpegRuntimeAfterTerminate = useCallback(() => {
    ffmpegRef.current = null;
    ffmpegLoadPromiseRef.current = null;
    ffmpegActiveJobRef.current = null;
    ffmpegProgressRelayRef.current = null;
    ffmpegLogRelayRef.current = null;
    setFfmpegRuntimeState({
      status: 'idle',
      hasProResSupport: null,
      errorReason: null,
      probeCompleted: false,
    });
  }, []);

  const ensureFfmpegWasmLoaded = useCallback(async () => {
    if (ffmpegRef.current) return ffmpegRef.current;
    if (ffmpegLoadPromiseRef.current) return ffmpegLoadPromiseRef.current;

    setFfmpegRuntimeState((prev) => ({
      ...prev,
      status: 'loading',
      errorReason: null,
    }));

    ffmpegLoadPromiseRef.current = (async () => {
      try {
        const ffmpegModule = await import('@ffmpeg/ffmpeg');
        const FFmpegCtor = ffmpegModule?.FFmpeg || ffmpegModule?.default?.FFmpeg || ffmpegModule?.default;
        if (!FFmpegCtor) {
          throw new Error('FFmpeg class was not found in @ffmpeg/ffmpeg');
        }

        const ffmpeg = new FFmpegCtor();
        ffmpeg.on('progress', (event) => {
          try {
            ffmpegProgressRelayRef.current?.(event);
          } catch (error) {
            console.warn('[LowerThirds] ffmpeg progress relay failed', error);
          }
        });
        ffmpeg.on('log', (event) => {
          try {
            ffmpegLogRelayRef.current?.(event);
          } catch (error) {
            console.warn('[LowerThirds] ffmpeg log relay failed', error);
          }
        });

        const coreURL = new URL(`${FFMPEG_CORE_BASE_PATH}/ffmpeg-core.js`, window.location.href).toString();
        const wasmURL = new URL(`${FFMPEG_CORE_BASE_PATH}/ffmpeg-core.wasm`, window.location.href).toString();
        const workerURL = new URL(`${FFMPEG_CORE_BASE_PATH}/ffmpeg-core.worker.js`, window.location.href).toString();

        try {
          await ffmpeg.load({ coreURL, wasmURL, workerURL });
        } catch (error) {
          // Stock single-thread @ffmpeg/core may not ship ffmpeg-core.worker.js; retry without workerURL.
          await ffmpeg.load({ coreURL, wasmURL });
        }

        ffmpegRef.current = ffmpeg;
        setFfmpegRuntimeState((prev) => ({
          ...prev,
          status: prev.probeCompleted
            ? (prev.hasProResSupport ? 'ready' : 'unsupported')
            : 'idle',
          errorReason: null,
        }));
        return ffmpeg;
      } catch (error) {
        console.error('[LowerThirds] Failed to load ffmpeg.wasm', error);
        ffmpegRef.current = null;
        ffmpegLoadPromiseRef.current = null;
        setFfmpegRuntimeState((prev) => ({
          ...prev,
          status: 'error',
          errorReason: 'Local ffmpeg core assets could not be loaded. Check public/vendor/ffmpeg setup.',
          hasProResSupport: null,
          probeCompleted: false,
        }));
        throw error;
      }
    })();

    return ffmpegLoadPromiseRef.current;
  }, []);

  const safeFfmpegDeleteFile = useCallback(async (ffmpeg, path) => {
    if (!ffmpeg || !path) return;
    try {
      await ffmpeg.deleteFile(path);
    } catch {
      // ignore cleanup failures
    }
  }, []);

  const safeFfmpegDeleteDir = useCallback(async (ffmpeg, path) => {
    if (!ffmpeg || !path) return;
    try {
      await ffmpeg.deleteDir(path);
    } catch {
      // ignore cleanup failures
    }
  }, []);

  const probeFfmpegMovProResSupport = useCallback(async () => {
    if (ffmpegRuntimeState.probeCompleted) {
      return Boolean(ffmpegRuntimeState.hasProResSupport);
    }

    const ffmpeg = await ensureFfmpegWasmLoaded();
    setFfmpegRuntimeState((prev) => ({
      ...prev,
      status: 'probing',
      errorReason: null,
    }));

    const probeDir = '/_mov_probe';
    const inputPath = `${probeDir}/in.png`;
    const outputPath = `${probeDir}/out.mov`;

    try {
      try {
        await ffmpeg.createDir(probeDir);
      } catch {
        // ignore if it already exists
      }

      const probePngBlob = dataUrlToBlob(FFMPEG_PROBE_PNG_DATA_URL);
      await ffmpeg.writeFile(inputPath, new Uint8Array(await probePngBlob.arrayBuffer()));

      const exitCode = await ffmpeg.exec([
        '-y',
        '-loop',
        '1',
        '-i',
        inputPath,
        '-frames:v',
        '1',
        '-c:v',
        'prores_ks',
        '-profile:v',
        '4',
        '-pix_fmt',
        'yuva444p10le',
        '-alpha_bits',
        '16',
        outputPath,
      ]);

      if (Number(exitCode) !== 0) {
        throw new Error(`ffmpeg exited with code ${exitCode}`);
      }

      await ffmpeg.readFile(outputPath);

      setFfmpegRuntimeState({
        status: 'ready',
        hasProResSupport: true,
        errorReason: null,
        probeCompleted: true,
      });
      return true;
    } catch (error) {
      console.warn('[LowerThirds] ffmpeg.wasm ProRes MOV probe failed', error);
      setFfmpegRuntimeState({
        status: 'unsupported',
        hasProResSupport: false,
        errorReason: 'ProRes MOV encoder is unavailable in the current ffmpeg.wasm core build.',
        probeCompleted: true,
      });
      return false;
    } finally {
      await safeFfmpegDeleteFile(ffmpeg, inputPath);
      await safeFfmpegDeleteFile(ffmpeg, outputPath);
      await safeFfmpegDeleteDir(ffmpeg, probeDir);
    }
  }, [ensureFfmpegWasmLoaded, ffmpegRuntimeState.hasProResSupport, ffmpegRuntimeState.probeCompleted, safeFfmpegDeleteDir, safeFfmpegDeleteFile]);

  const ensureFfmpegMovProResReady = useCallback(async () => {
    const supported = await probeFfmpegMovProResSupport();
    if (!supported) {
      throw new Error(ffmpegRuntimeState.errorReason || 'ProRes MOV export is unavailable in this browser ffmpeg runtime');
    }
    return true;
  }, [ffmpegRuntimeState.errorReason, probeFfmpegMovProResSupport]);

  const resolveExportFormatOptions = useCallback(async (dialogScope = {}) => {
    const {
      scope = 'single',
    } = dialogScope || {};
    const targetFps = Math.max(1, fps);
    let webCodecsSupported = false;
    try {
      webCodecsSupported = await checkWebCodecsSupport(targetFps);
    } catch (error) {
      console.warn('[LowerThirds] Failed to probe WebCodecs support for export dialog', error);
      webCodecsSupported = false;
    }

    const alphaRequired = config.backgroundMode === 'alpha';
    const hasWebCodecsAlpha = Boolean(webCodecsSupported && webCodecsAlphaConfigRef.current);
    const hasAnyWebCodecs = Boolean(
      webCodecsSupported && (webCodecsAlphaConfigRef.current || webCodecsOpaqueConfigRef.current)
    );
    const webmMediaRecorderMime = pickSupportedMediaRecorderMime(MEDIARECORDER_WEBM_MIME_CANDIDATES);
    const mp4MediaRecorderMime = pickSupportedMediaRecorderMime(MEDIARECORDER_MP4_MIME_CANDIDATES);
    const supportsPngStill = true;
    const supportsWebpStill = getCanvasImageMimeSupport('image/webp');
    const { totalFrames } = getDurations(targetFps);
    const movPreflight = getMovPreflightAssessment({
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      totalFramesPerClip: totalFrames + 1,
      clipCount: scope === 'batch' ? Math.max(1, people.length) : 1,
    });

    return EXPORT_FORMAT_DEFINITIONS.map((definition) => {
      let supported = false;
      let disabledReason = null;
      let note = definition.note || null;
      const runtime = {};

      if (definition.id === 'video-webm-alpha') {
        supported = hasWebCodecsAlpha;
        if (supported) {
          runtime.webCodecsOnly = true;
        } else {
          disabledReason = 'Transparent WebM requires WebCodecs alpha support in this browser.';
        }
      } else if (definition.id === 'video-webm') {
        supported = Boolean(hasAnyWebCodecs || webmMediaRecorderMime);
        runtime.mediaRecorderMime = webmMediaRecorderMime;
        if (!supported) {
          disabledReason = 'WebM recording is not supported by this browser.';
        } else if (!hasAnyWebCodecs && webmMediaRecorderMime) {
          note = 'Uses MediaRecorder fallback for WebM export.';
        }
      } else if (definition.id === 'video-mp4') {
        supported = Boolean(mp4MediaRecorderMime);
        runtime.mediaRecorderMime = mp4MediaRecorderMime;
        if (!supported) {
          disabledReason = 'MP4 recording is not available in this browser MediaRecorder.';
        }
      } else if (definition.id === 'video-mov-alpha-prores') {
        runtime.requiresFfmpegWasm = true;
        runtime.ffmpegProbeStatus = ffmpegRuntimeState.status;
        runtime.ffmpegProbeReason = ffmpegRuntimeState.errorReason;
        supported = true;
        if (ffmpegRuntimeState.probeCompleted && ffmpegRuntimeState.hasProResSupport === false) {
          supported = false;
          disabledReason = ffmpegRuntimeState.errorReason || 'ProRes MOV export is unavailable in this ffmpeg.wasm core build.';
        } else if (ffmpegRuntimeState.status === 'error') {
          supported = false;
          disabledReason = ffmpegRuntimeState.errorReason || 'ffmpeg.wasm runtime failed to load.';
        } else if (movPreflight.blocked) {
          supported = true;
          disabledReason = movPreflight.reason;
        } else if (movPreflight.warning) {
          note = `${note} ${movPreflight.warning}`;
          runtime.movPreflightWarning = movPreflight.warning;
        }
      } else if (definition.id === 'still-png') {
        supported = supportsPngStill;
        runtime.imageMimeType = 'image/png';
        if (!supported) {
          disabledReason = 'Canvas PNG export is not available in this browser.';
        }
      } else if (definition.id === 'still-webp') {
        supported = supportsWebpStill;
        runtime.imageMimeType = 'image/webp';
        if (!supported) {
          disabledReason = 'Canvas WebP export is not supported in this browser.';
        }
      } else if (definition.id === 'sequence-png-zip') {
        supported = supportsPngStill;
        runtime.imageMimeType = 'image/png';
        if (!supported) {
          disabledReason = 'Canvas PNG export is required for frame sequence export.';
        }
      }

      if (alphaRequired && !definition.supportsAlpha) {
        disabledReason = 'Disabled: current background mode is Alpha, and this format cannot preserve transparency.';
      }

      const selectable = Boolean(supported && !disabledReason);
      return {
        ...definition,
        supported,
        selectable,
        disabledReason,
        note,
        runtime,
      };
    });
  }, [
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    checkWebCodecsSupport,
    config.backgroundMode,
    ffmpegRuntimeState.errorReason,
    ffmpegRuntimeState.hasProResSupport,
    ffmpegRuntimeState.probeCompleted,
    ffmpegRuntimeState.status,
    fps,
    getDurations,
    people.length,
  ]);

  const recordPersonToBlobWebCodecs = async (person, encoderConfig, targetFpsParam = fps) => {
    await ensurePresetTextLayerFontsLoaded(config);
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    const ctx = canvas.getContext('2d');

    const resolvedFps = Math.max(1, targetFpsParam || encoderConfig.framerate || fps);
    const configWithFps = { ...encoderConfig, framerate: resolvedFps };
    const keepAlpha = configWithFps.alpha !== 'discard';

    const muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: {
        codec: 'V_VP9',
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        frameRate: resolvedFps,
        alpha: keepAlpha
      }
    });

    const encoder = new VideoEncoder({
      output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
      error: (err) => {
        throw err;
      }
    });

    encoder.configure(configWithFps);

    console.info(
      '[LowerThirds] Using WebCodecs fast export',
      configWithFps,
      keepAlpha ? '(alpha kept)' : '(no alpha - background will be opaque)'
    );

    const targetFps = configWithFps.framerate || resolvedFps;
    const frameDurationUs = Math.round(1_000_000 / targetFps);
    if (targetFps !== fps) {
      console.info(`[LowerThirds] Using target FPS ${targetFps} for export (preview currently set to ${fps})`);
    }
    const transitionInFrames = Math.max(1, Math.round(config.transitionInDuration * targetFps));
    const transitionOutFrames = Math.max(1, Math.round(config.transitionOutDuration * targetFps));
    const stillFrames = Math.max(0, Math.round(config.stillDuration * targetFps));

    let timestampUs = 0;
    const encodeFrame = async (frameIndex, keyFrame = false) => {
      drawFrame(ctx, frameIndex, person);
      const videoFrame = new VideoFrame(canvas, { timestamp: timestampUs });
      encoder.encode(videoFrame, { keyFrame });
      videoFrame.close();
      timestampUs += frameDurationUs;
      if (encoder.encodeQueueSize > 12) {
        await encoder.flush();
      }
    };

    // Transition in: encode every frame
    for (let i = 0; i < transitionInFrames; i++) {
      await encodeFrame(i, i === 0);
    }

    // Still: encode a single frame and jump the timestamp to the end of the still segment
    if (stillFrames > 0) {
      await encodeFrame(transitionInFrames, transitionInFrames === 0);
      timestampUs += (stillFrames - 1) * frameDurationUs;
    }

    // Transition out: encode every frame
    const startOutroFrame = transitionInFrames + stillFrames;
    for (let i = 0; i < transitionOutFrames; i++) {
      await encodeFrame(startOutroFrame + i, false);
    }

    await encoder.flush();
    muxer.finalize();
    encoder.close();

    return new Blob([muxer.target.buffer], { type: 'video/webm' });
  };

  const renderPersonFrameRange = useCallback(async (person, options = {}) => {
    const {
      imageMimeType = 'image/png',
      targetFps: targetFpsOverride = fps,
      startFrame = 0,
      endFrame = null,
      onFrame,
      onProgress,
      registerCancel,
      progressLabel,
      yieldEvery = 6,
    } = options;

    await ensurePresetTextLayerFontsLoaded(config);

    const targetFps = Math.max(1, targetFpsOverride);
    const { totalFrames } = getDurations(targetFps);
    const safeStartFrame = Math.max(0, Number(startFrame) || 0);
    const safeEndFrame = Math.min(
      totalFrames,
      endFrame == null ? totalFrames : Math.max(safeStartFrame, Number(endFrame) || 0),
    );
    const totalFrameCount = (safeEndFrame - safeStartFrame) + 1;

    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');

    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    registerCancel?.(cancel);

    const startTime = performance.now();
    onProgress?.({
      completed: 0,
      total: totalFrameCount,
      percent: 0,
      etaMs: null,
      frame: safeStartFrame,
      label: progressLabel,
    });

    for (let frame = safeStartFrame; frame <= safeEndFrame; frame += 1) {
      if (cancelled) throw new Error('Render cancelled');

      drawFrame(ctx, frame, person);
      const blob = await canvasToBlob(canvas, imageMimeType);
      const bytes = new Uint8Array(await blob.arrayBuffer());

      const indexInRange = frame - safeStartFrame;
      await onFrame?.({
        frame,
        indexInRange,
        bytes,
        mimeType: imageMimeType,
      });

      const completed = indexInRange + 1;
      const percent = completed / totalFrameCount;
      const elapsed = performance.now() - startTime;
      const etaMs = percent > 0 ? Math.max(0, (elapsed / percent) - elapsed) : null;
      onProgress?.({
        completed,
        total: totalFrameCount,
        percent,
        etaMs,
        frame,
        label: progressLabel,
      });

      if (frame % Math.max(1, yieldEvery) === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }
  }, [CANVAS_HEIGHT, CANVAS_WIDTH, config, drawFrame, ensurePresetTextLayerFontsLoaded, fps, getDurations]);

  const renderPersonMovAlphaToBlobFfmpeg = useCallback(async (person, options = {}) => {
    const {
      progressHeader = 'Rendering MOV export',
      progressLabel = `Rendering ${person?.name || 'lower third'}`,
      registerCancel,
    } = options;

    await ensureFfmpegMovProResReady();
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) {
      throw new Error('ffmpeg.wasm runtime is not ready');
    }

    const targetFps = Math.max(1, fps);
    const { totalFrames } = getDurations(targetFps);
    const totalFrameCount = totalFrames + 1;
    const segmentFrameCount = computeMovSegmentFrameCount(CANVAS_WIDTH, CANVAS_HEIGHT);
    const totalSegments = Math.max(1, Math.ceil(totalFrameCount / segmentFrameCount));
    const encoder = new TextEncoder();
    const jobId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const jobRoot = `/mov-job-${jobId}`;
    const tmpDir = `${jobRoot}/tmp`;
    const segmentPaths = [];
    const concatListPath = `${jobRoot}/concat.txt`;
    const finalOutputPath = `${jobRoot}/output.mov`;
    let terminatedByCancel = false;
    let cancelled = false;
    const movProgressState = {
      startTimeMs: performance.now(),
      lastPercent: 0,
    };

    const setVideoProgress = (percent, label, stage, etaMsOverride = undefined) => {
      const { percent: monotonicPercent, etaMs } = computeMonotonicEta(movProgressState, percent, performance.now());
      setExportProgress({
        mode: 'video',
        percent: monotonicPercent,
        etaMs: etaMsOverride == null ? etaMs : etaMsOverride,
        label,
        header: progressHeader,
        stage,
      });
    };

    const activateCancel = (phase) => {
      const cancel = () => {
        cancelled = true;
        const active = ffmpegActiveJobRef.current;
        if (active) active.cancelled = true;
        if (phase === 'encode' || phase === 'concat') {
          terminatedByCancel = true;
          try {
            ffmpeg.terminate();
          } catch {
            // ignore termination failures
          }
          resetFfmpegRuntimeAfterTerminate();
        }
      };
      ffmpegActiveJobRef.current = { phase, cancelled };
      registerCancel?.(cancel);
      cancelRenderRef.current = cancel;
      return cancel;
    };

    const updateSegmentEncodeProgress = (segmentIndex) => {
      ffmpegProgressRelayRef.current = (event) => {
        const raw = clamp(Number(event?.progress) || 0, 0, 1);
        const encodeShare = 0.35;
        const renderShare = 0.55;
        const percent = renderShare + (encodeShare * ((segmentIndex + raw) / totalSegments));
        setVideoProgress(
          percent,
          `${progressLabel} - Encoding segment ${segmentIndex + 1}/${totalSegments}`,
          'Encoding segment',
        );
      };
    };

    const updateConcatProgress = () => {
      ffmpegProgressRelayRef.current = (event) => {
        const raw = clamp(Number(event?.progress) || 0, 0, 1);
        const percent = 0.90 + (0.07 * raw);
        setVideoProgress(percent, `${progressLabel} - Concatenating segments`, 'Concatenating segments');
      };
    };

    try {
      try {
        await ffmpeg.createDir(jobRoot);
      } catch {
        // ignore if it already exists
      }
      try {
        await ffmpeg.createDir(tmpDir);
      } catch {
        // ignore if it already exists
      }

      let renderedFramesSoFar = 0;
      for (let segmentIndex = 0; segmentIndex < totalSegments; segmentIndex += 1) {
        if (cancelled) throw new Error('Render cancelled');

        const segmentStart = segmentIndex * segmentFrameCount;
        const segmentEnd = Math.min(totalFrames, segmentStart + segmentFrameCount - 1);
        const tmpFramePaths = [];

        activateCancel('render');
        // eslint-disable-next-line no-loop-func
        await renderPersonFrameRange(person, {
          imageMimeType: 'image/png',
          targetFps,
          startFrame: segmentStart,
          endFrame: segmentEnd,
          registerCancel,
          progressLabel: `${progressLabel} - Rendering segment ${segmentIndex + 1}/${totalSegments}`,
          // eslint-disable-next-line no-loop-func
          onFrame: async ({ indexInRange, bytes }) => {
            if (cancelled) throw new Error('Render cancelled');
            const framePath = `${tmpDir}/frame-${String(indexInRange).padStart(4, '0')}.png`;
            tmpFramePaths.push(framePath);
            await ffmpeg.writeFile(framePath, bytes);
          },
          // eslint-disable-next-line no-loop-func
          onProgress: ({ completed }) => {
            const overallRendered = renderedFramesSoFar + completed;
            const renderPercent = 0.55 * (overallRendered / totalFrameCount);
            setVideoProgress(
              renderPercent,
              `${progressLabel} - Rendering segment ${segmentIndex + 1}/${totalSegments}`,
              'Rendering segment',
            );
          },
          yieldEvery: 2,
        });
        renderedFramesSoFar += (segmentEnd - segmentStart + 1);

        if (cancelled) throw new Error('Render cancelled');

        const segmentPath = `${jobRoot}/seg_${String(segmentIndex).padStart(3, '0')}.mov`;
        segmentPaths.push(segmentPath);

        activateCancel('encode');
        updateSegmentEncodeProgress(segmentIndex);
        const encodeExitCode = await ffmpeg.exec([
          '-y',
          '-framerate',
          String(targetFps),
          '-start_number',
          '0',
          '-i',
          `${tmpDir}/frame-%04d.png`,
          '-c:v',
          'prores_ks',
          '-profile:v',
          '4',
          '-pix_fmt',
          'yuva444p10le',
          '-alpha_bits',
          '16',
          segmentPath,
        ]);
        ffmpegProgressRelayRef.current = null;
        if (Number(encodeExitCode) !== 0) {
          throw new Error(`ffmpeg segment encode failed with code ${encodeExitCode}`);
        }

        for (const framePath of tmpFramePaths) {
          await safeFfmpegDeleteFile(ffmpeg, framePath);
        }
      }

      if (cancelled) throw new Error('Render cancelled');

      let sourceOutputPath = segmentPaths[0];
      if (segmentPaths.length > 1) {
        const concatList = `${segmentPaths.map((path) => `file '${path.replace(/'/g, "'\\''")}'`).join('\n')}\n`;
        await ffmpeg.writeFile(concatListPath, encoder.encode(concatList));

        activateCancel('concat');
        updateConcatProgress();
        const concatExitCode = await ffmpeg.exec([
          '-y',
          '-f',
          'concat',
          '-safe',
          '0',
          '-i',
          concatListPath,
          '-c',
          'copy',
          finalOutputPath,
        ]);
        ffmpegProgressRelayRef.current = null;
        if (Number(concatExitCode) !== 0) {
          throw new Error(`ffmpeg concat failed with code ${concatExitCode}`);
        }
        sourceOutputPath = finalOutputPath;
      } else {
        setVideoProgress(0.97, `${progressLabel} - Packaging output`, 'Packaging output');
      }

      activateCancel('read');
      setVideoProgress(0.98, `${progressLabel} - Packaging output`, 'Packaging output');
      const outputBytes = await ffmpeg.readFile(sourceOutputPath);
      const normalizedBytes = outputBytes instanceof Uint8Array ? outputBytes : new Uint8Array(outputBytes);

      setVideoProgress(1, `${progressLabel} - Packaging output`, 'Packaging output', 0);
      return new Blob([normalizedBytes], { type: 'video/quicktime' });
    } catch (error) {
      if (cancelled || (error instanceof Error && error.message === 'Render cancelled')) {
        throw new Error('Render cancelled');
      }
      throw error;
    } finally {
      ffmpegProgressRelayRef.current = null;
      ffmpegLogRelayRef.current = null;
      ffmpegActiveJobRef.current = null;
      if (!terminatedByCancel && ffmpegRef.current) {
        await safeFfmpegDeleteFile(ffmpeg, concatListPath);
        await safeFfmpegDeleteFile(ffmpeg, finalOutputPath);
        for (const segmentPath of segmentPaths) {
          await safeFfmpegDeleteFile(ffmpeg, segmentPath);
        }
        await safeFfmpegDeleteDir(ffmpeg, tmpDir);
        await safeFfmpegDeleteDir(ffmpeg, jobRoot);
      }
    }
  }, [
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    ensureFfmpegMovProResReady,
    fps,
    getDurations,
    renderPersonFrameRange,
    resetFfmpegRuntimeAfterTerminate,
    safeFfmpegDeleteDir,
    safeFfmpegDeleteFile,
  ]);

  const renderPersonStillToBlob = useCallback(async (person, options = {}) => {
    const { mimeType = 'image/png' } = options;
    await ensurePresetTextLayerFontsLoaded(config);
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    drawFrame(ctx, 0, person, null, { staticStill: true });
    return canvasToBlob(canvas, mimeType);
  }, [CANVAS_HEIGHT, CANVAS_WIDTH, config, drawFrame, ensurePresetTextLayerFontsLoaded]);

  const appendPersonSequenceToZip = useCallback(async (zip, person, options = {}) => {
    const {
      imageMimeType = 'image/png',
      folderPrefix = '',
      onProgress,
      registerCancel,
      progressLabel,
    } = options;
    if (!zip) throw new Error('Zip instance is required');

    const targetFps = Math.max(1, fps);
    const { totalFrames } = getDurations(targetFps);
    const totalFrameCount = totalFrames + 1;
    const frameExtension = getSequenceFrameExtensionForMime(imageMimeType);

    const startTime = performance.now();
    onProgress?.(0, null, progressLabel);

    await renderPersonFrameRange(person, {
      imageMimeType,
      targetFps,
      startFrame: 0,
      endFrame: totalFrames,
      registerCancel,
      progressLabel,
      onFrame: async ({ frame, bytes }) => {
        const frameName = `frame-${String(frame).padStart(4, '0')}.${frameExtension}`;
        zip.file(
          `${folderPrefix}${frameName}`,
          bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength),
        );
      },
      onProgress: ({ completed }) => {
        const percent = completed / totalFrameCount;
        const elapsed = performance.now() - startTime;
        const etaMs = percent > 0 ? Math.max(0, (elapsed / percent) - elapsed) : null;
        onProgress?.(percent, etaMs, progressLabel);
      },
    });
  }, [fps, getDurations, renderPersonFrameRange]);

  const renderPersonSequenceZip = useCallback(async (person, options = {}) => {
    const zip = new JSZip();
    await appendPersonSequenceToZip(zip, person, options);
    return zip.generateAsync({ type: 'blob' });
  }, [appendPersonSequenceToZip]);

  const recordPersonToBlobMediaRecorder = async (person, options = {}) => {
    const {
      onProgress,
      totalMs,
      registerCancel,
      mimeType: mimeTypeOverride = null,
      outputMimeType = null,
    } = options;
    const targetFps = Math.max(1, fps);
    if (typeof MediaRecorder === 'undefined') {
      return Promise.reject(new Error('MediaRecorder export is not supported in this browser'));
    }
    await ensurePresetTextLayerFontsLoaded(config);
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    const ctx = canvas.getContext('2d');
    const stream = canvas.captureStream(targetFps);

    // Prefer VP9 for Alpha channel support in WebM
    const mimeType = mimeTypeOverride || pickSupportedMediaRecorderMime(MEDIARECORDER_WEBM_MIME_CANDIDATES) || 'video/webm';
    const blobMimeType = outputMimeType || (String(mimeType).split(';')[0] || 'video/webm').trim();

    return new Promise((resolve, reject) => {
      let done = false;
      let cancelled = false;
      let progressTimer = null;
      let recordLoop = null;
      const clearTimers = () => {
        if (progressTimer) clearInterval(progressTimer);
        if (recordLoop) clearInterval(recordLoop);
      };
      const safeResolve = (value) => {
        if (done) return;
        done = true;
        clearTimers();
        onProgress?.(1, 0);
        resolve(value);
      };
      const safeReject = (error) => {
        if (done) return;
        done = true;
        clearTimers();
        reject(error);
      };
      try {
        const recorderOptions = { videoBitsPerSecond: 5000000 };
        if (mimeType) recorderOptions.mimeType = mimeType;
        const recorder = new MediaRecorder(stream, recorderOptions);
        const chunks = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        recorder.onerror = (e) => safeReject(e.error || e.name || e);

        recorder.onstop = () => {
          if (cancelled) return;
          const blob = new Blob(chunks, { type: blobMimeType });
          safeResolve(blob);
        };

        recorder.start();

        const startTime = performance.now();
        if (onProgress && Number.isFinite(totalMs) && totalMs > 0) {
          onProgress(0, totalMs);
          progressTimer = setInterval(() => {
            const elapsed = performance.now() - startTime;
            const pct = Math.min(0.98, elapsed / totalMs);
            onProgress(pct, Math.max(0, totalMs - elapsed));
          }, 200);
        }

        // Manually drive the animation for the recording to ensure perfect frames
        let frame = 0;
        const { totalFrames } = getDurations(targetFps);

        recordLoop = setInterval(() => {
          drawFrame(ctx, frame, person);
          frame++;

          if (frame > totalFrames) {
            clearInterval(recordLoop);
            recorder.stop();
          }
        }, 1000 / targetFps);

        const cancel = () => {
          if (done || cancelled) return;
          cancelled = true;
          try {
            recorder.stop();
          } catch (_) {
            // ignore
          }
          safeReject(new Error('Render cancelled'));
        };
        registerCancel?.(cancel);
      } catch (err) {
        clearTimers();
        reject(err);
      }
    });
  };

  const recordPersonToBlob = async (person, options = {}) => {
    const {
      formatOption,
      onMediaRecorderProgress,
      registerCancel,
      progressHeader,
      progressLabel,
    } = options;
    const formatId = formatOption?.id || 'video-webm';
    const targetFps = Math.max(1, fps);
    if (formatId === 'video-mov-alpha-prores') {
      const { totalFrames } = getDurations(targetFps);
      const movPreflight = getMovPreflightAssessment({
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        totalFramesPerClip: totalFrames + 1,
        clipCount: 1,
      });
      if (movPreflight.blocked) {
        throw new Error(movPreflight.reason || 'MOV export is too large for browser-only ffmpeg.wasm export');
      }
      return renderPersonMovAlphaToBlobFfmpeg(person, {
        progressHeader: progressHeader || buildExportProgressHeader(formatOption, config.backgroundMode === 'alpha'),
        progressLabel: progressLabel || `Rendering ${person?.name || 'lower third'}`,
        registerCancel: (fn) => {
          cancelRenderRef.current = fn;
          registerCancel?.(fn);
        },
      });
    }
    if (formatId === 'video-mp4') {
      const mimeType = formatOption?.runtime?.mediaRecorderMime || pickSupportedMediaRecorderMime(MEDIARECORDER_MP4_MIME_CANDIDATES);
      if (!mimeType) {
        throw new Error('MP4 export is not supported in this browser');
      }
      const { totalFrames } = getDurations(targetFps);
      const totalMs = (totalFrames + 1) * (1000 / targetFps);
      return recordPersonToBlobMediaRecorder(person, {
        mimeType,
        outputMimeType: 'video/mp4',
        onProgress: onMediaRecorderProgress,
        totalMs,
        registerCancel: (fn) => {
          cancelRenderRef.current = fn;
          registerCancel?.(fn);
        },
      });
    }

    const wantsAlpha = formatId === 'video-webm-alpha';
    const useWebCodecs = await checkWebCodecsSupport(targetFps);
    if (useWebCodecs) {
      const encoderConfig = wantsAlpha
        ? webCodecsAlphaConfigRef.current
        : (webCodecsOpaqueConfigRef.current || webCodecsAlphaConfigRef.current);

      if (encoderConfig) {
        try {
          cancelRenderRef.current = null;
          return await recordPersonToBlobWebCodecs(person, encoderConfig, targetFps);
        } catch (err) {
          if (wantsAlpha) {
            throw err;
          }
          console.warn('WebCodecs export failed, falling back to MediaRecorder', err);
        }
      } else if (wantsAlpha) {
        throw new Error('Transparent WebM export is not supported in this browser');
      }
    } else if (wantsAlpha) {
      throw new Error('Transparent WebM export requires WebCodecs support');
    }

    const mediaRecorderMime = formatOption?.runtime?.mediaRecorderMime || pickSupportedMediaRecorderMime(MEDIARECORDER_WEBM_MIME_CANDIDATES);
    if (!mediaRecorderMime) {
      throw new Error('WebM export is not supported in this browser');
    }
    console.info('[LowerThirds] Using MediaRecorder fallback export');
    const { totalFrames } = getDurations(targetFps);
    const totalMs = (totalFrames + 1) * (1000 / targetFps);
    return recordPersonToBlobMediaRecorder(person, {
      mimeType: mediaRecorderMime,
      outputMimeType: 'video/webm',
      onProgress: onMediaRecorderProgress,
      totalMs,
      registerCancel: (fn) => {
        cancelRenderRef.current = fn;
        registerCancel?.(fn);
      }
    });
  };

  // --- UI HANDLERS ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numberFields = [
      'transitionInDuration',
      'transitionOutDuration',
      'stillDuration',
    ];
    const nextValue = type === 'checkbox'
      ? checked
      : numberFields.includes(name)
        ? Number(value)
        : value;

    setConfig(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const handleTransitionTypeChange = (field, value) => {
    setConfig(prev => {
      const next = { ...prev, [field]: value };
      if (value === 'fade') {
        if (field === 'transitionInType') next.fadeIn = true;
        if (field === 'transitionOutType') next.fadeOut = true;
      }
      return next;
    });
  };

  const handleFadeCurveChange = (direction, curve) => {
    const field = direction === 'out' ? 'fadeOutCurve' : 'fadeInCurve';
    setConfig(prev => ({ ...prev, [field]: curve }));
    setOpenFadeMenu(null);
  };

  const applyTotalDuration = (targetSeconds) => {
    if (!Number.isFinite(targetSeconds)) return;
    setConfig(prev => {
      const transitionsSum = prev.transitionInDuration + prev.transitionOutDuration;
      const currentTotal = transitionsSum + prev.stillDuration;
      const minTotal = MIN_TRANSITION * 2 + MIN_STILL;
      const target = Math.max(targetSeconds, minTotal);

      if (target >= transitionsSum + MIN_STILL) {
        const nextStill = target - transitionsSum;
        const nextFadeIn = prev.fadeInDuration == null ? prev.fadeInDuration : Math.min(prev.fadeInDuration, prev.transitionInDuration);
        const nextFadeOut = prev.fadeOutDuration == null ? prev.fadeOutDuration : Math.min(prev.fadeOutDuration, prev.transitionOutDuration);
        return {
          ...prev,
          stillDuration: Number(nextStill.toFixed(2)),
          fadeInDuration: nextFadeIn == null ? nextFadeIn : Number(nextFadeIn.toFixed(2)),
          fadeOutDuration: nextFadeOut == null ? nextFadeOut : Number(nextFadeOut.toFixed(2)),
        };
      }

      const factor = currentTotal ? target / currentTotal : 1;
      let nextTransitionIn = Math.max(MIN_TRANSITION, prev.transitionInDuration * factor);
      let nextTransitionOut = Math.max(MIN_TRANSITION, prev.transitionOutDuration * factor);

      let remaining = target - nextTransitionIn - nextTransitionOut;

      if (remaining < MIN_STILL) {
        const needed = MIN_STILL - remaining;
        const reducibleIn = nextTransitionIn - MIN_TRANSITION;
        const reducibleOut = nextTransitionOut - MIN_TRANSITION;
        const reducibleTotal = reducibleIn + reducibleOut;

        if (needed > 0 && reducibleTotal > 0) {
          const ratio = Math.min(1, needed / reducibleTotal);
          nextTransitionIn -= reducibleIn * ratio;
          nextTransitionOut -= reducibleOut * ratio;
        }

        remaining = target - nextTransitionIn - nextTransitionOut;
      }

      const nextStill = Math.max(MIN_STILL, remaining);
      const nextFadeIn = prev.fadeInDuration == null ? prev.fadeInDuration : Math.min(prev.fadeInDuration, nextTransitionIn);
      const nextFadeOut = prev.fadeOutDuration == null ? prev.fadeOutDuration : Math.min(prev.fadeOutDuration, nextTransitionOut);

      return {
        ...prev,
        transitionInDuration: Number(nextTransitionIn.toFixed(2)),
        transitionOutDuration: Number(nextTransitionOut.toFixed(2)),
        stillDuration: Number(nextStill.toFixed(2)),
        fadeInDuration: nextFadeIn == null ? nextFadeIn : Number(nextFadeIn.toFixed(2)),
        fadeOutDuration: nextFadeOut == null ? nextFadeOut : Number(nextFadeOut.toFixed(2)),
      };
    });
  };

  const handleTotalDurationChange = (e) => {
    const value = e.target.value;
    setTotalInputValue(value);
    if (!isEditingTotal) setIsEditingTotal(true);
    if (value.trim() === '') return;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    applyTotalDuration(numeric);
  };

  const handleTotalDurationBlur = () => {
    if (totalInputValue.trim() === '') {
      setTotalInputValue(totalDuration.toFixed(2));
      setIsEditingTotal(false);
      return;
    }
    const numeric = Number(totalInputValue);
    if (Number.isFinite(numeric)) {
      applyTotalDuration(numeric);
    }
    setIsEditingTotal(false);
  };

  const toggleFade = (field, durationField, transitionDuration) => {
    setConfig(prev => {
      const enabling = !prev[field];
      if (!enabling) return { ...prev, [field]: false };
      const proposedDuration = transitionDuration / 2;
      const nextDuration = Math.min(transitionDuration, Math.max(MIN_FADE, proposedDuration));
      return {
        ...prev,
        [field]: true,
        [durationField]: Number(nextDuration.toFixed(2))
      };
    });
  };

  const startEdgeDrag = (edge) => (e) => {
    e.preventDefault();
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDragState({
      edge,
      startX: e.clientX,
      width: rect.width,
      initial: {
        transitionInDuration: config.transitionInDuration,
        stillDuration: config.stillDuration,
        transitionOutDuration: config.transitionOutDuration,
        total: totalDuration
      }
    });
  };

  const startFadeDrag = (direction) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const slotEl = e.currentTarget.closest('.fade-slot');
    if (!slotEl) return;
    const rect = slotEl.getBoundingClientRect();
    const blockDuration = direction === 'in' ? config.transitionInDuration : config.transitionOutDuration;
    if (!blockDuration || rect.width <= 0) return;
    const durationField = direction === 'in' ? 'fadeInDuration' : 'fadeOutDuration';
    const initialDuration = Math.min(
      blockDuration,
      Math.max(MIN_FADE, config[durationField] ?? blockDuration / 2)
    );
    setFadeDrag({
      direction,
      startX: e.clientX,
      blockWidth: rect.width,
      blockDuration,
      initialDuration
    });
  };

  useEffect(() => {
    if (!dragState) return;
    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

    const handleMove = (e) => {
      const { startX, width, initial, edge } = dragState;
      const secondsPerPixel = width ? initial.total / width : 0;
      const deltaSeconds = (e.clientX - startX) * secondsPerPixel;

      let edge1 = initial.transitionInDuration;
      let edge2 = initial.transitionInDuration + initial.stillDuration;
      let edge3 = initial.total;

      if (edge === 'intro-end') {
        edge1 = clamp(edge1 + deltaSeconds, MIN_TRANSITION, edge2 - MIN_STILL);
      } else if (edge === 'still-end') {
        edge2 = clamp(edge2 + deltaSeconds, edge1 + MIN_STILL, edge3 - MIN_TRANSITION);
      } else if (edge === 'outro-end') {
        edge3 = Math.max(edge2 + MIN_TRANSITION, edge3 + deltaSeconds);
      }

      const nextTransitionIn = edge1;
      const nextStill = edge2 - edge1;
      const nextOutro = edge3 - edge2;

      setConfig(prev => ({
        ...prev,
        transitionInDuration: Number(nextTransitionIn.toFixed(2)),
        stillDuration: Number(nextStill.toFixed(2)),
        transitionOutDuration: Number(nextOutro.toFixed(2)),
        fadeInDuration: Math.min(
          Number(nextTransitionIn.toFixed(2)),
          Math.max(MIN_FADE, prev.fadeInDuration ?? nextTransitionIn / 2)
        ),
        fadeOutDuration: Math.min(
          Number(nextOutro.toFixed(2)),
          Math.max(MIN_FADE, prev.fadeOutDuration ?? nextOutro / 2)
        ),
      }));
    };

    const handleUp = () => setDragState(null);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('mouseleave', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('mouseleave', handleUp);
    };
  }, [dragState]);

  useEffect(() => {
    if (!fadeDrag) return;
    const handleMove = (e) => {
      const { startX, blockWidth, blockDuration, direction, initialDuration } = fadeDrag;
      if (!blockWidth || !blockDuration) return;
      const secondsPerPixel = blockDuration / blockWidth;
      const deltaPixels = e.clientX - startX;
      const directionMultiplier = direction === 'out' ? -1 : 1;
      const deltaSeconds = deltaPixels * secondsPerPixel * directionMultiplier;
      const nextSeconds = Math.min(
        blockDuration,
        Math.max(MIN_FADE, initialDuration + deltaSeconds)
      );
      setConfig(prev => ({
        ...prev,
        [direction === 'in' ? 'fadeInDuration' : 'fadeOutDuration']: Number(nextSeconds.toFixed(2)),
      }));
    };

    const handleUp = () => setFadeDrag(null);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('mouseleave', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('mouseleave', handleUp);
    };
  }, [fadeDrag]);

  useEffect(() => {
    if (!openFadeMenu) return;
    const handleClick = (e) => {
      const target = e.target;
      if (target instanceof Element && target.closest('.fade-curve-menu')) return;
      setOpenFadeMenu(null);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpenFadeMenu(null);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openFadeMenu]);

  const getPresetById = useCallback((presetId) => (
    allPresets.find((preset) => preset.id === presetId) || null
  ), [allPresets]);

  const cleanupPeopleGenericOverridesForPreset = useCallback((presetId, shapePrimitives = null) => {
    const validPrimitiveIds = Array.isArray(shapePrimitives)
      ? new Set(
        shapePrimitives
          .filter((shape, index) => (
            shape?.layerKind === 'text'
            && normalizeTextSource(shape?.textSource, 'main') === 'generic'
            && typeof (shape?.id || `shape-${index}`) === 'string'
          ))
          .map((shape, index) => shape?.id || `shape-${index}`),
      )
      : null;

    setPeople((prev) => {
      let changed = false;
      const next = prev.map((person) => {
        const updated = cleanupPersonGenericTextOverridesForPreset({
          person,
          presetId,
          validPrimitiveIds,
        });
        if (updated !== person) changed = true;
        return updated;
      });
      return changed ? next : prev;
    });
  }, []);

  const getPresetEditorGeometry = useCallback((preset) => {
    const visualOverride = extractVisualConfigFromPreset(preset);
    const metrics = getDesignMetrics(measureCtx, TEMP_PREVIEW_PERSON, {
      ...config,
      ...visualOverride,
    });
    const templateBlueprint = buildTemplateLayerBlueprint({
      preset: preset?.preset || visualOverride.preset,
      metrics,
    });
    const templateBoundsForEditor = metrics.templateVisibleBoundsWithGlobal;
    const templateLocalBoundsForEditor = metrics.templateVisibleBounds;

    return {
      renderBounds: metrics.renderBounds,
      template: templateBoundsForEditor
        ? {
          bounds: templateBoundsForEditor,
          localBounds: templateLocalBoundsForEditor || templateBoundsForEditor,
        }
        : null,
      primitives: metrics.primitives.map((shape) => ({
        id: shape.id,
        type: shape.type,
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
        rotation: shape.rotation,
        strokeWidth: shape.strokeWidth,
        cornerRadius: shape.cornerRadius,
        skewX: shape.skewX,
        fill: shape.fill,
        stroke: shape.stroke,
        visible: shape.visible,
        layerKind: shape.layerKind,
        textSource: shape.textSource,
        textPaddingX: shape.textPaddingX,
        textFontRatio: shape.textFontRatio,
        textBaselineRatio: shape.textBaselineRatio,
        textFontFamily: shape.textFontFamily || null,
        templateFamily: shape.templateFamily,
        templatePart: shape.templatePart,
        colorRole: shape.colorRole,
        fillColorMode: shape.fillColorMode,
        fillColorRole: shape.fillColorRole,
        fillColorValue: shape.fillColorValue,
        strokeColorMode: shape.strokeColorMode,
        strokeColorRole: shape.strokeColorRole,
        strokeColorValue: shape.strokeColorValue,
        autoSizeBinding: shape.autoSizeBinding || null,
        baseBounds: shape.baseBounds || null,
        contentLocalBounds: shape.contentLocalBounds || null,
        bounds: shape.hitBoundsWithGlobal || shape.boundsWithGlobal,
        localBounds: shape.hitLocalBounds || shape.localBounds || shape.baseBounds,
      })),
      templateBlueprint,
      rotation: metrics.rotation,
      rotationPivot: metrics.rotationPivot,
    };
  }, [config, getDesignMetrics, measureCtx]);

  const createMainPrimitiveFromTemplateBlueprintLayer = useCallback((layer, presetFamily) => {
    const isTextLayer = layer?.layerKind === 'text';
    const imageLayer = isImagePrimitive(layer);
    return stampPrimitiveResetSnapshot({
      ...layer,
      id: `shape-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
      type: layer?.type || (isTextLayer ? 'rect' : 'rect'),
      layerKind: isTextLayer ? 'text' : (imageLayer ? 'image' : 'shape'),
      textSource: normalizeTextSource(layer?.textSource, 'main'),
      textValue: typeof layer?.textValue === 'string' ? layer.textValue : '',
      hideFromPeoplePanel: Boolean(layer?.hideFromPeoplePanel),
      skewX: Number(layer?.skewX || 0),
      fill: isTextLayer || imageLayer ? false : layer?.fill !== false,
      stroke: isTextLayer || imageLayer ? false : Boolean(layer?.stroke),
      mainTextVisible: false,
      subTextVisible: false,
      templateFamily: layer?.templateFamily || presetFamily || null,
      templatePart: layer?.templatePart || null,
      autoSizeBinding: layer?.autoSizeBinding || null,
    }, { overwrite: true });
  }, []);

  const buildMainTemplateMaterializationPatch = useCallback((sourceConfig) => {
    if (!sourceConfig) return null;
    const metrics = getDesignMetrics(measureCtx, selectedPerson, sourceConfig);
    const templateBlueprint = buildTemplateLayerBlueprint({
      preset: sourceConfig.preset,
      metrics,
    });
    const blueprintLayers = Array.isArray(templateBlueprint?.layers) ? templateBlueprint.layers : [];
    if (!blueprintLayers.length) {
      return {
        templateShapeVisible: false,
        templateMainTextVisible: false,
        templateSubTextVisible: false,
      };
    }

    const sourceShapes = Array.isArray(sourceConfig.shapePrimitives) ? sourceConfig.shapePrimitives : [];
    const nonTemplateShapes = sourceShapes
      .filter((shape) => !isTemplateManagedPrimitive(shape))
      .map((shape) => normalizePrimitiveWithResetSnapshot({
        ...shape,
        mainTextVisible: false,
        subTextVisible: false,
      }));
    const created = blueprintLayers.map((layer) => (
      createMainPrimitiveFromTemplateBlueprintLayer(layer, sourceConfig.preset)
    ));
    const templateShapePrimitives = created.filter((shape) => shape.layerKind !== 'text');
    const templateTextPrimitives = created.filter((shape) => shape.layerKind === 'text');
    const nextShapePrimitives = [
      ...templateShapePrimitives,
      ...nonTemplateShapes,
      ...templateTextPrimitives,
    ];

    return {
      shapePrimitives: nextShapePrimitives,
      layerOrder: normalizePresetLayerOrder([], nextShapePrimitives),
      layerLocks: normalizePresetLayerLocks(sourceConfig.layerLocks, nextShapePrimitives),
      templateShapeVisible: false,
      templateMainTextVisible: false,
      templateSubTextVisible: false,
    };
  }, [createMainPrimitiveFromTemplateBlueprintLayer, getDesignMetrics, measureCtx, selectedPerson]);

  const syncPersistedPresetState = useCallback((persistedPreset) => {
    if (!persistedPreset) return;
    const normalizedPreset = normalizePresetWithResetSnapshots(persistedPreset);
    setCustomPresets((prev) => upsertCustomPreset(prev, normalizedPreset));
    cleanupPeopleGenericOverridesForPreset(normalizedPreset.id, normalizedPreset.shapePrimitives);
    setActivePresetId(normalizedPreset.id);
    setConfig((prev) => applyPresetToConfig(prev, normalizedPreset));
    if (isPresetOverlayOpen && overlayPresetId === activePresetId) {
      setOverlayPresetId(normalizedPreset.id);
      const nextDraft = preparePresetDraft(normalizedPreset);
      setOverlayDraftState(nextDraft);
      resetOverlayDraftUndoSession(nextDraft);
      setIsDraftDirty(false);
    }
  }, [
    activePresetId,
    cleanupPeopleGenericOverridesForPreset,
    isPresetOverlayOpen,
    overlayPresetId,
    preparePresetDraft,
    resetOverlayDraftUndoSession,
    setOverlayDraftState,
  ]);

  const openMainImageImport = useCallback(() => {
    if (isRecording || !selectedMainImageEntry) return;
    setIsMainImageImportOpen(true);
  }, [isRecording, selectedMainImageEntry]);

  const handleMainImageImportConfirm = useCallback(({ imageData, imageCropMode }) => {
    const targetPrimitiveId = selectedMainImageEntry?.primitiveId;
    const activePreset = getPresetById(activePresetId)
      || getPresetById(DEFAULT_ACTIVE_PRESET_ID)
      || DEFAULT_PRESETS[0];
    if (!targetPrimitiveId || !activePreset) {
      setIsMainImageImportOpen(false);
      return;
    }
    const workingPreset = {
      ...clonePreset(activePreset),
      ...extractVisualConfigFromPreset(config),
      updatedAt: Number(activePreset?.updatedAt || Date.now()),
    };
    let nextWorkingPreset = workingPreset;
    let nextSelectedPrimitiveId = targetPrimitiveId;

    if (targetPrimitiveId === MAIN_TEMPLATE_LOGO_BADGE_IMAGE_ID) {
      const materialized = buildMainTemplateMaterializationPatch(nextWorkingPreset);
      if (!materialized?.shapePrimitives) {
        setIsMainImageImportOpen(false);
        return;
      }
      nextWorkingPreset = {
        ...nextWorkingPreset,
        ...materialized,
      };
      const replacementTarget = nextWorkingPreset.shapePrimitives.find((shape) => (
        isImagePrimitive(shape) && shape?.templatePart === 'logo-icon'
      )) || nextWorkingPreset.shapePrimitives.find((shape) => isImagePrimitive(shape));
      if (!replacementTarget?.id) {
        setIsMainImageImportOpen(false);
        return;
      }
      nextSelectedPrimitiveId = replacementTarget.id;
    }

    let didReplaceTarget = false;
    const sourcePrimitives = Array.isArray(nextWorkingPreset?.shapePrimitives)
      ? nextWorkingPreset.shapePrimitives
      : [];
    const nextShapePrimitives = sourcePrimitives.map((shape, index) => {
      const primitiveId = shape?.id || `shape-${index}`;
      if (primitiveId !== nextSelectedPrimitiveId) return shape;
      didReplaceTarget = true;
      return {
        ...shape,
        ...buildImageReplacementPatch(shape, { imageData, imageCropMode }),
      };
    });
    if (!didReplaceTarget) {
      setIsMainImageImportOpen(false);
      return;
    }

    const replacedPreset = {
      ...nextWorkingPreset,
      shapePrimitives: nextShapePrimitives,
    };
    const persistedPreset = activePreset.isBuiltIn
      ? {
        ...duplicatePreset(replacedPreset, `${activePreset.name} Copy`),
        isBuiltIn: false,
        updatedAt: Date.now(),
      }
      : {
        ...clonePreset(replacedPreset),
        isBuiltIn: false,
        name: activePreset.name?.trim() || 'Custom Preset',
        updatedAt: Date.now(),
      };
    const persistedImageEntries = getImagePrimitiveEntries(persistedPreset.shapePrimitives);
    const fallbackSelectedPrimitiveId = persistedImageEntries[0]?.primitiveId || null;
    const hasSelectedImage = persistedImageEntries.some((entry) => entry.primitiveId === nextSelectedPrimitiveId);

    syncPersistedPresetState(persistedPreset);
    setSelectedMainImagePrimitiveId(hasSelectedImage ? nextSelectedPrimitiveId : fallbackSelectedPrimitiveId);
    setIsMainImageImportOpen(false);
  }, [
    activePresetId,
    buildMainTemplateMaterializationPatch,
    config,
    getPresetById,
    selectedMainImageEntry,
    syncPersistedPresetState,
  ]);

  const handlePresetSelect = (event) => {
    const presetId = event.target.value;
    const preset = getPresetById(presetId);
    if (!preset || isRecording) return;
    setActivePresetId(preset.id);
    setConfig((prev) => applyPresetToConfigPreservingThemeColors(prev, preset));
  };

  const openPresetOverlay = () => {
    const selected = getPresetById(activePresetId)
      || getPresetById(DEFAULT_ACTIVE_PRESET_ID)
      || allPresets[0];
    if (!selected) return;
    const nextDraft = preparePresetDraft(selected);
    setOverlayPresetId(selected.id);
    setOverlayDraftState(nextDraft);
    resetOverlayDraftUndoSession(nextDraft);
    setIsDraftDirty(false);
    setIsPresetOverlayOpen(true);
  };

  const maybeDiscardDraftChanges = () => {
    if (!isDraftDirty) return true;
    return window.confirm('Discard unsaved preset changes?');
  };

  const closePresetOverlay = () => {
    if (!maybeDiscardDraftChanges()) return;
    resetOverlayDraftUndoSession(null);
    setIsPresetOverlayOpen(false);
  };

  const handleOverlaySelectPreset = (presetId) => {
    if (presetId === overlayPresetId) return;
    if (!maybeDiscardDraftChanges()) return;
    const selected = getPresetById(presetId);
    if (!selected) return;
    const nextDraft = preparePresetDraft(selected);
    setOverlayPresetId(selected.id);
    setOverlayDraftState(nextDraft);
    resetOverlayDraftUndoSession(nextDraft);
    setIsDraftDirty(false);
  };

  const handleOverlayDraftChange = (partial, options = {}) => {
    if (!partial || !Object.keys(partial).length) return;
    const currentDraft = overlayDraftCurrentRef.current;
    if (!currentDraft) return;

    const {
      trackUndo = true,
      undoGroup = null,
      markDirty = true,
      resetBaseline = false,
    } = options || {};
    const nextDraft = {
      ...currentDraft,
      ...partial,
    };

    if (trackUndo) {
      if (!undoGroup || overlayActiveUndoGroupRef.current !== undoGroup) {
        pushOverlayDraftUndoSnapshot(currentDraft);
      }
      overlayActiveUndoGroupRef.current = undoGroup || null;
    } else {
      overlayActiveUndoGroupRef.current = null;
    }

    setOverlayDraftState(nextDraft);

    if (resetBaseline) {
      resetOverlayDraftUndoSession(nextDraft);
      setIsDraftDirty(false);
      return;
    }

    if (markDirty) {
      setIsDraftDirty(true);
    }
  };

  const handleOverlayUndoDraft = () => {
    if (isRecording) return;
    const stack = overlayDraftUndoStackRef.current;
    if (!stack.length) return;

    const nextStack = [...stack];
    const previousDraft = nextStack.pop();
    if (!previousDraft) return;

    overlayDraftUndoStackRef.current = nextStack;
    overlayActiveUndoGroupRef.current = null;
    setOverlayDraftUndoCount(nextStack.length);

    const nextDraft = clonePreset(previousDraft);
    setOverlayDraftState(nextDraft);
    setIsDraftDirty(
      serializePresetDraft(nextDraft) !== overlayDraftBaselineSerializedRef.current
    );
  };

  const handleOverlayCreateNew = () => {
    const source = draftPreset || getPresetById(overlayPresetId) || getPresetById(activePresetId) || DEFAULT_PRESETS[0];
    const next = duplicatePreset(source, 'New Preset');
    const persisted = {
      ...next,
      isBuiltIn: false,
      updatedAt: Date.now(),
    };
    setCustomPresets((prev) => upsertCustomPreset(prev, persisted));
    setActivePresetId(persisted.id);
    setConfig((prev) => applyPresetToConfig(prev, persisted));
    setOverlayPresetId(persisted.id);
    {
      const nextDraft = preparePresetDraft(persisted);
      setOverlayDraftState(nextDraft);
      resetOverlayDraftUndoSession(nextDraft);
    }
    setIsDraftDirty(false);
  };

  const handleOverlaySavePreset = () => {
    if (!draftPreset) return;
    const normalizedName = draftPreset.isBuiltIn
      ? `${draftPreset.name} Copy`
      : (draftPreset.name?.trim() || 'Custom Preset');
    const baseForSave = draftPreset.isBuiltIn
      ? duplicatePreset(draftPreset, `${draftPreset.name} Copy`)
      : clonePreset(draftPreset);
    const savedPreset = {
      ...baseForSave,
      isBuiltIn: false,
      name: normalizedName,
      updatedAt: Date.now(),
    };

    cleanupPeopleGenericOverridesForPreset(savedPreset.id, savedPreset.shapePrimitives);
    setCustomPresets((prev) => upsertCustomPreset(prev, savedPreset));
    setActivePresetId(savedPreset.id);
    setConfig((prev) => applyPresetToConfig(prev, savedPreset));
    setOverlayPresetId(savedPreset.id);
    {
      const nextDraft = preparePresetDraft(savedPreset);
      setOverlayDraftState(nextDraft);
      resetOverlayDraftUndoSession(nextDraft);
    }
    setIsDraftDirty(false);
  };

  const handleOverlayDeletePreset = () => {
    if (!draftPreset || draftPreset.isBuiltIn) return;
    if (!window.confirm(`Delete preset "${draftPreset.name}"?`)) return;

    cleanupPeopleGenericOverridesForPreset(draftPreset.id, null);
    setCustomPresets((prev) => prev.filter((preset) => preset.id !== draftPreset.id));

    const fallbackPreset = getPresetById(activePresetId)
      || getPresetById(DEFAULT_ACTIVE_PRESET_ID)
      || DEFAULT_PRESETS[0];
    const nextActivePreset = draftPreset.id === activePresetId
      ? (getPresetById(DEFAULT_ACTIVE_PRESET_ID) || DEFAULT_PRESETS[0])
      : fallbackPreset;
    if (nextActivePreset) {
      const nextDraft = preparePresetDraft(nextActivePreset);
      setActivePresetId(nextActivePreset.id);
      setConfig((prev) => applyPresetToConfig(prev, nextActivePreset));
      setOverlayPresetId(nextActivePreset.id);
      setOverlayDraftState(nextDraft);
      resetOverlayDraftUndoSession(nextDraft);
    } else {
      const nextDraft = preparePresetDraft(DEFAULT_PRESETS[0]);
      setOverlayPresetId(DEFAULT_ACTIVE_PRESET_ID);
      setOverlayDraftState(nextDraft);
      resetOverlayDraftUndoSession(nextDraft);
    }
    setIsDraftDirty(false);
  };

  const drawOverlayPreviewFrame = useCallback((ctx, preset) => {
    const visualOverride = extractVisualConfigFromPreset(preset);
    drawFrame(ctx, 0, TEMP_PREVIEW_PERSON, visualOverride, { staticStill: true });
  }, [drawFrame]);

  const handlePersonChange = (personId, field, value) => {
    setPeople(prev => prev.map(p => p.id === personId ? { ...p, [field]: value } : p));
  };

  const handlePersonGenericTextChange = useCallback((personId, primitiveId, value, fallbackText = '') => {
    setPeople((prev) => prev.map((person) => {
      if (person.id !== personId) return person;
      return updatePersonGenericTextValue({
        person,
        presetId: activePresetId,
        primitiveId,
        nextValue: value,
        fallbackText,
      });
    }));
  }, [activePresetId]);

  const handleTogglePersonMirrored = useCallback((personId) => {
    setPeople((prev) => prev.map((person) => (
      person.id === personId ? { ...person, mirrored: !person.mirrored } : person
    )));
  }, []);

  const handleAddPerson = () => {
    const nextIndex = people.length + 1;
    const newPerson = {
      id: `person-${Date.now()}`,
      name: `Person ${nextIndex}`,
      title: 'Title',
      mirrored: false,
      genericTextByPresetId: {},
    };
    setPeople(prev => [...prev, newPerson]);
    setSelectedPersonId(newPerson.id);
  };

  const buildGoLiveRequestPayload = useCallback(() => {
    const nextProject = {
      version: 1,
      people,
      selectedPersonId,
      activePresetId,
      customPresets,
      config,
      aspectRatioId,
      fps,
    };
    const persistedProject = replaceProjectState(nextProject);
    const playlistItems = getLivePlaylistSnapshot();
    const previewItemId = playlistItems.find((item) => item.personId === selectedPersonId)?.id
      || playlistItems[0]?.id
      || null;

    return {
      persistedProject,
      playlistItems,
      previewItemId,
    };
  }, [activePresetId, aspectRatioId, config, customPresets, fps, people, selectedPersonId]);

  const handleOpenGoLive = useCallback(() => {
    if (isRecording || isCreatingLiveRoom) return;
    setGoLiveError(null);
    setGoLiveLaunchState(GO_LIVE_LAUNCH_STATE_IDLE);
    setLocalRelayPhase(GO_LIVE_LOCAL_RELAY_PHASE_CHECKING);
    setLocalRelayMessage(GO_LIVE_LOCAL_RELAY_CHECKING_MESSAGE);
    setLocalRelayStatus(null);
    setIsGoLiveDialogOpen(true);
  }, [isCreatingLiveRoom, isRecording]);

  const handleCloseGoLiveDialog = useCallback(() => {
    if (isCreatingLiveRoom) return;
    setIsGoLiveDialogOpen(false);
    setGoLiveError(null);
    setGoLiveLaunchState(GO_LIVE_LAUNCH_STATE_IDLE);
    setLocalRelayPhase(GO_LIVE_LOCAL_RELAY_PHASE_CHECKING);
    setLocalRelayMessage(GO_LIVE_LOCAL_RELAY_CHECKING_MESSAGE);
    setLocalRelayStatus(null);
  }, [isCreatingLiveRoom]);

  const handleLaunchLocalGoLive = useCallback(async () => {
    if (
      isRecording
      || isCreatingLiveRoom
      || localRelayPhase !== GO_LIVE_LOCAL_RELAY_PHASE_READY
    ) return;

    const { persistedProject, playlistItems, previewItemId } = buildGoLiveRequestPayload();
    setGoLiveError(null);

    let helperStatus = localRelayStatus;

    try {
      helperStatus = await ensureLocalHelperReady({
        startIfNeeded: false,
        platform: helperPlatform,
      });
      setLocalRelayStatus(helperStatus);
      setLocalRelayPhase(GO_LIVE_LOCAL_RELAY_PHASE_READY);
      setLocalRelayMessage(GO_LIVE_LOCAL_RELAY_READY_MESSAGE);
    } catch (error) {
      const classifiedError = classifyLocalRequestError(error, { platform: helperPlatform });
      setLocalRelayStatus(null);
      setLocalRelayPhase(GO_LIVE_LOCAL_RELAY_PHASE_OFFLINE);
      setLocalRelayMessage(getGoLiveLocalRelayUnavailableMessage(classifiedError));
      setGoLiveError(error.message || 'Unable to create a local live room right now.');
      setGoLiveLaunchState(GO_LIVE_LAUNCH_STATE_ERROR);
      setIsGoLiveDialogOpen(true);
      return;
    }

    const liveWindow = openOrFocusLiveWindow();

    try {
      setGoLiveLaunchState(GO_LIVE_LAUNCH_STATE_CREATING_ROOM);
      const payload = await createLocalHelperRoom({
        project: persistedProject,
        playlistItems,
        previewItemId,
        loopbackHttpOrigin: helperStatus.loopbackHttpOrigin,
        platform: helperPlatform,
      });
      const launchRelayOrigin = getSafeLocalLaunchRelayOrigin({
        loopbackHttpOrigin: helperStatus.loopbackHttpOrigin,
        preferredRelayHttpOrigin: helperStatus.preferredRelayHttpOrigin,
      });

      const nextLiveUrl = getLiveUrl({
        roomId: payload.roomId,
        controlToken: payload.controlToken,
        relayOrigin: launchRelayOrigin,
      });
      if (liveWindow && !liveWindow.closed) {
        liveWindow.location.replace(nextLiveUrl);
        liveWindow.focus?.();
      } else {
        openOrFocusLiveWindow({
          roomId: payload.roomId,
          controlToken: payload.controlToken,
          relayOrigin: launchRelayOrigin,
        });
      }
      setIsGoLiveDialogOpen(false);
      setGoLiveLaunchState(GO_LIVE_LAUNCH_STATE_IDLE);
    } catch (error) {
      if (liveWindow && !liveWindow.closed) {
        liveWindow.close?.();
      }
      setGoLiveError(error.message || 'Unable to create a local live room right now.');
      setGoLiveLaunchState(GO_LIVE_LAUNCH_STATE_ERROR);
      setIsGoLiveDialogOpen(true);
    }
  }, [
    buildGoLiveRequestPayload,
    helperPlatform,
    isCreatingLiveRoom,
    isRecording,
    localRelayPhase,
    localRelayStatus,
  ]);

  const handleRemovePerson = (personId) => {
    setPeople(prev => {
      const updated = prev.filter(p => p.id !== personId);
      setSelectedPersonId(currentSelected => {
        if (currentSelected !== personId) return currentSelected;
        return updated[0]?.id || null;
      });
      return updated;
    });
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore cleanup failures
      }
    }, 0);
  };

  const handleOpenExportDialog = useCallback(async ({ scope, person }) => {
    if (isRecording) return;
    if (scope === 'single' && !person) return;

    const requestId = ++exportDialogRequestIdRef.current;
    const nextPersonId = scope === 'single' ? (person?.id || null) : null;
    setExportDialog({
      isOpen: true,
      scope,
      personId: nextPersonId,
      isChecking: true,
      checkingMessage: 'Checking browser export support...',
      options: [],
      selectedFormatId: null,
      error: null,
    });

    try {
      const resolvedOptions = await resolveExportFormatOptions({ scope });
      if (exportDialogRequestIdRef.current !== requestId) return;
      const selectableOptions = resolvedOptions.filter((option) => option.selectable);
      const reusableSelection = selectableOptions.some((option) => option.id === lastSelectedExportFormatIdRef.current)
        ? lastSelectedExportFormatIdRef.current
        : null;
      setExportDialog((prev) => ({
        ...prev,
        isChecking: false,
        checkingMessage: null,
        options: resolvedOptions,
        selectedFormatId: reusableSelection || selectableOptions[0]?.id || null,
        error: null,
      }));
    } catch (error) {
      if (exportDialogRequestIdRef.current !== requestId) return;
      console.error('[LowerThirds] Failed to build export format options', error);
      setExportDialog((prev) => ({
        ...prev,
        isChecking: false,
        checkingMessage: null,
        options: [],
        selectedFormatId: null,
        error: 'Could not check export format support in this browser.',
      }));
    }
  }, [isRecording, resolveExportFormatOptions]);

  const handleCloseExportDialog = useCallback(() => {
    exportDialogRequestIdRef.current += 1;
    setExportFormatHelpPopover(null);
    setExportDialog(createEmptyExportDialogState());
  }, []);

  const closeExportFormatHelpPopover = useCallback((target = null, options = {}) => {
    const { force = false } = options;
    setExportFormatHelpPopover((prev) => {
      if (!prev) return null;
      if (target && (prev.optionId !== target.optionId || prev.kind !== target.kind)) {
        return prev;
      }
      if (!force && prev.pinned) return prev;
      return null;
    });
  }, []);

  const openExportFormatHelpPopover = useCallback((target, mode = 'hover') => {
    if (!target?.optionId || !target?.kind) return;
    setExportFormatHelpPopover((prev) => {
      if (!prev) {
        return { ...target, pinned: mode === 'click' };
      }
      const sameTarget = prev.optionId === target.optionId && prev.kind === target.kind;
      if (mode === 'click') {
        if (sameTarget) {
          return prev.pinned ? null : { ...target, pinned: true };
        }
        return { ...target, pinned: true };
      }
      if (prev.pinned && !sameTarget) {
        return prev;
      }
      return { ...target, pinned: sameTarget ? Boolean(prev.pinned) : false };
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleExportSingle = async (person, formatOption) => {
    if (isRecording || !person || !formatOption) return;
    setIsRecording(true);
    setExportProgress(null);
    try {
      const alphaRequested = config.backgroundMode === 'alpha';
      const progressHeader = buildExportProgressHeader(formatOption, alphaRequested);
      const progressLabel = `Rendering ${person.name || 'lower third'}`;
      let blob = null;
      if (formatOption.kind === 'video') {
        blob = await recordPersonToBlob(person, {
          formatOption,
          progressHeader,
          progressLabel,
          onMediaRecorderProgress: (percent, etaMs) => {
            setExportProgress({
              mode: 'video',
              percent,
              etaMs,
              label: progressLabel,
              header: progressHeader,
            });
          },
          registerCancel: (fn) => {
            cancelRenderRef.current = fn;
          }
        });
      } else if (formatOption.kind === 'still') {
        blob = await renderPersonStillToBlob(person, {
          mimeType: formatOption?.runtime?.imageMimeType || getStillMimeTypeForFormat(formatOption.id),
        });
      } else if (formatOption.kind === 'sequence') {
        blob = await renderPersonSequenceZip(person, {
          imageMimeType: formatOption?.runtime?.imageMimeType || 'image/png',
          progressLabel,
          onProgress: (percent, etaMs, nextLabel) => {
            setExportProgress({
              mode: 'sequence',
              percent,
              etaMs,
              label: nextLabel || progressLabel,
              header: progressHeader,
            });
          },
          registerCancel: (fn) => {
            cancelRenderRef.current = fn;
          }
        });
      }

      if (!blob) {
        throw new Error(`Unsupported export format: ${formatOption.id || 'unknown'}`);
      }

      const safeName = slugify(person.name || 'lower-third');
      const filename = formatOption.kind === 'sequence'
        ? `lower-third-${safeName}-frames.zip`
        : `lower-third-${safeName}.${formatOption.extension}`;
      downloadBlob(blob, filename);
    } catch (err) {
      if (!(err instanceof Error && err.message === 'Render cancelled')) {
        console.error('Export failed', err);
      }
    } finally {
      setExportProgress(null);
      setIsRecording(false);
      cancelRenderRef.current = null;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleBatchExport = async (formatOption) => {
    if (isRecording || people.length === 0 || !formatOption) return;
    setIsRecording(true);
    setExportProgress(null);
    try {
      const alphaRequested = config.backgroundMode === 'alpha';
      const progressHeader = buildExportProgressHeader(formatOption, alphaRequested);
      const zip = new JSZip();
      if (formatOption.kind === 'sequence') {
        for (const person of people) {
          const safeName = slugify(person.name || 'lower-third');
          const progressLabel = `Rendering ${person.name || 'lower third'}`;
          await appendPersonSequenceToZip(zip, person, {
            imageMimeType: formatOption?.runtime?.imageMimeType || 'image/png',
            folderPrefix: `lower-third-${safeName}/`,
            progressLabel,
            onProgress: (percent, etaMs, nextLabel) => {
              setExportProgress({
                mode: 'sequence',
                percent,
                etaMs,
                label: nextLabel || progressLabel,
                header: progressHeader,
              });
            },
            registerCancel: (fn) => {
              cancelRenderRef.current = fn;
            },
          });
        }
      } else {
        for (const person of people) {
          const progressLabel = `Rendering ${person.name || 'lower third'}`;
          let blob = null;
          if (formatOption.kind === 'video') {
            blob = await recordPersonToBlob(person, {
              formatOption,
              progressHeader,
              progressLabel,
              onMediaRecorderProgress: (percent, etaMs) => {
                setExportProgress({
                  mode: 'video',
                  percent,
                  etaMs,
                  label: progressLabel,
                  header: progressHeader,
                });
              },
              registerCancel: (fn) => {
                cancelRenderRef.current = fn;
              }
            });
          } else if (formatOption.kind === 'still') {
            blob = await renderPersonStillToBlob(person, {
              mimeType: formatOption?.runtime?.imageMimeType || getStillMimeTypeForFormat(formatOption.id),
            });
          }

          if (!blob) {
            throw new Error(`Unsupported export format: ${formatOption.id || 'unknown'}`);
          }

          const buffer = await blob.arrayBuffer();
          const safeName = slugify(person.name || 'lower-third');
          zip.file(`lower-third-${safeName}.${formatOption.extension}`, buffer);
        }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFilename = formatOption.kind === 'sequence' ? 'lower-thirds-frames.zip' : 'lower-thirds.zip';
      downloadBlob(zipBlob, zipFilename);
    } catch (err) {
      if (!(err instanceof Error && err.message === 'Render cancelled')) {
        console.error('Batch export failed', err);
      }
    } finally {
      setExportProgress(null);
      setIsRecording(false);
      cancelRenderRef.current = null;
    }
  };

  const handleConfirmExportFormat = useCallback(async () => {
    if (exportDialog.isChecking || !exportDialog.selectedFormatId) return;
    let selectedFormat = exportDialog.options.find((option) => option.id === exportDialog.selectedFormatId);
    if (!selectedFormat || !selectedFormat.selectable) return;

    const dialogRequestId = exportDialogRequestIdRef.current;
    if (selectedFormat.id === 'video-mov-alpha-prores') {
      try {
        setExportDialog((prev) => ({
          ...prev,
          isChecking: true,
          checkingMessage: 'Loading ffmpeg core...',
          error: null,
        }));
        await ensureFfmpegWasmLoaded();
        if (exportDialogRequestIdRef.current !== dialogRequestId) return;

        setExportDialog((prev) => ({
          ...prev,
          isChecking: true,
          checkingMessage: 'Checking ProRes support...',
          error: null,
        }));
        const movSupported = await probeFfmpegMovProResSupport();
        if (exportDialogRequestIdRef.current !== dialogRequestId) return;

        let refreshedOptions = await resolveExportFormatOptions({ scope: exportDialog.scope });
        if (exportDialogRequestIdRef.current !== dialogRequestId) return;
        if (!movSupported) {
          refreshedOptions = refreshedOptions.map((option) => (
            option.id !== 'video-mov-alpha-prores'
              ? option
              : {
                ...option,
                supported: false,
                selectable: false,
                disabledReason: option.disabledReason || 'ProRes MOV encoder is unavailable in the current ffmpeg.wasm core build.',
              }
          ));
        }

        const refreshedSelection = refreshedOptions.find((option) => option.id === selectedFormat.id);
        const selectableOptions = refreshedOptions.filter((option) => option.selectable);
        const nextSelection = refreshedSelection?.selectable
          ? refreshedSelection.id
          : (selectableOptions[0]?.id || null);

        setExportDialog((prev) => ({
          ...prev,
          isChecking: false,
          checkingMessage: null,
          options: refreshedOptions,
          selectedFormatId: nextSelection,
          error: null,
        }));

        if (!movSupported || !refreshedSelection?.selectable) {
          return;
        }

        selectedFormat = refreshedSelection;
      } catch (error) {
        if (exportDialogRequestIdRef.current !== dialogRequestId) return;
        console.error('[LowerThirds] Failed to prepare ffmpeg.wasm MOV export', error);
        const refreshedOptions = await resolveExportFormatOptions({ scope: exportDialog.scope }).catch(() => exportDialog.options);
        const selectableOptions = refreshedOptions.filter((option) => option.selectable);
        setExportDialog((prev) => ({
          ...prev,
          isChecking: false,
          checkingMessage: null,
          options: refreshedOptions,
          selectedFormatId: refreshedOptions.some((option) => option.id === prev.selectedFormatId && option.selectable)
            ? prev.selectedFormatId
            : (selectableOptions[0]?.id || null),
          error: 'Could not prepare MOV export in this browser. Try another format.',
        }));
        return;
      }
    }

    lastSelectedExportFormatIdRef.current = selectedFormat.id;
    const dialogSnapshot = exportDialog;
    handleCloseExportDialog();

    if (dialogSnapshot.scope === 'single') {
      const person = people.find((item) => item.id === dialogSnapshot.personId) || null;
      if (!person) return;
      await handleExportSingle(person, selectedFormat);
      return;
    }

    await handleBatchExport(selectedFormat);
  }, [
    ensureFfmpegWasmLoaded,
    exportDialog,
    handleBatchExport,
    handleCloseExportDialog,
    handleExportSingle,
    people,
    probeFfmpegMovProResSupport,
    resolveExportFormatOptions,
  ]);

  useEffect(() => {
    if (exportDialog.isOpen) return;
    setExportFormatHelpPopover(null);
  }, [exportDialog.isOpen]);

  useEffect(() => {
    if (!exportDialog.isOpen || !exportFormatHelpPopover) return undefined;

    const handleDocumentMouseDown = (event) => {
      const target = event.target;
      if (target && typeof target.closest === 'function' && target.closest('.export-format-help-wrap')) {
        return;
      }
      setExportFormatHelpPopover(null);
    };

    const handleDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        setExportFormatHelpPopover(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [exportDialog.isOpen, exportFormatHelpPopover]);

  const mainPositionBounds = useMemo(
    () => getDesignMetrics(measureCtx, selectedPerson, config).renderBounds,
    [config, getDesignMetrics, measureCtx, selectedPerson],
  );

  const clampConfigPosition = useCallback((nextConfig, canvasSizeOverride = null) => {
    const targetCanvasWidth = Math.max(1, Number(canvasSizeOverride?.width) || CANVAS_WIDTH);
    const targetCanvasHeight = Math.max(1, Number(canvasSizeOverride?.height) || CANVAS_HEIGHT);
    const nextBounds = getDesignMetrics(measureCtx, selectedPerson, nextConfig).renderBounds;
    return {
      ...nextConfig,
      posX: Number(clamp(withDefaultNumber(nextConfig.posX, 0), -nextBounds.minX, targetCanvasWidth - nextBounds.maxX).toFixed(2)),
      posY: Number(clamp(withDefaultNumber(nextConfig.posY, 0), -nextBounds.minY, targetCanvasHeight - nextBounds.maxY).toFixed(2)),
    };
  }, [CANVAS_HEIGHT, CANVAS_WIDTH, getDesignMetrics, measureCtx, selectedPerson]);

  const fitConfigToCanvas = useCallback((sourceConfig, canvasSizeOverride = null) => {
    if (!sourceConfig) return sourceConfig;
    const targetCanvasWidth = Math.max(1, Number(canvasSizeOverride?.width) || CANVAS_WIDTH);
    const targetCanvasHeight = Math.max(1, Number(canvasSizeOverride?.height) || CANVAS_HEIGHT);
    const initialBounds = getDesignMetrics(measureCtx, selectedPerson, sourceConfig).renderBounds;
    let nextConfig = { ...sourceConfig };
    const fitRatio = Math.min(
      1,
      initialBounds?.width > 0 ? targetCanvasWidth / initialBounds.width : 1,
      initialBounds?.height > 0 ? targetCanvasHeight / initialBounds.height : 1,
    );

    if (Number.isFinite(fitRatio) && fitRatio < 0.9999) {
      const requestedScale = clamp(withDefaultNumber(nextConfig.scale, 1) * fitRatio, MIN_SCALE, MAX_SCALE);
      nextConfig = applyMainScaleToVisualElements(nextConfig, requestedScale);
    }

    return clampConfigPosition(nextConfig, {
      width: targetCanvasWidth,
      height: targetCanvasHeight,
    });
  }, [CANVAS_HEIGHT, CANVAS_WIDTH, clampConfigPosition, getDesignMetrics, measureCtx, selectedPerson]);

  const positionLimits = useMemo(() => ({
    minX: Number((-mainPositionBounds.minX).toFixed(2)),
    maxX: Number((CANVAS_WIDTH - mainPositionBounds.maxX).toFixed(2)),
    minY: Number((-mainPositionBounds.minY).toFixed(2)),
    maxY: Number((CANVAS_HEIGHT - mainPositionBounds.maxY).toFixed(2)),
  }), [CANVAS_HEIGHT, CANVAS_WIDTH, mainPositionBounds.maxX, mainPositionBounds.maxY, mainPositionBounds.minX, mainPositionBounds.minY]);

  const handleAspectRatioChange = useCallback((event) => {
    const nextAspectRatioId = event.target.value;
    const nextCanvasFormat = getCanvasFormatById(nextAspectRatioId);
    if (!nextCanvasFormat || nextCanvasFormat.id === aspectRatioId || isRecording) return;

    setAspectRatioId(nextCanvasFormat.id);
    setMainSnapGuides({ vertical: [], horizontal: [] });
    setConfig((prev) => fitConfigToCanvas(prev, {
      width: nextCanvasFormat.width,
      height: nextCanvasFormat.height,
    }));
  }, [aspectRatioId, fitConfigToCanvas, isRecording]);

  const handleMainPositionNumberChange = useCallback((axis) => (event) => {
    const rawValue = event.target.value;
    if (rawValue === '' || rawValue === '-' || rawValue === '+') return;
    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric)) return;
    setConfig((prev) => clampConfigPosition({
      ...prev,
      [axis]: numeric,
    }));
  }, [clampConfigPosition]);

  const handleMainScaleSlider = (event) => {
    const nextScale = clamp(Number(event.target.value), MIN_SCALE, MAX_SCALE);
    if (!Number.isFinite(nextScale)) return;
    setConfig((prev) => {
      const scaled = applyMainScaleToVisualElements(prev, nextScale);
      return clampConfigPosition(scaled);
    });
  };

  const getAxisLockedMainMoveDelta = useCallback((delta, shiftKey) => {
    const safeDelta = {
      x: Number(delta?.x) || 0,
      y: Number(delta?.y) || 0,
    };
    if (!shiftKey) {
      mainMoveAxisLockRef.current = null;
      return { delta: safeDelta, axisLock: null };
    }
    if (!mainMoveAxisLockRef.current) {
      if (Math.hypot(safeDelta.x, safeDelta.y) < MAIN_MOVE_AXIS_LOCK_THRESHOLD) {
        return { delta: safeDelta, axisLock: null };
      }
      mainMoveAxisLockRef.current = Math.abs(safeDelta.x) >= Math.abs(safeDelta.y) ? 'x' : 'y';
    }
    if (mainMoveAxisLockRef.current === 'x') {
      return { delta: { x: safeDelta.x, y: 0 }, axisLock: 'x' };
    }
    if (mainMoveAxisLockRef.current === 'y') {
      return { delta: { x: 0, y: safeDelta.y }, axisLock: 'y' };
    }
    return { delta: safeDelta, axisLock: null };
  }, []);

  const updateMainPositionFromClient = useCallback((clientX, clientY, dragInfo, inputState = null) => {
    if (!mainPositionRef.current) return;
    const rect = mainPositionRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    if (dragInfo.mode === 'move') {
      const bounds = dragInfo.bounds;
      const boxWidthPx = Math.max(2, (bounds.width / CANVAS_WIDTH) * rect.width);
      const boxHeightPx = Math.max(2, (bounds.height / CANVAS_HEIGHT) * rect.height);
      const localX = clientX - rect.left;
      const localY = clientY - rect.top;
      const clampedLeft = clamp(localX - dragInfo.offsetX, 0, Math.max(0, rect.width - boxWidthPx));
      const clampedTop = clamp(localY - dragInfo.offsetY, 0, Math.max(0, rect.height - boxHeightPx));
      const rawPosX = (clampedLeft / rect.width) * CANVAS_WIDTH - bounds.minX;
      const rawPosY = (clampedTop / rect.height) * CANVAS_HEIGHT - bounds.minY;
      const rawClampedPosX = clamp(rawPosX, -bounds.minX, CANVAS_WIDTH - bounds.maxX);
      const rawClampedPosY = clamp(rawPosY, -bounds.minY, CANVAS_HEIGHT - bounds.maxY);
      const axisLockedMove = getAxisLockedMainMoveDelta({
        x: rawClampedPosX - dragInfo.startPosX,
        y: rawClampedPosY - dragInfo.startPosY,
      }, Boolean(inputState?.shiftKey));
      const activeMoveAxisLock = axisLockedMove.axisLock;
      let moveDelta = axisLockedMove.delta;
      let nextPosX = clamp(dragInfo.startPosX + moveDelta.x, -bounds.minX, CANVAS_WIDTH - bounds.maxX);
      let nextPosY = clamp(dragInfo.startPosY + moveDelta.y, -bounds.minY, CANVAS_HEIGHT - bounds.maxY);

      if (dragInfo.snapEnabled && dragInfo.startBoundsWorld) {
        const snapped = snapTranslatedBounds({
          startBounds: dragInfo.startBoundsWorld,
          delta: moveDelta,
          canvasWidth: CANVAS_WIDTH,
          canvasHeight: CANVAS_HEIGHT,
          gridType: mainSnapGridType,
        });
        let snappedDelta = snapped.delta;
        let snappedGuides = snapped.guides;
        if (activeMoveAxisLock === 'x') {
          snappedDelta = { x: snappedDelta.x, y: moveDelta.y };
          snappedGuides = {
            vertical: snappedGuides.vertical || [],
            horizontal: [],
          };
        } else if (activeMoveAxisLock === 'y') {
          snappedDelta = { x: moveDelta.x, y: snappedDelta.y };
          snappedGuides = {
            vertical: [],
            horizontal: snappedGuides.horizontal || [],
          };
        }
        moveDelta = snappedDelta;
        nextPosX = clamp(dragInfo.startPosX + moveDelta.x, -bounds.minX, CANVAS_WIDTH - bounds.maxX);
        nextPosY = clamp(dragInfo.startPosY + moveDelta.y, -bounds.minY, CANVAS_HEIGHT - bounds.maxY);
        setMainSnapGuides(snappedGuides);
      } else {
        setMainSnapGuides({ vertical: [], horizontal: [] });
      }

      setConfig((prev) => ({
        ...prev,
        posX: Number(clamp(nextPosX, -bounds.minX, CANVAS_WIDTH - bounds.maxX).toFixed(2)),
        posY: Number(clamp(nextPosY, -bounds.minY, CANVAS_HEIGHT - bounds.maxY).toFixed(2)),
      }));
      return;
    }

    if (dragInfo.mode !== 'resize') return;
    setMainSnapGuides({ vertical: [], horizontal: [] });
    const deltaXCanvas = ((clientX - dragInfo.startClientX) / rect.width) * CANVAS_WIDTH;
    const deltaYCanvas = ((clientY - dragInfo.startClientY) / rect.height) * CANVAS_HEIGHT;
    const startBounds = dragInfo.startBounds;
    if (!startBounds.width || !startBounds.height) return;

    const widthRatio = dragInfo.handle.includes('e')
      ? (startBounds.width + deltaXCanvas) / startBounds.width
      : dragInfo.handle.includes('w')
        ? (startBounds.width - deltaXCanvas) / startBounds.width
        : 1;
    const heightRatio = dragInfo.handle.includes('s')
      ? (startBounds.height + deltaYCanvas) / startBounds.height
      : dragInfo.handle.includes('n')
        ? (startBounds.height - deltaYCanvas) / startBounds.height
        : 1;

    let ratio = 1;
    if (dragInfo.handle.length === 2) {
      ratio = Math.min(widthRatio, heightRatio);
    } else if (dragInfo.handle.includes('e') || dragInfo.handle.includes('w')) {
      ratio = widthRatio;
    } else {
      ratio = heightRatio;
    }
    const nextScale = clamp(
      withDefaultNumber(dragInfo.startConfig.scale, 1) * Math.max(0.2, ratio),
      MIN_SCALE,
      MAX_SCALE,
    );
    const previewConfig = applyMainScaleToVisualElements(dragInfo.startConfig, nextScale);
    const nextBounds = getDesignMetrics(measureCtx, selectedPerson, previewConfig).renderBounds;

    const startLeft = dragInfo.startConfig.posX + startBounds.minX;
    const startTop = dragInfo.startConfig.posY + startBounds.minY;
    const startRight = dragInfo.startConfig.posX + startBounds.maxX;
    const startBottom = dragInfo.startConfig.posY + startBounds.maxY;

    let nextPosX = dragInfo.startConfig.posX;
    let nextPosY = dragInfo.startConfig.posY;
    if (dragInfo.handle === 'nw') {
      nextPosX = startRight - nextBounds.maxX;
      nextPosY = startBottom - nextBounds.maxY;
    } else if (dragInfo.handle === 'ne') {
      nextPosX = startLeft - nextBounds.minX;
      nextPosY = startBottom - nextBounds.maxY;
    } else if (dragInfo.handle === 'se') {
      nextPosX = startLeft - nextBounds.minX;
      nextPosY = startTop - nextBounds.minY;
    } else if (dragInfo.handle === 'sw') {
      nextPosX = startRight - nextBounds.maxX;
      nextPosY = startTop - nextBounds.minY;
    }

    setConfig((prev) => ({
      ...prev,
      ...previewConfig,
      posX: Number(clamp(nextPosX, -nextBounds.minX, CANVAS_WIDTH - nextBounds.maxX).toFixed(2)),
      posY: Number(clamp(nextPosY, -nextBounds.minY, CANVAS_HEIGHT - nextBounds.maxY).toFixed(2)),
    }));
  }, [
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    getAxisLockedMainMoveDelta,
    getDesignMetrics,
    mainSnapGridType,
    measureCtx,
    selectedPerson,
  ]);

  useEffect(() => {
    if (!mainPositionDrag) return undefined;
    const handleMove = (event) => {
      event.preventDefault();
      updateMainPositionFromClient(event.clientX, event.clientY, mainPositionDrag, { shiftKey: event.shiftKey });
    };
    const handleUp = () => {
      mainMoveAxisLockRef.current = null;
      setMainPositionDrag(null);
      setMainSnapGuides({ vertical: [], horizontal: [] });
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('mouseleave', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('mouseleave', handleUp);
    };
  }, [mainPositionDrag, updateMainPositionFromClient]);

  useEffect(() => {
    if (isMainGridSnapEnabled) return;
    setMainSnapGuides({ vertical: [], horizontal: [] });
  }, [isMainGridSnapEnabled]);

  const handleMainPositionMouseDown = (event) => {
    if (isRecording || !mainPositionRef.current) return;
    event.preventDefault();
    mainMoveAxisLockRef.current = null;
    setMainSnapGuides({ vertical: [], horizontal: [] });
    const rect = mainPositionRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const boxWidthPx = Math.max(2, (mainPositionBounds.width / CANVAS_WIDTH) * rect.width);
    const boxHeightPx = Math.max(2, (mainPositionBounds.height / CANVAS_HEIGHT) * rect.height);
    const currentLeft = ((config.posX + mainPositionBounds.minX) / CANVAS_WIDTH) * rect.width;
    const currentTop = ((config.posY + mainPositionBounds.minY) / CANVAS_HEIGHT) * rect.height;
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const inside = (
      clickX >= currentLeft &&
      clickX <= currentLeft + boxWidthPx &&
      clickY >= currentTop &&
      clickY <= currentTop + boxHeightPx
    );

    const dragInfo = {
      mode: 'move',
      bounds: mainPositionBounds,
      offsetX: inside ? clickX - currentLeft : boxWidthPx / 2,
      offsetY: inside ? clickY - currentTop : boxHeightPx / 2,
      startPosX: Number(config.posX) || 0,
      startPosY: Number(config.posY) || 0,
      startBoundsWorld: {
        minX: (Number(config.posX) || 0) + mainPositionBounds.minX,
        minY: (Number(config.posY) || 0) + mainPositionBounds.minY,
        maxX: (Number(config.posX) || 0) + mainPositionBounds.maxX,
        maxY: (Number(config.posY) || 0) + mainPositionBounds.maxY,
        width: mainPositionBounds.width,
        height: mainPositionBounds.height,
      },
      snapEnabled: isMainGridSnapEnabled,
    };
    setMainPositionDrag(dragInfo);
    updateMainPositionFromClient(event.clientX, event.clientY, dragInfo, { shiftKey: event.shiftKey });
  };

  const handleMainResizeMouseDown = (handle) => (event) => {
    if (isRecording) return;
    event.preventDefault();
    event.stopPropagation();
    mainMoveAxisLockRef.current = null;
    setMainSnapGuides({ vertical: [], horizontal: [] });
    setMainPositionDrag({
      mode: 'resize',
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startConfig: {
        ...config,
      },
      startBounds: mainPositionBounds,
    });
  };

  const mainPositionRectStyle = useMemo(() => ({
    width: `${(mainPositionBounds.width / CANVAS_WIDTH) * 100}%`,
    height: `${(mainPositionBounds.height / CANVAS_HEIGHT) * 100}%`,
    left: `${((config.posX + mainPositionBounds.minX) / CANVAS_WIDTH) * 100}%`,
    top: `${((config.posY + mainPositionBounds.minY) / CANVAS_HEIGHT) * 100}%`,
  }), [CANVAS_HEIGHT, CANVAS_WIDTH, config.posX, config.posY, mainPositionBounds.height, mainPositionBounds.minX, mainPositionBounds.minY, mainPositionBounds.width]);
  const mainVisibleSnapGrid = useMemo(() => (
    buildCanvasVisibleGridLines({
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      gridType: mainSnapGridType,
    })
  ), [CANVAS_HEIGHT, CANVAS_WIDTH, mainSnapGridType]);
  const showMainSnapGrid = isMainGridSnapEnabled && isMainSnapGridVisible;

  const safeTotalDuration = Math.max(totalDuration, 0.1);
  const introPercent = (config.transitionInDuration / safeTotalDuration) * 100;
  const stillPercent = (config.stillDuration / safeTotalDuration) * 100;
  const outroPercent = (config.transitionOutDuration / safeTotalDuration) * 100;
  const stillStart = config.transitionInDuration;
  const outroStart = config.transitionInDuration + config.stillDuration;
  const fadeInPercent = config.transitionInDuration
    ? Math.min(
      100,
      (Math.min(config.fadeInDuration ?? config.transitionInDuration / 2, config.transitionInDuration) / config.transitionInDuration) * 100
    )
    : 0;
  const fadeOutPercent = config.transitionOutDuration
    ? Math.min(
      100,
      (Math.min(config.fadeOutDuration ?? config.transitionOutDuration / 2, config.transitionOutDuration) / config.transitionOutDuration) * 100
    )
    : 0;
  const fadeOutBoundaryPercent = Math.max(0, Math.min(100, 100 - fadeOutPercent));
  const showIntroFadeControls = isSlide(config.transitionInType);
  const showOutroFadeControls = isSlide(config.transitionOutType);
  const hasSelection = Boolean(selectedPerson);
  const exportDialogSelectedOption = exportDialog.options.find((option) => option.id === exportDialog.selectedFormatId) || null;
  const exportDialogHasSelectableOption = exportDialog.options.some((option) => option.selectable);
  const orderedExportDialogOptions = useMemo(
    () => getExportFormatDisplayOrder(exportDialog.options),
    [exportDialog.options],
  );
  const isAlphaBackgroundMode = config.backgroundMode === 'alpha';
  const isMovExportDialogSelection = exportDialogSelectedOption?.id === 'video-mov-alpha-prores';
  const showMovGreenBackgroundNotice = isMovExportDialogSelection && config.backgroundMode === 'green';

  const computeInputSize = (text, fallback = 4) => {
    const len = (text || '').length;
    return Math.min(40, Math.max(fallback, len));
  };

  const genericTextFallbackLabelsByPrimitiveId = useMemo(() => {
    let genericIndex = 0;
    return new Map(
      (Array.isArray(config?.shapePrimitives) ? config.shapePrimitives : [])
        .map((shape, index) => {
          if (shape?.layerKind !== 'text') return null;
          if (normalizeTextSource(shape?.textSource, 'main') !== 'generic') return null;
          genericIndex += 1;
          return [shape?.id || `shape-${index}`, `Generic text ${genericIndex}`];
        })
        .filter(Boolean),
    );
  }, [config?.shapePrimitives]);

  const visibleGenericTextLayers = useMemo(() => {
    const shapePrimitives = Array.isArray(config?.shapePrimitives) ? config.shapePrimitives : [];
    const shapeByPrimitiveId = new Map(
      shapePrimitives.map((shape, index) => [shape?.id || `shape-${index}`, shape]),
    );
    return normalizePresetLayerOrder(config?.layerOrder, shapePrimitives)
      .map((layerId) => layerIdToPrimitiveId(layerId))
      .filter(Boolean)
      .map((primitiveId) => {
        const primitive = shapeByPrimitiveId.get(primitiveId);
        if (!primitive || primitive.layerKind !== 'text') return null;
        if (normalizeTextSource(primitive.textSource, 'main') !== 'generic') return null;
        if (primitive.hideFromPeoplePanel === true) return null;
        const fallbackLabel = genericTextFallbackLabelsByPrimitiveId.get(primitiveId) || 'Generic text';
        const layerName = normalizeLayerName(primitive.layerName);
        return {
          primitiveId,
          label: layerName || fallbackLabel,
          fallbackLabel,
          fallbackText: typeof primitive.textValue === 'string' ? primitive.textValue : '',
        };
      })
      .filter(Boolean);
  }, [config?.layerOrder, config?.shapePrimitives, genericTextFallbackLabelsByPrimitiveId]);

  const resolveVisibleGenericTextValue = (person, layer) => resolvePersonGenericTextValue({
    person,
    presetId: activePresetId,
    primitiveId: layer.primitiveId,
    fallbackText: layer.fallbackText,
  });

  const selectedFadeInCurve = fadeCurveOptions.find(option => option.value === (config.fadeInCurve || 'linear')) || fadeCurveOptions[0];
  const selectedFadeOutCurve = fadeCurveOptions.find(option => option.value === (config.fadeOutCurve || 'linear')) || fadeCurveOptions[0];

  const renderExportFormatHelpTrigger = (option, message) => {
    if (!message) return null;
    const kind = 'warning';
    const target = { optionId: option.id, kind };
    const isOpen = exportFormatHelpPopover?.optionId === option.id && exportFormatHelpPopover?.kind === kind;
    const popoverId = `export-format-help-${option.id}-${kind}`;
    const iconSrc = exportWarningIconSrc;
    const buttonLabel = `Show warning for ${option.label}`;
    const paragraphs = String(message)
      .split(/\n{2,}/)
      .map((part) => part.trim())
      .filter(Boolean);

    return (
      <div
        key={`${option.id}-${kind}`}
        className="export-format-help-wrap"
        onMouseEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          openExportFormatHelpPopover(target, 'hover');
        }}
        onMouseLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          closeExportFormatHelpPopover(target);
        }}
      >
        <button
          type="button"
          className={`export-format-help-trigger ${kind} ${isOpen ? 'is-open' : ''}`}
          aria-label={buttonLabel}
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-describedby={isOpen ? popoverId : undefined}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openExportFormatHelpPopover(target, 'click');
          }}
          onFocus={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openExportFormatHelpPopover(target, 'focus');
          }}
          onBlur={(event) => {
            event.preventDefault();
            event.stopPropagation();
            closeExportFormatHelpPopover(target);
          }}
        >
          <img src={iconSrc} alt="" aria-hidden="true" />
        </button>
        {isOpen && (
          <div id={popoverId} role="tooltip" className={`export-format-help-popover ${kind}`}>
            {paragraphs.map((paragraph, index) => (
              <p key={`${popoverId}-p-${index}`}>{paragraph}</p>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-container">
      {saveError && (
        <div className="app-error-banner" role="alert">
          {saveError}
        </div>
      )}
      <div className="controls-panel">
        <div className="app-brand-row">
          <h2 className="app-brand-heading">
            <img className="app-brand-logo" src={animcueLogo} alt="AnimCue" />
          </h2>
          <div className="app-kofi-button-host" ref={kofiButtonHostRef} />
        </div>

        <div className="control-group person-editor">
          <div className="person-editor-fields">
            <input
              type="text"
              placeholder="Name"
              value={selectedPerson?.name || ''}
              onChange={(e) => selectedPerson && handlePersonChange(selectedPerson.id, 'name', e.target.value)}
              disabled={!hasSelection || isRecording}
            />
            <input
              type="text"
              placeholder="Title"
              value={selectedPerson?.title || ''}
              onChange={(e) => selectedPerson && handlePersonChange(selectedPerson.id, 'title', e.target.value)}
              disabled={!hasSelection || isRecording}
            />
            {hasSelection && visibleGenericTextLayers.map((layer) => (
              <label key={`selected-person-${layer.primitiveId}`} className="person-editor-generic-field">
                <span className="person-editor-generic-label">{layer.label}</span>
                <input
                  type="text"
                  value={resolveVisibleGenericTextValue(selectedPerson, layer)}
                  onChange={(e) => selectedPerson && handlePersonGenericTextChange(
                    selectedPerson.id,
                    layer.primitiveId,
                    e.target.value,
                    layer.fallbackText,
                  )}
                  disabled={isRecording}
                  aria-label={layer.label}
                />
              </label>
            ))}
          </div>
          {!hasSelection && <div className="muted-text">Add a person to start editing.</div>}
        </div>

        <div className="control-grid main-color-grid">
          <div className="control-group">
            <label htmlFor="main-color-primary">Primary</label>
            <input
              id="main-color-primary"
              type="color"
              name="colorPrimary"
              value={config.colorPrimary}
              onChange={handleChange}
              disabled={isRecording}
            />
          </div>
          <div className="control-group">
            <label htmlFor="main-color-secondary">Secondary</label>
            <input
              id="main-color-secondary"
              type="color"
              name="colorSecondary"
              value={config.colorSecondary}
              onChange={handleChange}
              disabled={isRecording}
            />
          </div>
          <div className="control-group">
            <label htmlFor="main-color-upper">Text upper</label>
            <input
              id="main-color-upper"
              type="color"
              name="colorTextUpper"
              value={config.colorTextUpper}
              onChange={handleChange}
              disabled={isRecording}
            />
          </div>
          <div className="control-group">
            <label htmlFor="main-color-lower">Text lower</label>
            <input
              id="main-color-lower"
              type="color"
              name="colorTextLower"
              value={config.colorTextLower}
              onChange={handleChange}
              disabled={isRecording}
            />
          </div>
        </div>

        <div className="control-group sidebar-preset-entry">
          <button
            type="button"
            className="secondary-btn sidebar-edit-presets-btn"
            onClick={openPresetOverlay}
            disabled={isRecording}
          >
            Edit presets
          </button>
        </div>

        <div className="control-group">
          <label htmlFor="background-mode-select">Background Mode</label>
          <div className="background-mode-row">
            <select
              id="background-mode-select"
              className="background-mode-select"
              name="backgroundMode"
              value={config.backgroundMode}
              onChange={handleChange}
            >
              <option value="alpha">Transparent (Alpha)</option>
              <option value="green">Color Key</option>
            </select>
            {config.backgroundMode === 'green' && (
              <label className="color-key-picker" htmlFor="background-key-color">
                <span>Key color</span>
                <input
                  id="background-key-color"
                  className="color-key-input"
                  type="color"
                  name="backgroundKeyColor"
                  value={config.backgroundKeyColor}
                  onChange={handleChange}
                />
              </label>
            )}
          </div>
        </div>

        {mainSidebarImageEntries.length > 0 && (
          <div className="control-group">
            <label htmlFor={hasMultipleMainImages ? 'main-image-select' : undefined}>Preset image</label>
            <div className="background-mode-row main-image-row">
              {hasMultipleMainImages && (
                <select
                  id="main-image-select"
                  className="background-mode-select main-image-select"
                  value={selectedMainImageEntry?.primitiveId || ''}
                  onChange={(event) => setSelectedMainImagePrimitiveId(event.target.value)}
                  disabled={isRecording}
                  aria-label="Preset image"
                >
                  {mainSidebarImageEntries.map((entry) => (
                    <option key={entry.primitiveId} value={entry.primitiveId}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                className="secondary-btn main-image-replace-btn"
                onClick={openMainImageImport}
                disabled={isRecording || !selectedMainImageEntry}
              >
                Replace image
              </button>
            </div>
          </div>
        )}

        <div className="control-group position-group main-position-group">
          <div className="position-group-header">
            <label>Position + Size</label>
            <div className="snap-controls-row">
              {isMainGridSnapEnabled && (
                <>
                  <select
                    className="snap-grid-type-select"
                    value={mainSnapGridType}
                    onChange={(event) => setMainSnapGridType(event.target.value)}
                    disabled={isRecording}
                    aria-label="Snap grid type"
                    title="Snap grid type"
                  >
                    {SNAP_GRID_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={`secondary-btn snap-toggle-btn snap-grid-visibility-btn ${showMainSnapGrid ? 'is-active' : ''}`}
                    onClick={() => setIsMainSnapGridVisible((prev) => !prev)}
                    disabled={isRecording}
                    aria-pressed={showMainSnapGrid}
                    aria-label={isMainSnapGridVisible ? 'Hide snap grid' : 'Show snap grid'}
                    title={isMainSnapGridVisible ? 'Hide snap grid' : 'Show snap grid'}
                  >
                    {isMainSnapGridVisible ? <GridEyeOpenIcon /> : <GridEyeClosedIcon />}
                  </button>
                </>
              )}
              <button
                type="button"
                className={`secondary-btn snap-toggle-btn ${isMainGridSnapEnabled ? 'is-active' : ''}`}
                onClick={() => setIsMainGridSnapEnabled((prev) => !prev)}
                disabled={isRecording}
                aria-pressed={isMainGridSnapEnabled}
                aria-label={isMainGridSnapEnabled ? 'Disable snapping' : 'Enable snapping'}
                title={isMainGridSnapEnabled ? 'Snapping on' : 'Snapping off'}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M1 6V9C1 12.866 4.13401 16 8 16C11.866 16 15 12.866 15 9V6H10V9C10 10.1046 9.10457 11 8 11C6.89543 11 6 10.1046 6 9V6H1Z" fill="currentColor" />
                  <path d="M1 4H6V1H1V4Z" fill="currentColor" />
                  <path d="M10 4H15V1H10V4Z" fill="currentColor" />
                </svg>
              </button>
            </div>
          </div>
          <div
            className="position-preview main-position-preview"
            ref={mainPositionRef}
            onMouseDown={handleMainPositionMouseDown}
            style={{ aspectRatio: canvasAspectRatio }}
          >
            {showMainSnapGrid && (
              <div className="snap-grid-overlay" aria-hidden="true">
                {mainVisibleSnapGrid.x.map((x) => (
                  <div
                    key={`grid-vx-${x}`}
                    className="snap-grid-line vertical"
                    style={{ left: `${(x / CANVAS_WIDTH) * 100}%` }}
                  />
                ))}
                {mainVisibleSnapGrid.y.map((y) => (
                  <div
                    key={`grid-hy-${y}`}
                    className="snap-grid-line horizontal"
                    style={{ top: `${(y / CANVAS_HEIGHT) * 100}%` }}
                  />
                ))}
              </div>
            )}
            {(mainSnapGuides.vertical.length > 0 || mainSnapGuides.horizontal.length > 0) && (
              <div className="position-snap-guides" aria-hidden="true">
                {mainSnapGuides.vertical.map((x) => (
                  <div
                    key={`vx-${x}`}
                    className="position-snap-guide vertical"
                    style={{ left: `${(x / CANVAS_WIDTH) * 100}%` }}
                  />
                ))}
                {mainSnapGuides.horizontal.map((y) => (
                  <div
                    key={`hy-${y}`}
                    className="position-snap-guide horizontal"
                    style={{ top: `${(y / CANVAS_HEIGHT) * 100}%` }}
                  />
                ))}
              </div>
            )}
            <div className="position-rect" style={mainPositionRectStyle}>
              <span className="position-rect-label">Drag</span>
              <button
                type="button"
                className="main-position-resize-handle nw"
                onMouseDown={handleMainResizeMouseDown('nw')}
                aria-label="Resize from top left"
                disabled={isRecording}
              />
              <button
                type="button"
                className="main-position-resize-handle ne"
                onMouseDown={handleMainResizeMouseDown('ne')}
                aria-label="Resize from top right"
                disabled={isRecording}
              />
              <button
                type="button"
                className="main-position-resize-handle sw"
                onMouseDown={handleMainResizeMouseDown('sw')}
                aria-label="Resize from bottom left"
                disabled={isRecording}
              />
              <button
                type="button"
                className="main-position-resize-handle se"
                onMouseDown={handleMainResizeMouseDown('se')}
                aria-label="Resize from bottom right"
                disabled={isRecording}
              />
            </div>
          </div>
          <div className="position-readout position-readout-controls">
            <div className="position-readout-field">
              <span className="position-readout-label">X</span>
              <input
                className="position-readout-number compact-number-input"
                type="number"
                step="1"
                min={String(Math.ceil(positionLimits.minX))}
                max={String(Math.floor(positionLimits.maxX))}
                value={Math.round(config.posX)}
                onChange={handleMainPositionNumberChange('posX')}
                disabled={isRecording}
                aria-label="X position"
              />
            </div>
            <div className="position-readout-field">
              <span className="position-readout-label">Y</span>
              <input
                className="position-readout-number compact-number-input"
                type="number"
                step="1"
                min={String(Math.ceil(positionLimits.minY))}
                max={String(Math.floor(positionLimits.maxY))}
                value={Math.round(config.posY)}
                onChange={handleMainPositionNumberChange('posY')}
                disabled={isRecording}
                aria-label="Y position"
              />
            </div>
            <div className="position-readout-scale">Scale {(config.scale * 100).toFixed(0)}%</div>
          </div>
          <div className="slider-row">
            <input
              type="range"
              min={String(MIN_SCALE)}
              max={String(MAX_SCALE)}
              step="0.01"
              value={config.scale}
              onChange={handleMainScaleSlider}
              disabled={isRecording}
            />
            <span className="slider-value">{Math.round(config.scale * 100)}%</span>
          </div>
        </div>
      </div>

      <div className="main-panel">
        <div className="preset-bar">
          <div className="control-group compact">
            <label>Preset</label>
            <select
              aria-label="Preset selector"
              value={activePresetId}
              onChange={handlePresetSelect}
              disabled={isRecording}
            >
              {allPresets.map((preset) => (
                <option key={preset.id} value={preset.id}>{preset.name}</option>
              ))}
            </select>
          </div>
          <button type="button" className="secondary-btn" onClick={openPresetOverlay} disabled={isRecording}>
            Edit presets
          </button>
        </div>

        <div className="preview-panel">
          <div className="preview-toolbar">
            <select
              id="fps-select"
              className="preview-fps-select"
              value={fps}
              onChange={(e) => setFps(Number(e.target.value))}
              disabled={isRecording}
            >
              {FPS_OPTIONS.map((option) => (
                <option key={option} value={option}>{option} fps</option>
              ))}
            </select>
            <select
              id="aspect-ratio-select"
              className="preview-fps-select preview-aspect-select"
              value={aspectRatioId}
              onChange={handleAspectRatioChange}
              disabled={isRecording}
              aria-label="Aspect ratio selector"
            >
              {CANVAS_FORMATS.map((format) => (
                <option key={format.id} value={format.id}>{format.label}</option>
              ))}
            </select>
          </div>
          <canvas
            ref={canvasRef}
            className={`preview-canvas ${config.backgroundMode === 'alpha' ? 'preview-canvas-alpha' : ''}`}
            style={{ aspectRatio: canvasAspectRatio }}
          />
        </div>

        <div className="timeline-panel">
          <div className="timeline-header">
            <div className="timeline-title">Timeline</div>
          </div>

          <div className="transition-grid">
            <div className="transition-select">
              <label>Opening transition</label>
              <div className="transition-row">
                <select
                  ref={introSelectRef}
                  value={config.transitionInType}
                  onChange={(e) => handleTransitionTypeChange('transitionInType', e.target.value)}
                >
                  <option value="slide-left">Slide in from left</option>
                  <option value="slide-right">Slide in from right</option>
                  <option value="fade">Fade in</option>
                </select>
              </div>
            </div>

            <div className="timeline-total-control transition-total">
              <label htmlFor="total-duration-input">Total duration (s)</label>
              <input
                id="total-duration-input"
                type="number"
                className="timeline-total-input"
                step="0.01"
                min={(MIN_TRANSITION * 2 + MIN_STILL).toFixed(2)}
                value={totalInputValue}
                onChange={handleTotalDurationChange}
                onFocus={() => setIsEditingTotal(true)}
                onBlur={handleTotalDurationBlur}
              />
            </div>

            <div className="transition-select">
              <label>Ending transition</label>
              <div className="transition-row">
                <select
                  ref={outroSelectRef}
                  value={config.transitionOutType}
                  onChange={(e) => handleTransitionTypeChange('transitionOutType', e.target.value)}
                >
                  <option value="slide-left">Slide out to left</option>
                  <option value="slide-right">Slide out to right</option>
                  <option value="fade">Fade out</option>
                </select>
              </div>
            </div>
          </div>

          {(showIntroFadeControls || showOutroFadeControls) && (
            <div className="timeline-fade-row">
              <div className="fade-slot intro" style={{ width: `${introPercent}%` }}>
                {showIntroFadeControls && (
                  <>
                    <div className="fade-chip-row">
                      <button
                        type="button"
                        className={`fade-chip ${config.fadeIn ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenFadeMenu(null);
                          toggleFade('fadeIn', 'fadeInDuration', config.transitionInDuration);
                        }}
                        disabled={isRecording}
                      >
                        {config.fadeIn ? 'Remove fade' : 'Add fade'}
                      </button>
                      {config.fadeIn && (
                        <div className="fade-curve-menu">
                          <button
                            type="button"
                            className="fade-curve-trigger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFadeMenu(openFadeMenu === 'intro' ? null : 'intro');
                            }}
                            disabled={isRecording}
                            aria-label={`Fade in curve: ${selectedFadeInCurve.label}`}
                            title={`Fade in curve: ${selectedFadeInCurve.label}`}
                          >
                            <img
                              src={selectedFadeInCurve.icon}
                              alt=""
                              className="fade-curve-icon"
                              aria-hidden="true"
                            />
                          </button>
                          {openFadeMenu === 'intro' && (
                            <div className="fade-curve-dropdown">
                              {fadeCurveOptions.map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  className={`fade-curve-option ${config.fadeInCurve === option.value ? 'selected' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFadeCurveChange('in', option.value);
                                  }}
                                  disabled={isRecording}
                                >
                                  <img
                                    src={option.icon}
                                    alt=""
                                    className="fade-curve-option-icon"
                                    aria-hidden="true"
                                  />
                                  <div className="fade-curve-copy">
                                    <span className="fade-curve-label">{option.label}</span>
                                    <span className="fade-curve-hint">
                                      {option.value === 'ease-in'
                                        ? 'Starts slow, ramps up'
                                        : option.value === 'ease-out'
                                          ? 'Starts fast, ramps down'
                                          : 'Even pace'}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {config.fadeIn && (
                      <div className="fade-bar">
                        <div
                          className="fade-bar-track intro"
                          style={{ '--fade-boundary': `${fadeInPercent}%` }}
                        >
                          <div className="fade-bar-fill-clip intro">
                            <div
                              className="fade-bar-fill intro"
                              style={{ width: `${fadeInPercent}%` }}
                            />
                          </div>
                          <div className="fade-bar-handle" onMouseDown={startFadeDrag('in')} />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="fade-slot still" style={{ width: `${stillPercent}%` }} />
              <div className="fade-slot outro" style={{ width: `${outroPercent}%` }}>
                {showOutroFadeControls && (
                  <>
                    <div className="fade-chip-row">
                      <button
                        type="button"
                        className={`fade-chip ${config.fadeOut ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenFadeMenu(null);
                          toggleFade('fadeOut', 'fadeOutDuration', config.transitionOutDuration);
                        }}
                        disabled={isRecording}
                      >
                        {config.fadeOut ? 'Remove fade' : 'Add fade'}
                      </button>
                      {config.fadeOut && (
                        <div className="fade-curve-menu">
                          <button
                            type="button"
                            className="fade-curve-trigger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFadeMenu(openFadeMenu === 'outro' ? null : 'outro');
                            }}
                            disabled={isRecording}
                            aria-label={`Fade out curve: ${selectedFadeOutCurve.label}`}
                            title={`Fade out curve: ${selectedFadeOutCurve.label}`}
                          >
                            <img
                              src={selectedFadeOutCurve.icon}
                              alt=""
                              className="fade-curve-icon"
                              aria-hidden="true"
                            />
                          </button>
                          {openFadeMenu === 'outro' && (
                            <div className="fade-curve-dropdown fade-curve-dropdown-right">
                              {fadeCurveOptions.map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  className={`fade-curve-option ${config.fadeOutCurve === option.value ? 'selected' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFadeCurveChange('out', option.value);
                                  }}
                                  disabled={isRecording}
                                >
                                  <img
                                    src={option.icon}
                                    alt=""
                                    className="fade-curve-option-icon"
                                    aria-hidden="true"
                                  />
                                  <div className="fade-curve-copy">
                                    <span className="fade-curve-label">{option.label}</span>
                                    <span className="fade-curve-hint">
                                      {option.value === 'ease-in'
                                        ? 'Starts slow, ramps up'
                                        : option.value === 'ease-out'
                                          ? 'Starts fast, ramps down'
                                          : 'Even pace'}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {config.fadeOut && (
                      <div className="fade-bar">
                        <div
                          className="fade-bar-track outro"
                          style={{ '--fade-boundary': `${fadeOutBoundaryPercent}%` }}
                        >
                          <div className="fade-bar-fill-clip outro">
                            <div
                              className="fade-bar-fill outro"
                              style={{ width: `${fadeOutPercent}%` }}
                            />
                          </div>
                          <div className="fade-bar-handle" onMouseDown={startFadeDrag('out')} />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="timeline-track" ref={timelineRef}>
            <div
              className="timeline-block intro"
              style={{ width: `${introPercent}%` }}
              onClick={() => introSelectRef.current?.focus()}
            >
              <div className="timeline-block-icon">
                <img src="/transition.webp" alt="Opening transition" />
              </div>
              <span className="timeline-block-label">{config.transitionInDuration.toFixed(2)}s</span>
              <div className="drag-handle right" onMouseDown={startEdgeDrag('intro-end')} />
            </div>
            <div className="timeline-block still" style={{ width: `${stillPercent}%` }}>
              <div className="timeline-block-icon">
                <img src="/still.webp" alt="Still duration" />
              </div>
              <span className="timeline-block-label">{config.stillDuration.toFixed(2)}s</span>
              <div className="drag-handle left" onMouseDown={startEdgeDrag('intro-end')} />
              <div className="drag-handle right" onMouseDown={startEdgeDrag('still-end')} />
            </div>
            <div
              className="timeline-block outro"
              style={{ width: `${outroPercent}%` }}
              onClick={() => outroSelectRef.current?.focus()}
            >
              <div className="timeline-block-icon">
                <img src="/transition.webp" alt="Ending transition" />
              </div>
              <span className="timeline-block-label">{config.transitionOutDuration.toFixed(2)}s</span>
              <div className="drag-handle left" onMouseDown={startEdgeDrag('still-end')} />
              <div className="drag-handle right" onMouseDown={startEdgeDrag('outro-end')} />
            </div>
          </div>

          <div className="timeline-scale">
            <span>0s</span>
            <span>{stillStart.toFixed(1)}s</span>
            <span>{outroStart.toFixed(1)}s</span>
            <span>{safeTotalDuration.toFixed(1)}s</span>
          </div>
        </div>
      </div>

        <div className="people-panel">
          <div className="people-header">
            <h2>People</h2>
            <button type="button" className="secondary-btn people-go-live-btn" onClick={handleOpenGoLive} disabled={isRecording || isCreatingLiveRoom}>
              {isCreatingLiveRoom ? 'Starting...' : 'Go Live!'}
            </button>
          </div>
          {goLiveError ? <div className="people-go-live-error" role="alert">{goLiveError}</div> : null}

          <div className="people-list">
          {people.map(person => (
            <div
              key={person.id}
              className={`person-card ${person.id === selectedPersonId ? 'selected' : ''}`}
              onClick={() => {
                if (isRecording) return;
                setSelectedPersonId(person.id);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isRecording) {
                  e.preventDefault();
                  setSelectedPersonId(person.id);
                }
              }}
            >
              <div className="person-meta">
                <input
                  className="person-inline-input person-name-input"
                  type="text"
                  value={person.name}
                  placeholder="Name"
                  size={computeInputSize(person.name, 4)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={(e) => handlePersonChange(person.id, 'name', e.target.value)}
                  disabled={isRecording}
                />
                <input
                  className="person-inline-input person-title-input"
                  type="text"
                  value={person.title}
                  placeholder="Title"
                  size={computeInputSize(person.title, 5)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={(e) => handlePersonChange(person.id, 'title', e.target.value)}
                  disabled={isRecording}
                />
                {visibleGenericTextLayers.map((layer) => (
                  <label
                    key={`${person.id}-${layer.primitiveId}`}
                    className="person-inline-generic-field"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      className="person-inline-input person-title-input person-generic-input"
                      type="text"
                      value={resolveVisibleGenericTextValue(person, layer)}
                      placeholder=""
                      size={computeInputSize(resolveVisibleGenericTextValue(person, layer), 5)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onChange={(e) => handlePersonGenericTextChange(
                        person.id,
                        layer.primitiveId,
                        e.target.value,
                        layer.fallbackText,
                      )}
                      disabled={isRecording}
                      aria-label={layer.label}
                    />
                  </label>
                ))}
              </div>
              <div className="person-card-actions">
                <button
                  className={`secondary-btn person-action-btn person-mirror-btn ${person.mirrored ? 'is-active' : ''}`.trim()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTogglePersonMirrored(person.id);
                  }}
                  disabled={isRecording}
                  aria-label={`Toggle mirror for ${person.name || 'person'}`}
                  aria-pressed={person.mirrored}
                  title="Mirror"
                >
                  <MirrorLeftIcon className="person-action-icon" />
                </button>
                <button
                  className="delete-btn icon-only"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemovePerson(person.id);
                  }}
                  disabled={isRecording}
                  aria-label={`Delete ${person.name || 'person'}`}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 3a1 1 0 0 0-.94.66L7.38 5H4a1 1 0 1 0 0 2h.6l.74 12.06A2.5 2.5 0 0 0 7.83 21h8.34a2.5 2.5 0 0 0 2.49-1.94L19.4 7H20a1 1 0 1 0 0-2h-3.38l-.68-1.34A1 1 0 0 0 15 3H9Zm.62 2h4.76l.34.68a1 1 0 0 0 .9.57h2.26l-.73 11.86a.5.5 0 0 1-.5.44H7.83a.5.5 0 0 1-.5-.44L6.6 6.25H8.86a1 1 0 0 0 .9-.57L9.62 5Zm-.62 4a1 1 0 0 0-1 1v6a1 1 0 1 0 2 0v-6a1 1 0 0 0-1-1Zm4 0a1 1 0 0 0-1 1v6a1 1 0 1 0 2 0v-6a1 1 0 0 0-1-1Z" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        <button className="add-card-btn" onClick={handleAddPerson} disabled={isRecording}>+ Add person</button>

        <div className="export-actions">
          <button
            className="secondary-btn ghost"
            onClick={() => handleOpenExportDialog({ scope: 'single', person: selectedPerson })}
            disabled={isRecording || !hasSelection}
          >
            Export Single
          </button>
          <button
            className={`export-btn primary ${isRecording ? 'recording' : ''}`}
            onClick={() => handleOpenExportDialog({ scope: 'batch' })}
            disabled={isRecording || people.length === 0}
          >
            {isRecording ? 'Rendering...' : 'Export all'}
          </button>
        </div>
      </div>

      {exportDialog.isOpen && (
        <div className="export-format-overlay" role="presentation">
          <div
            className="export-format-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-format-dialog-title"
          >
            <div className="export-format-header">
              <h3 id="export-format-dialog-title">Select export format</h3>
              <p>{exportDialog.scope === 'single' ? 'Single export' : 'Batch export'}</p>
            </div>

            {isAlphaBackgroundMode && (
              <div className="export-format-notice" role="note">
                Transparent background is enabled (Alpha mode). Opaque-only formats are disabled.
                Switch Background Mode to green/chroma key to enable opaque WebM or MP4.
              </div>
            )}

            {showMovGreenBackgroundNotice && (
              <div className="export-format-notice export-format-notice-warning" role="note">
                MOV supports alpha, but current Background Mode is green/chroma key. This export will include the green background unless you switch Background Mode to Alpha.
              </div>
            )}

            {exportDialog.isChecking ? (
              <div className="export-format-loading">{exportDialog.checkingMessage || 'Checking browser export support...'}</div>
            ) : exportDialog.error ? (
              <div className="export-format-error" role="alert">{exportDialog.error}</div>
            ) : (
              <div className="export-format-options" role="radiogroup" aria-label="Export formats">
                {orderedExportDialogOptions.map((option) => {
                  const { warningMessage } = getExportFormatHelpContent(option);
                  return (
                    <label
                      key={option.id}
                      className={`export-format-option ${option.selectable ? '' : 'disabled'} ${exportDialog.selectedFormatId === option.id ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="export-format"
                        value={option.id}
                        checked={exportDialog.selectedFormatId === option.id}
                        onChange={() => {
                          if (!option.selectable) return;
                          setExportDialog((prev) => ({ ...prev, selectedFormatId: option.id }));
                        }}
                        disabled={!option.selectable}
                      />
                      <div className="export-format-option-copy">
                        <div className="export-format-option-title-row">
                          <div className="export-format-option-title-group">
                            <span className="export-format-option-title">{option.label}</span>
                            {warningMessage && (
                              <div className="export-format-option-meta-icons" aria-label={`${option.label} metadata`}>
                                {renderExportFormatHelpTrigger(option, warningMessage)}
                              </div>
                            )}
                          </div>
                          <span className="export-format-option-kind">{option.kind}</span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="export-format-actions">
              <button
                type="button"
                className="secondary-btn"
                onClick={handleCloseExportDialog}
                disabled={isRecording}
              >
                Cancel
              </button>
              <button
                type="button"
                className="export-btn primary"
                onClick={handleConfirmExportFormat}
                disabled={isRecording || exportDialog.isChecking || !exportDialogHasSelectableOption || !exportDialogSelectedOption}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}

      <GoLiveLaunchDialog
        isOpen={isGoLiveDialogOpen}
        launchState={goLiveLaunchState}
        errorMessage={goLiveError || ''}
        downloadUrl={helperDownloadUrl}
        platform={helperPlatform}
        localRelayPhase={localRelayPhase}
        localRelayMessage={localRelayMessage}
        onClose={handleCloseGoLiveDialog}
        onLaunchLocal={handleLaunchLocalGoLive}
      />

      <PresetOverlay
        isOpen={isPresetOverlayOpen}
        presets={allPresets}
        selectedPresetId={overlayPresetId}
        draftPreset={draftPreset}
        isDraftDirty={isDraftDirty}
        onClose={closePresetOverlay}
        onSelectPreset={handleOverlaySelectPreset}
        onDraftChange={handleOverlayDraftChange}
        canUndoDraft={overlayDraftUndoCount > 0}
        onUndoDraft={handleOverlayUndoDraft}
        onCreateNew={handleOverlayCreateNew}
        onSavePreset={handleOverlaySavePreset}
        onDeletePreset={handleOverlayDeletePreset}
        drawPreviewFrame={drawOverlayPreviewFrame}
        getPresetEditorGeometry={getPresetEditorGeometry}
        isRecording={isRecording}
        canvasDimensions={activeCanvasFormat}
      />

      <ImageImportOverlay
        isOpen={isMainImageImportOpen}
        lockedMode={selectedMainImageEntry?.primitive?.imageCropMode || null}
        onConfirm={handleMainImageImportConfirm}
        onCancel={() => setIsMainImageImportOpen(false)}
      />

      {exportProgress && (
        <div className="export-progress-overlay" role="status" aria-live="polite">
          <div className="export-progress-modal">
            <div className="export-progress-header">{exportProgress.header || 'Rendering export'}</div>
            <div className="export-progress-label">{exportProgress.label || 'Rendering export'}</div>
            <div className="export-progress-bar">
              <div
                className="export-progress-fill"
                style={{ width: `${Math.min(100, Math.max(0, Math.round((exportProgress.percent || 0) * 100)))}%` }}
              />
            </div>
            <div className="export-progress-meta">
              <span>{Math.min(100, Math.max(0, Math.round((exportProgress.percent || 0) * 100)))}%</span>
              {Number.isFinite(exportProgress.etaMs) && (
                <span>~{Math.max(0, Math.round(exportProgress.etaMs / 1000))}s remaining</span>
              )}
            </div>
            <button
              type="button"
              className="export-cancel-btn"
              onClick={() => {
                const cancel = cancelRenderRef.current;
                if (cancel) cancel();
              }}
              disabled={!cancelRenderRef.current}
            >
              Cancel render
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
